import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "@/build-stamp";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import {
  LanguageSelector,
  MobileNavMenu,
  MobileNavToggle,
  NavbarLogo,
} from "@/components/ui/resizable-navbar";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { format, isBefore, startOfDay } from "date-fns";
import { toast } from "sonner";
import type { DateRange } from "react-day-picker";
import { Calendar as CalendarIcon, ChevronDown, Globe } from "lucide-react";
import type { Language } from "@/data/translations";
import type { BookPageTranslations } from "@/data/book-page-i18n";
import { ROOMS_DATA } from "@/data/rooms";
import { Room } from "@/types/room";
import { ROOM_METADATA, FilterCategory, getRoomMetadata, type ViewBadgeType } from "@/data/room-metadata";
import { splitOccupancyIntoRooms } from "@/utils/split-stay-occupancy";
import {
  UPSELLS,
  effectiveMewsVariableCountIds,
  getMewsProductIdsForUpsellForRatePlan,
  getResolvedUpsellPrices,
  upsellPrimaryMewsProductIdForBrowse,
  type MewsUpsellRatePlan,
  type Upsell,
} from "@/data/upsells";
import {
  getBookPageBeddingLabel,
  getLocalizedBookRoomDisplay,
  getLocalizedUpsellCopy,
} from "@/utils/bookLocalizedCopy";
import { BestRateGuaranteePill } from "@/components/booking/BestRateGuaranteePill";
import { UpsellCard } from "@/components/booking/UpsellCard";
import type { LucideIcon } from "lucide-react";
import { AiOutlineEdit } from "react-icons/ai";
import { IoMoon, IoPeople } from "react-icons/io5";
import { MdOutlineDeleteForever, MdOutlineDiscount } from "react-icons/md";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronUp,
  CheckCircle2,
  Clock,
  Coffee,
  DoorOpen,
  Filter,
  Gift,
  GlassWater,
  Loader2,
  Maximize2,
  MessageCircle,
  Minus,
  Percent,
  Plus,
  Sofa,
  Sparkles,
  Star,
  UtensilsCrossed,
  Wifi,
} from "lucide-react";
import { BsPersonVcard } from "react-icons/bs";
import { TbHomeCheck, TbHomeStar } from "react-icons/tb";
import { NationalityCountryInput } from "@/components/booking/NationalityCountryInput";
import { PhoneCountryInput } from "@/components/booking/PhoneCountryInput";
import { useMewsConfiguration } from "@/contexts/MewsConfigurationContext";
import { useBookDisplayCurrency } from "@/hooks/use-book-display-currency";
import { getAvailability } from "@/services/mews-api/availability";
import {
  convertToOccupancies,
  mapMewsRateToLocalRatePlan,
  buildProductPriceMap,
  extractProductPrice,
} from "@/services/mews-api/mappers";
import {
  bookPageOccupancyFitRank,
  buildMewsCategoryBedMaps,
  getBookPageOccupancyCapacity,
  getBookPageOccupancyDisplay,
} from "@/services/mews-api/mews-configuration-bed-capacity";
import {
  getLocalRoomId,
  getMewsRoomCategoryId,
  loadRoomMappingFromEnv,
} from "@/services/mews-api/room-mapping";
import {
  validateGuestDetails,
  mapGuestDetailsToMewsCustomer,
  defaultGuestDetailsForm,
  type GuestDetailsForm,
  type GuestDetailsValidationErrors,
  type GuestDetailsValidationMessages,
} from "@/services/mews-api/guest-details";
import {
  createReservationGroup,
  getReservationPrice,
  getReservationGroup,
  getExceedingReservationIndexes,
} from "@/services/mews-api/reservation-groups";
import {
  canUseOptionB,
  loadPciProxySecureFieldsScript,
  getPaymentCards,
  authorizePaymentCard,
  collectBrowserInfo,
  getMewsApplicationsBaseUrl,
  getApiClient,
  isOnSessionPaymentMode,
  canUseOnSessionPayment,
  getAmountToChargeOnConfirmation,
  buildPaymentRequestRedirectUrl,
  buildPaymentConfirmationReturnUrl,
  encodeMewsReturnUrl,
  resolvePaymentOutcome,
  getChargedAmountFromGroup,
  type PaymentOutcome,
} from "@/services/mews-api";
import {
  getPendingOnSessionReservationGroupId,
  clearPendingOnSessionReservationGroupId,
  markOnSessionPaymentRedirect,
} from "@/services/mews-api/payment-requests";
import type {
  RequiredField,
  CreateReservationGroupResponse,
  CreateReservationGroupReservationData,
  GetReservationGroupResponse,
  GetReservationPriceResponse,
  GetReservationPriceReservationRequest,
  GetReservationPriceReservationResponse,
  CreateReservationGroupCreditCardData,
  Product,
  ProductOrderPricingInfo,
  AgeCategory,
} from "@/services/mews-api/types";
import { SecureFieldsCardInput, type SecureFieldsCardInputRef } from "@/components/booking/SecureFieldsCardInput";
import { CurrencyCodeWithFlag } from "@/components/booking/CurrencyCodeWithFlag";
import { OptimizedImage } from "@/components/OptimizedImage";
import { encodePublicPath } from "@/utils/encodePublicPath";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  collectCurrencyKeysFromProducts,
  sortCurrencySelectorOptions,
} from "@/utils/book-display-currency";
import {
  collectCurrencyKeysFromPrice,
  pickDisplayAmount,
  pickProductPricingAlignedToReference,
  pickProductPricingAmount,
  type MewsMultiCurrencyPrice,
  type ProductPricingValueMap,
} from "@/utils/pick-display-amount";
import { formatBookMoney } from "@/utils/format-book-money";
import { pushSojernBookEvent } from "@/utils/sojernBookDataLayer";
import {
  hasBookPurchaseFired,
  purchaseEmailFromGuestDetails,
  pushBookPurchaseEvent,
  readStashedPurchaseChfSnapshot,
  stashBookPurchaseChfAmount,
  stashBookPurchaseChfSnapshot,
  stashBookPurchaseEmailHash,
  type BookPurchaseGroup,
} from "@/utils/bookPurchaseDataLayer";
import ContactConciergeModal from "@/components/ContactConciergeModal";

// --- Custom room feature icons ---

const RoomSizeIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <path d="M4 8V4H8" />
    <path d="M16 4H20V8" />
    <path d="M20 16V20H16" />
    <path d="M8 20H4V16" />
    <rect x="7" y="7" width="10" height="10" strokeDasharray="2 2" />
    <path d="M2 12H5M19 12H22M12 2V5M12 19V22" strokeWidth="0.5" />
  </svg>
);

const OccupancyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <circle cx="12" cy="7" r="3" />
    <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <path d="M17 11l2 2 4-4" strokeWidth="0.75" />
  </svg>
);

const RoomViewIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <rect x="4" y="3" width="16" height="18" rx="1" />
    <path d="M4 10h16M12 3v18" />
    <path d="M7 14l2-1 3 2 4-3 2 1" strokeWidth="0.75" />
  </svg>
);

const BedIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <path d="M3 12h18M3 7v13M21 7v13M3 11c0-2 2-4 4-4h10c2 0 4 2 4 4" />
    <path d="M7 11V9a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M13 11V9a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
  </svg>
);

const ServiceIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4l1.4-1.4M17 7l1.4-1.4" />
    <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" strokeDasharray="1 1" />
  </svg>
);

const WellnessIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M12 8v8M8 12h8" strokeWidth="0.75" />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

// --- End custom room feature icons ---

/** Desktop skeleton: matches PremiumRoomCard (68% gallery, 35% panel, 6 amenities, description, rates toggle). */
const RoomCardSkeletonPremium = () => (
  <div className="overflow-hidden rounded-[32px] border-0 bg-[rgba(253,253,253,0.65)] shadow-[0_4px_24px_rgba(0,0,0,0.06)] backdrop-blur-[10px]">
    <div className="relative flex h-auto flex-col book:h-[540px] book:flex-row">
      <div
        className={cn(
          "relative h-auto w-full overflow-visible book:h-full book:w-[68%] book:overflow-hidden",
          "aspect-[3/2] book:aspect-auto",
          "rounded-bl-[32px] book:rounded-bl-[32px] book:rounded-br-none book:rounded-tl-[32px] book:rounded-tr-none",
        )}
      >
        <Skeleton className="h-full w-full rounded-none" />
      </div>
      <div className="relative flex w-full min-h-[440px] flex-col overflow-hidden rounded-tr-[32px] rounded-br-[32px] border-r-2 border-white bg-[rgba(253,253,253,0.65)] p-6 book:absolute book:top-0 book:right-0 book:bottom-0 book:min-h-0 book:w-[35%] book:backdrop-blur-[7px] lg:p-8">
        <div className="relative z-10 shrink-0 space-y-1">
          <Skeleton className="h-2.5 w-24 rounded" />
          <Skeleton className="h-8 w-[85%] max-w-[17rem] rounded" />
        </div>
        <div className="relative z-10 mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          <div className="flex w-full shrink-0 flex-col items-end gap-2">
            <Skeleton className="h-2.5 w-14 rounded" />
            <Skeleton className="h-8 w-32 rounded" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col items-start">
            <div className="w-full">
              <div className="grid w-full grid-cols-3 gap-x-2 gap-y-4 border-t border-b border-pine-green/5 py-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex flex-col items-center text-center">
                    <Skeleton className="mb-2 h-6 w-6 rounded" />
                    <Skeleton className="h-2 w-9 rounded" />
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-2 px-4 text-center sm:px-6 lg:text-left">
                <Skeleton className="mx-auto h-[15px] w-full max-w-none rounded lg:mx-0" />
                <Skeleton className="mx-auto h-[15px] w-full max-w-none rounded lg:mx-0" />
                <Skeleton className="mx-auto h-[15px] w-[88%] rounded lg:mx-0" />
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 mt-auto flex w-full shrink-0 flex-col pt-2">
          <div className="w-full border-t border-pine-green/15" aria-hidden />
          <div className="mt-2 flex justify-center px-1">
            <Skeleton className="h-9 w-[min(100%,14rem)] rounded-none" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** Mobile / tablet skeleton: matches RoomCard (carousel, header row, AmenityIconGrid, description, rates toggle). */
const RoomCardSkeletonCompact = () => (
  <div className="flex min-h-[17.5rem] flex-col overflow-hidden rounded-2xl border border-white/50 bg-[rgba(253,253,253,0.8)] shadow-[0_4px_24px_rgba(0,0,0,0.06)] backdrop-blur-[10px]">
    <div className="aspect-[3/2] w-full shrink-0 sm:aspect-video">
      <Skeleton className="h-full w-full rounded-none" />
    </div>
    <div className="shrink-0 space-y-1.5 px-3 pb-2 pt-3 sm:px-6 book:pb-3">
      <div className="flex items-start justify-between gap-3 sm:gap-4">
        <Skeleton className="h-7 min-w-[11rem] max-w-[65%] flex-1 rounded sm:min-w-[13rem] sm:h-8" />
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Skeleton className="h-2.5 w-10 rounded" />
          <Skeleton className="h-6 w-[4.5rem] rounded sm:h-7 sm:w-24" />
        </div>
      </div>
    </div>
    <div className="flex min-h-0 flex-1 flex-col space-y-0 p-0 pb-2">
      <div className="px-3 sm:px-6 book:pt-0">
        <div className="grid grid-cols-3 gap-3 py-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              <Skeleton className="mb-1.5 h-7 w-7 rounded md:h-8 md:w-8 book:h-6 book:w-6" />
              <Skeleton className="h-2.5 w-10 rounded book:h-2 book:w-9" />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 space-y-2 px-4 sm:px-6 lg:hidden">
        <Skeleton className="h-[15px] w-full rounded" />
        <Skeleton className="h-[15px] w-full rounded" />
        <Skeleton className="mx-auto h-[15px] w-[90%] rounded sm:mx-0" />
      </div>
      <div className="mt-auto w-full shrink-0 px-3 pt-3 sm:px-6">
        <div className="w-full border-t border-pine-green/15" aria-hidden />
        <div className="mt-2 flex justify-center px-1">
          <Skeleton className="h-9 w-[min(100%,14rem)] rounded-none" />
        </div>
      </div>
    </div>
  </div>
);
import { useNavigate, useSearchParams } from "react-router-dom";

/** English and localized `/book` routes — used for leave-session guard. */
function pathIsBookRoute(pathname: string): boolean {
  const p = pathname.replace(/\/$/, "") || "/";
  return p === "/book" || p === "/de/book" || p === "/fr/book";
}

/** `history.state` key for the extra entry that makes the first Back fire `popstate` without leaving `/book`. */
const BOOK_LEAVE_TRAP_STATE_KEY = "bookLeaveTrap";
/** Stored in `leaveGuardPendingHrefRef` when the guard was opened from the browser back button. */
const BOOK_LEAVE_POPSTATE = "__bookLeavePopstate__";

/**
 * After the leave dialog closes, a trailing `popstate` can arrive a few ms later while `leaveGuardOpenRef`
 * is already false → the handler would open the modal again (flash). Re-push the trap only, no modal.
 * 10ms is enough in ideal conditions; 50ms absorbs timer/jitter (your logs showed ~4ms).
 */
const LEAVE_GUARD_POST_CLOSE_POPSTATE_MS = 50;

/** If `navigate(-2)` after "Yes" still leaves us on `/book`, try one more `navigate(-1)` after this delay. */
const LEAVE_GUARD_CONFIRM_BACK_RETRY_MS = 50;

/** Clear `leaveViaConfirmedBackRef` if pathname never left `/book` (avoids trapping future Back presses). */
const LEAVE_GUARD_CONFIRM_BACK_WATCHDOG_MS = 800;

/**
 * Merge the leave-trap marker into the current `history.state` so React Router's `idx` / `key` / `usr`
 * stay intact. Replacing state with only `{ bookLeaveTrap }` desyncs the router and can fire duplicate
 * `popstate` handling (e.g. the leave modal opening twice on one Back press).
 */
function historyStateWithBookLeaveTrap(): Record<string, unknown> {
  const s = window.history.state;
  if (s !== null && typeof s === "object" && !Array.isArray(s)) {
    return { ...(s as Record<string, unknown>), [BOOK_LEAVE_TRAP_STATE_KEY]: 1 };
  }
  return { [BOOK_LEAVE_TRAP_STATE_KEY]: 1 };
}

/** Dev-only: `sessionStorage.setItem("DEBUG_BOOK_LEAVE","1");` then reload; open console and use the browser Back button on `/book`. */
function bookLeaveDebugEnabled(): boolean {
  try {
    return (
      import.meta.env.DEV &&
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem("DEBUG_BOOK_LEAVE") === "1"
    );
  } catch {
    return false;
  }
}

function bookLeaveTrace(phase: string, detail?: Record<string, unknown>) {
  if (!bookLeaveDebugEnabled()) return;
  console.log(
    `%c[book-leave]%c ${phase}`,
    "color:#b45309;font-weight:bold",
    "color:inherit",
    { ms: Number(performance.now().toFixed(1)), ...detail },
  );
}

type BookingStep = "rooms" | "addons" | "details" | "confirmation";

type LadderKey = "good" | "better" | "best" | "premium" | "suite";

type RoomAvailabilityRate = {
  pricePerNight: number;
  totalAmount: number;
  rateName?: string;
  mewsPrice?: MewsMultiCurrencyPrice;
};

type RoomAvailability = {
  available: boolean;
  /** Raw Mews `AvailableRoomCount` for this category (same occupancy band as the fetch). */
  availableRoomCount: number;
  mewsRoomCategoryId: string;
  ratesByRateId: Record<string, RoomAvailabilityRate>;
};

function getCheapestMappedAvailabilityRate(
  roomAvailability: RoomAvailability | undefined,
): RoomAvailabilityRate | null {
  if (!roomAvailability) return null;
  let best: RoomAvailabilityRate | null = null;
  for (const [rateId, rate] of Object.entries(roomAvailability.ratesByRateId)) {
    if (mapMewsRateToLocalRatePlan(rateId, rate.rateName) === null) continue;
    if (!best || rate.pricePerNight < best.pricePerNight) best = rate;
  }
  return best;
}

type BookingRoom = {
  key: LadderKey;
  ladderLabel: "Good" | "Better" | "Best" | "Premium" | "Suite";
  displayName: string;
  subtitle: string;
  recommended?: boolean;
  sourceRoom: Room;
};

const formatCHF = (value: number) =>
  new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

const formatBookingAmount = (value: number, currency: string) =>
  currency === "CHF" ? formatCHF(value) : `${currency} ${value.toFixed(2)}`;

/** Shown with MEWS rates: compare-at figure for a 15% direct-booker saving (guest pays the MEWS amount). */
const DIRECT_BOOKER_DISCOUNT_FRACTION = 0.15;

const compareAtPriceForMewsAmount = (mewsAmount: number) =>
  mewsAmount / (1 - DIRECT_BOOKER_DISCOUNT_FRACTION);

/** Browse UI: format room/rate amounts in the selected display currency using stored Mews `Price` maps (no refetch). */
function browseMewsPricing(
  mewsPrice: MewsMultiCurrencyPrice | null | undefined,
  displayCurrency: string,
  chfPricePerNight: number,
  chfTotal: number,
  chargedNights: number,
  locale: string,
): {
  nightlyFormatted: string;
  nightlyCompareAtFormatted: string;
  totalFormatted: string;
  totalCompareAtFormatted: string;
  savingsFormatted: string;
} {
  const nights = Math.max(1, chargedNights);
  const picked = mewsPrice
    ? pickDisplayAmount(mewsPrice, displayCurrency, { settlementCurrencyHint: "CHF" })
    : null;
  if (picked) {
    const totalVal = picked.value;
    const code = picked.currencyCode;
    const nightVal = totalVal / nights;
    const compareNight = compareAtPriceForMewsAmount(nightVal);
    const compareTotal = compareAtPriceForMewsAmount(totalVal);
    return {
      nightlyFormatted: formatBookMoney(nightVal, code, locale),
      nightlyCompareAtFormatted: formatBookMoney(compareNight, code, locale),
      totalFormatted: formatBookMoney(totalVal, code, locale),
      totalCompareAtFormatted: formatBookMoney(compareTotal, code, locale),
      savingsFormatted: formatBookMoney(compareTotal - totalVal, code, locale),
    };
  }
  const compareNight = compareAtPriceForMewsAmount(chfPricePerNight);
  const compareTotal = compareAtPriceForMewsAmount(chfTotal);
  return {
    nightlyFormatted: formatBookMoney(chfPricePerNight, "CHF", locale),
    nightlyCompareAtFormatted: formatBookMoney(compareNight, "CHF", locale),
    totalFormatted: formatBookMoney(chfTotal, "CHF", locale),
    totalCompareAtFormatted: formatBookMoney(compareTotal, "CHF", locale),
    savingsFormatted: formatBookMoney(compareTotal - chfTotal, "CHF", locale),
  };
}

/**
 * Add-ons use a separate model from room: selling ≈ 90% of list (see `getResolvedUpsellPrices` in upsells.ts).
 * From reservation/price product lines, pre-discount ≈ gross / 0.9 per line.
 */
const MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST = 0.9;

const MEWS_PICK_DISPLAY_OPTS = { settlementCurrencyHint: "CHF" as const };

/**
 * Absolute gross sell from `hotels/Products` for enhance-step card display (matches display currency rules).
 */
function pickAbsoluteMewsProductSellForCard(
  prod: Product | undefined,
  displayCurrency: string,
): { value: number; currencyCode: string } | null {
  if (!prod) return null;
  const isAbsolute =
    chargingModeLower(prod.Pricing?.Discriminator) === "absolute";
  if (!isAbsolute) return null;
  let picked = pickProductPricingAmount(
    prod.Pricing.Value as ProductPricingValueMap,
    displayCurrency,
    MEWS_PICK_DISPLAY_OPTS,
  );
  if (!picked) {
    const vDisp = extractProductPrice(prod, displayCurrency);
    if (vDisp != null && vDisp > 0) {
      picked = { value: vDisp, currencyCode: displayCurrency.trim().toUpperCase() };
    } else {
      const vChf = extractProductPrice(prod, "CHF");
      if (vChf != null && vChf > 0) {
        picked = { value: vChf, currencyCode: "CHF" };
      }
    }
  }
  return picked;
}

/**
 * Enhance-step card gross: same alignment rules as {@link accumulateMewsUpsellAddonAmountsInRoomCurrency}
 * (`roomCurrencyCode` from the room rate). When the rate-specific SKU is CHF-only, pass `alignFromProduct`
 * (typically the **standard** base product) so multi-currency / CHF→room ISO matches distributor add-on math.
 */
function pickAbsoluteUpsellGrossForEnhanceCard(
  prod: Product | undefined,
  displayCurrency: string,
  roomCurrencyCode: string,
  alignFromProduct: Product | undefined,
): { value: number; currencyCode: string } | null {
  if (!prod) return null;
  const isAbsolute =
    chargingModeLower(prod.Pricing?.Discriminator) === "absolute";
  if (!isAbsolute) return null;
  const valueMap = prod.Pricing.Value as ProductPricingValueMap;
  const ref = roomCurrencyCode.trim().toUpperCase();
  const alignMap =
    alignFromProduct &&
    chargingModeLower(alignFromProduct.Pricing?.Discriminator) === "absolute"
      ? (alignFromProduct.Pricing.Value as ProductPricingValueMap)
      : null;
  const aligned = pickProductPricingAlignedToReference(
    valueMap,
    displayCurrency,
    ref,
    MEWS_PICK_DISPLAY_OPTS,
    alignMap,
  );
  if (aligned && aligned.currencyCode.trim().toUpperCase() === ref) {
    return aligned;
  }
  return pickAbsoluteMewsProductSellForCard(prod, displayCurrency);
}

function chargingModeLower(mode: string | undefined): string {
  return mode?.trim().toLowerCase() ?? "";
}

/**
 * Fallback when {@link pickAbsoluteUpsellGrossForEnhanceCard} returns null: read CHF gross from the
 * **rate-selected** ProductId (`sellBaseId`) and standard anchor (`listAnchorBaseId`) via `extractProductPrice`
 * / `buildProductPriceMap`. Avoids `u.price` from `getResolvedUpsellPrices`, which is **standard-base only**
 * and makes signature cards match standard when the primary picker fails.
 */
function resolveEnhanceCardChfSellAndListAnchor(
  sellBaseId: string,
  listAnchorBaseId: string | undefined,
  productById: Map<string, Product>,
  products: Product[] | undefined,
): { sellChf: number; listChf: number } | null {
  const sellProd = productById.get(sellBaseId);
  let sellChf =
    sellProd && chargingModeLower(sellProd.Pricing?.Discriminator) === "absolute"
      ? extractProductPrice(sellProd, "CHF")
      : null;
  if (sellChf == null || sellChf <= 0) {
    const m = buildProductPriceMap(products ?? [], "CHF");
    const v = m.get(sellBaseId);
    sellChf = v != null && v > 0 ? v : null;
  }
  if (sellChf == null || sellChf <= 0) return null;

  const listPid = listAnchorBaseId ?? sellBaseId;
  const listProd = productById.get(listPid);
  let listChf =
    listProd && chargingModeLower(listProd.Pricing?.Discriminator) === "absolute"
      ? extractProductPrice(listProd, "CHF")
      : null;
  if (listChf == null || listChf <= 0) {
    const m = buildProductPriceMap(products ?? [], "CHF");
    const v = m.get(listPid);
    listChf = v != null && v > 0 ? v : sellChf;
  }
  return { sellChf, listChf: listChf > 0 ? listChf : sellChf };
}

/**
 * Scales a single-sku Absolute add-on when Mews `ChargingMode` is PerPerson or PerPersonPerTimeUnit:
 * adults × adult unit + children × child unit (optional `mewsChildProductId`), × nights for PerPersonPerTimeUnit.
 */
function blendedPerPersonAbsoluteAddonAmounts(params: {
  upsell: Upsell;
  mainProduct: Product;
  adultValueMap: ProductPricingValueMap;
  adultSell: number;
  adultList: number;
  occupancy: { adults: number; children: number } | undefined;
  chargedNights: number | undefined;
  productById: Map<string, Product>;
  displayCurrency: string;
  roomCurrencyCode: string;
}): { sell: number; list: number } {
  const {
    upsell,
    mainProduct,
    adultValueMap,
    adultSell,
    adultList,
    occupancy,
    chargedNights,
    productById,
    displayCurrency,
    roomCurrencyCode,
  } = params;

  const ref = roomCurrencyCode.trim().toUpperCase();
  const mode = chargingModeLower(mainProduct.ChargingMode);
  const perHead = mode === "perperson" || mode === "perpersonpertimeunit";
  if (!perHead || !occupancy) {
    return { sell: adultSell, list: adultList };
  }

  const timeFactor = mode === "perpersonpertimeunit" ? Math.max(1, chargedNights ?? 1) : 1;
  const adults = Math.max(0, occupancy.adults);
  const children = Math.max(0, occupancy.children);

  let childSell = adultSell;
  let childList = adultList;
  const childPid = upsell.mewsChildProductId;
  if (childPid) {
    const childProd = productById.get(childPid);
    if (childProd?.Pricing?.Discriminator === "Absolute") {
      const childMap = childProd.Pricing.Value as ProductPricingValueMap;
      const cp = pickProductPricingAlignedToReference(
        childMap,
        displayCurrency,
        ref,
        MEWS_PICK_DISPLAY_OPTS,
        adultValueMap,
      );
      if (cp && cp.currencyCode.trim().toUpperCase() === ref) {
        childSell = cp.value;
        childList = cp.value / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST;
      }
    }
  }

  const sell = (adultSell * adults + childSell * children) * timeFactor;
  const list = (adultList * adults + childList * children) * timeFactor;
  return { sell, list };
}

/** Standard variable-count base `Pricing.Value` map — used to align signature duplicate SKUs to room ISO (same idea as enhance-step cards’ `listAnchorProd`). */
function standardVariableCountAnchorValueMap(
  u: Upsell,
  productById: Map<string, Product>,
): ProductPricingValueMap | null {
  const std = u.mewsVariableCount;
  if (!std) return null;
  const p = productById.get(std.base);
  return p?.Pricing?.Discriminator === "Absolute"
    ? (p.Pricing.Value as ProductPricingValueMap)
    : null;
}

/**
 * Gross sell + list-equivalent (gross / 0.9) for one upsell’s MEWS lines using ProductIds for `productRate`.
 * Used to split **selling** (guest’s chosen room rate) from **MRP / compare-at** (always standard SKUs).
 */
function mewsUpsellAddonSellAndListEquivForProductRatePlan(
  u: Upsell,
  count: number,
  productRate: MewsUpsellRatePlan,
  productById: Map<string, Product>,
  displayCurrency: string,
  code: string,
  occupancy?: { adults: number; children: number },
  chargedNights?: number,
): { sell: number; listEq: number } | null {
  const ids = getMewsProductIdsForUpsellForRatePlan(u, count, productRate);
  const varDef = effectiveMewsVariableCountIds(u, productRate);
  const baseProd = varDef ? productById.get(varDef.base) : undefined;
  const baseMap =
    baseProd?.Pricing?.Discriminator === "Absolute"
      ? (baseProd.Pricing.Value as ProductPricingValueMap)
      : null;
  const stdVarAnchorMap = standardVariableCountAnchorValueMap(u, productById);
  const useStdAnchorForSignatureVarBase =
    productRate === "signature" &&
    Boolean(u.mewsVariableCountSignature) &&
    stdVarAnchorMap != null &&
    u.mewsVariableCount != null &&
    varDef != null &&
    u.mewsVariableCount.base !== varDef.base;

  let sell = 0;
  let listEq = 0;

  for (let idx = 0; idx < ids.length; idx++) {
    const productId = ids[idx]!;
    const prod = productById.get(productId);
    if (!prod) return null;

    if (prod.Pricing?.Discriminator === "Absolute") {
      const valueMap = prod.Pricing.Value as ProductPricingValueMap;
      let alignMap: ProductPricingValueMap | null = null;
      if (varDef && baseMap) {
        if (idx > 0) {
          alignMap = baseMap;
        } else if (useStdAnchorForSignatureVarBase) {
          alignMap = stdVarAnchorMap;
        } else {
          alignMap = baseMap;
        }
      }
      const picked = pickProductPricingAlignedToReference(
        valueMap,
        displayCurrency,
        code,
        MEWS_PICK_DISPLAY_OPTS,
        alignMap,
      );
      if (!picked || picked.currencyCode.trim().toUpperCase() !== code) return null;
      let lineSell = picked.value;
      let lineList = picked.value / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST;
      if (!varDef && ids.length === 1) {
        const blended = blendedPerPersonAbsoluteAddonAmounts({
          upsell: u,
          mainProduct: prod,
          adultValueMap: valueMap,
          adultSell: lineSell,
          adultList: lineList,
          occupancy,
          chargedNights,
          productById,
          displayCurrency,
          roomCurrencyCode: code,
        });
        lineSell = blended.sell;
        lineList = blended.list;
      }
      sell += lineSell;
      listEq += lineList;
      continue;
    }

    /**
     * Variable-count 2nd–4th person products are **Relative** in Mews (see `docs/room booking page/upsells.md`):
     * `Value` is `{ ProductIds, Multiplier, Target }`, not a currency map. Incremental gross in display
     * ISO = base absolute gross × ratio, where ratio is `Multiplier/100` when Multiplier looks like a
     * percent (>10), else Multiplier as a direct factor (e.g. 1 = one extra base unit).
     */
    if (prod.Pricing?.Discriminator === "Relative" && baseMap && varDef && idx > 0) {
      const rel = prod.Pricing.Value as { Multiplier?: number };
      const mult = rel?.Multiplier;
      if (mult == null || !Number.isFinite(mult)) return null;
      const basePick = pickProductPricingAlignedToReference(
        baseMap,
        displayCurrency,
        code,
        MEWS_PICK_DISPLAY_OPTS,
        useStdAnchorForSignatureVarBase ? stdVarAnchorMap : baseMap,
      );
      if (!basePick || basePick.currencyCode.trim().toUpperCase() !== code) return null;
      const ratio = mult > 10 ? mult / 100 : mult;
      const increment = basePick.value * ratio;
      if (!Number.isFinite(increment) || increment < -1e-6) return null;
      sell += increment;
      listEq += increment / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST;
      continue;
    }

    return null;
  }

  return { sell, listEq };
}

/**
 * Sums add-on sell from the guest’s **selected** rate (standard vs signature) and list/compare-at from
 * **standard** MEWS SKUs only. Variable-count relative products may lack the guest’s ISO on `Pricing.Value`;
 * amounts are aligned via {@link pickProductPricingAlignedToReference} (CHF ratio from base product).
 */
function accumulateMewsUpsellAddonAmountsInRoomCurrency(params: {
  enhanceStayUpsells: Upsell[];
  upsellCounts: Record<string, number>;
  products: Product[] | undefined;
  displayCurrency: string;
  roomCurrencyCode: string;
  occupancy?: { adults: number; children: number };
  chargedNights?: number;
  ratePlanId: RatePlanId;
}): { sellSum: number; listSum: number } | null {
  const {
    enhanceStayUpsells,
    upsellCounts,
    products,
    displayCurrency,
    roomCurrencyCode,
    occupancy,
    chargedNights,
    ratePlanId,
  } = params;
  const mewsRate: MewsUpsellRatePlan = ratePlanId === "signature" ? "signature" : "standard";
  const code = roomCurrencyCode.trim().toUpperCase();
  const productById = new Map<string, Product>();
  for (const p of products ?? []) {
    productById.set(p.Id, p);
  }

  let addonSellSum = 0;
  let addonListSum = 0;

  for (const u of enhanceStayUpsells) {
    const count = upsellCounts[u.id] ?? 0;
    const forSell = mewsUpsellAddonSellAndListEquivForProductRatePlan(
      u,
      count,
      mewsRate,
      productById,
      displayCurrency,
      code,
      occupancy,
      chargedNights,
    );
    const forList = mewsUpsellAddonSellAndListEquivForProductRatePlan(
      u,
      count,
      "standard",
      productById,
      displayCurrency,
      code,
      occupancy,
      chargedNights,
    );
    if (!forSell || !forList) return null;
    addonSellSum += forSell.sell;
    addonListSum += forList.listEq;
  }

  return { sellSum: addonSellSum, listSum: addonListSum };
}

function sumProductOrderGross(prices: ProductOrderPricingInfo[] | undefined): number {
  if (!prices?.length) return 0;
  return prices.reduce((s, p) => s + (p.TotalAmount?.GrossValue ?? 0), 0);
}

function sumProductOrderListEquivalent(prices: ProductOrderPricingInfo[] | undefined): number {
  if (!prices?.length) return 0;
  return prices.reduce((s, p) => {
    const g = p.TotalAmount?.GrossValue ?? 0;
    if (g <= 0) return s;
    return s + g / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST;
  }, 0);
}

/**
 * Booking summary strikethrough total = room compare-at + add-on list subtotal (two discount models).
 * Room: `compareAtPriceForMewsAmount(roomGross)` only on the room portion.
 * Add-ons: sum of list prices (local) or gross/0.9 from MEWS `ProductOrderPrices`.
 */
function bookingSummaryCompareAtTotal(params: {
  grossFromApi: number | undefined;
  productOrderPrices: ProductOrderPricingInfo[] | undefined;
  roomTotalLocal: number;
  addonTotalLocal: number;
  addonListTotalLocal: number;
}): number {
  const { grossFromApi, productOrderPrices, roomTotalLocal, addonTotalLocal, addonListTotalLocal } = params;

  if (grossFromApi != null) {
    const productGrossSum = sumProductOrderGross(productOrderPrices);
    if (productGrossSum > 1e-6) {
      const roomGross = Math.max(0, grossFromApi - productGrossSum);
      return compareAtPriceForMewsAmount(roomGross) + sumProductOrderListEquivalent(productOrderPrices);
    }
    if (addonTotalLocal > 1e-6) {
      const roomGross = Math.max(0, grossFromApi - addonTotalLocal);
      return compareAtPriceForMewsAmount(roomGross) + addonListTotalLocal;
    }
    return compareAtPriceForMewsAmount(grossFromApi);
  }

  return compareAtPriceForMewsAmount(roomTotalLocal) + addonListTotalLocal;
}

/** Per-stay room row + add-ons row for multi-room summary / charges (API gross split vs browse fallback). */
function getPerStayRoomLineMoneyLabels(
  apiRow:
    | {
        TotalAmount?: { GrossValue?: number; Currency?: string };
        ProductOrderPrices?: ProductOrderPricingInfo[];
      }
    | undefined,
  br: {
    roomTotal: number;
    browse: {
      totalFormatted: string;
      totalCompareAtFormatted: string;
    } | null;
    addonSell: number;
    addonList: number;
  },
  opts?: {
    /** In browse/API-fallback rows, format zero add-ons in this currency (matches guest picker). */
    displayCurrency?: string;
    locale?: string;
  },
): {
  room: { effective: string; compareAt: string | null };
  addons: { effective: string; compareAt: string | null };
} {
  const cur = apiRow?.TotalAmount?.Currency ?? "CHF";
  const gross = apiRow?.TotalAmount?.GrossValue;
  const products = apiRow?.ProductOrderPrices;

  if (gross != null && Number.isFinite(gross)) {
    const productGross = sumProductOrderGross(products);
    const roomEff = Math.max(0, gross - productGross);
    const roomCompare = compareAtPriceForMewsAmount(roomEff);
    const roomCompareFmt = roomCompare > roomEff + 1e-6 ? formatBookingAmount(roomCompare, cur) : null;
    const roomEffFmt = formatBookingAmount(roomEff, cur);

    let addonEff = productGross;
    let addonListEquiv = sumProductOrderListEquivalent(products);
    if (addonEff < 1e-6 && br.addonSell > 1e-6) {
      addonEff = br.addonSell;
      addonListEquiv = br.addonList;
    }
    const addonCompareFmt =
      addonListEquiv > addonEff + 1e-6 ? formatBookingAmount(addonListEquiv, cur) : null;
    const addonEffFmt = formatBookingAmount(addonEff, cur);

    return {
      room: { effective: roomEffFmt, compareAt: roomCompareFmt },
      addons: { effective: addonEffFmt, compareAt: addonCompareFmt },
    };
  }

  const roomEffFmt =
    br.browse?.totalFormatted ?? (br.roomTotal > 1e-6 ? formatCHF(br.roomTotal) : "—");
  const roomCompareFmt = br.browse && br.roomTotal > 1e-6 ? br.browse.totalCompareAtFormatted : null;
  const dc = opts?.displayCurrency;
  const loc = opts?.locale;
  const addonEffFmt =
    br.addonSell < 1e-6 && dc && loc
      ? formatBookMoney(0, dc, loc)
      : formatCHF(br.addonSell);
  const addonCompareFmt = br.addonList > br.addonSell + 1e-6 ? formatCHF(br.addonList) : null;

  return {
    room: { effective: roomEffFmt, compareAt: roomCompareFmt },
    addons: { effective: addonEffFmt, compareAt: addonCompareFmt },
  };
}

/**
 * Booking-summary total / compare-at / savings in the guest’s display currency when room `Price` and
 * each selected product `Pricing.Value` all resolve to the same ISO code (no client-side FX).
 */
function tryFormatBookingSummaryInDisplayCurrency(params: {
  roomMewsPrice: MewsMultiCurrencyPrice | null | undefined;
  displayCurrency: string;
  locale: string;
  products: Product[] | undefined;
  enhanceStayUpsells: Upsell[];
  selectedUpsells: Record<string, number>;
  occupancy: { adults: number; children: number };
  chargedNights: number;
  ratePlanId: RatePlanId;
}): {
  effectiveFormatted: string;
  compareAtFormatted: string | null;
  savingsFormatted: string | null;
  hasSavingsPill: boolean;
} | null {
  const {
    roomMewsPrice,
    displayCurrency,
    locale,
    products,
    enhanceStayUpsells,
    selectedUpsells,
    occupancy,
    chargedNights,
    ratePlanId,
  } = params;
  const roomPick = pickDisplayAmount(roomMewsPrice, displayCurrency, { settlementCurrencyHint: "CHF" });
  if (!roomPick) return null;

  const code = roomPick.currencyCode;
  const addonTotals = accumulateMewsUpsellAddonAmountsInRoomCurrency({
    enhanceStayUpsells,
    upsellCounts: selectedUpsells,
    products,
    displayCurrency,
    roomCurrencyCode: code,
    occupancy,
    chargedNights,
    ratePlanId,
  });
  if (!addonTotals) return null;
  const { sellSum: addonSellSum, listSum: addonListSum } = addonTotals;

  const effectiveVal = roomPick.value + addonSellSum;
  const compareAtVal = compareAtPriceForMewsAmount(roomPick.value) + addonListSum;
  const hasCompareAt = compareAtVal > effectiveVal + 1e-6;
  const savingsVal = hasCompareAt ? compareAtVal - effectiveVal : 0;

  return {
    effectiveFormatted: formatBookMoney(effectiveVal, code, locale),
    compareAtFormatted: hasCompareAt ? formatBookMoney(compareAtVal, code, locale) : null,
    savingsFormatted: savingsVal > 0.005 ? formatBookMoney(savingsVal, code, locale) : null,
    hasSavingsPill: savingsVal > 0.005,
  };
}

/** Per-stay room/add-on rows + combined stay subtotal in display currency (same rules as `tryFormatBookingSummaryInDisplayCurrency`). */
type StaySummaryDisplayLines = {
  currencyCode: string;
  stayEffective: number;
  stayCompareAt: number;
  room: { effective: string; compareAt: string | null };
  addons: { effective: string; compareAt: string | null };
  stayEffectiveFormatted: string;
  stayCompareAtFormatted: string | null;
};

function tryFormatStayRoomLinesInDisplayCurrency(params: {
  mewsPrice: MewsMultiCurrencyPrice | null | undefined;
  /**
   * When the rate has no `Price` map but browse still has a CHF settlement total, use this so
   * per-stay display lines (and massage add-ons in CHF) can resolve like `browseMewsPricing`’s fallback.
   */
  settlementRoomGrossChf?: number;
  displayCurrency: string;
  locale: string;
  products: Product[] | undefined;
  enhanceStayUpsells: Upsell[];
  addonUpsellCounts: Record<string, number>;
  adults: number;
  children: number;
  chargedNights: number;
  ratePlanId: RatePlanId;
}): StaySummaryDisplayLines | null {
  const {
    mewsPrice,
    settlementRoomGrossChf,
    displayCurrency,
    locale,
    products,
    enhanceStayUpsells,
    addonUpsellCounts,
    adults,
    children,
    chargedNights,
    ratePlanId,
  } = params;
  let roomPick = pickDisplayAmount(mewsPrice, displayCurrency, { settlementCurrencyHint: "CHF" });
  const displayCodeUpper = displayCurrency.trim().toUpperCase();
  if (
    !roomPick &&
    settlementRoomGrossChf != null &&
    settlementRoomGrossChf > 1e-6 &&
    displayCodeUpper === "CHF"
  ) {
    roomPick = { value: settlementRoomGrossChf, currencyCode: "CHF" };
  }
  if (!roomPick) return null;

  const code = roomPick.currencyCode;
  const addonTotals = accumulateMewsUpsellAddonAmountsInRoomCurrency({
    enhanceStayUpsells,
    upsellCounts: addonUpsellCounts,
    products,
    displayCurrency,
    roomCurrencyCode: code,
    occupancy: { adults, children },
    chargedNights,
    ratePlanId,
  });
  if (!addonTotals) return null;
  const { sellSum: addonSellSum, listSum: addonListSum } = addonTotals;

  const roomCompareVal = compareAtPriceForMewsAmount(roomPick.value);
  const roomCompareFmt =
    roomCompareVal > roomPick.value + 1e-6 ? formatBookMoney(roomCompareVal, code, locale) : null;

  const addonCompareFmt =
    addonListSum > addonSellSum + 1e-6 ? formatBookMoney(addonListSum, code, locale) : null;

  const stayEff = roomPick.value + addonSellSum;
  const stayCmp = roomCompareVal + addonListSum;
  const stayCompareFmt =
    stayCmp > stayEff + 1e-6 ? formatBookMoney(stayCmp, code, locale) : null;

  return {
    currencyCode: code,
    stayEffective: stayEff,
    stayCompareAt: stayCmp,
    room: {
      effective: formatBookMoney(roomPick.value, code, locale),
      compareAt: roomCompareFmt,
    },
    addons: {
      effective: formatBookMoney(addonSellSum, code, locale),
      compareAt: addonCompareFmt,
    },
    stayEffectiveFormatted: formatBookMoney(stayEff, code, locale),
    stayCompareAtFormatted: stayCompareFmt,
  };
}

/**
 * Add-on subtotal + compare-at from `hotels/Products` in the guest’s display currency (no room slice).
 * Used when browse shows the room in display ISO but `getPerStayRoomLineMoneyLabels` would format add-ons in CHF.
 */
function tryFormatAddonBundleInDisplayCurrency(params: {
  displayCurrency: string;
  locale: string;
  products: Product[] | undefined;
  enhanceStayUpsells: Upsell[];
  addonUpsellCounts: Record<string, number>;
  occupancy: { adults: number; children: number };
  chargedNights: number;
  ratePlanId: RatePlanId;
}): { effective: string; compareAt: string | null } | null {
  const {
    displayCurrency,
    locale,
    products,
    enhanceStayUpsells,
    addonUpsellCounts,
    occupancy,
    chargedNights,
    ratePlanId,
  } = params;
  const guestCode = displayCurrency.trim().toUpperCase();
  const mewsRate = ratePlanId === "signature" ? "signature" : "standard";

  let anyProductId = false;
  for (const u of enhanceStayUpsells) {
    const count = addonUpsellCounts[u.id] ?? 0;
    if (getMewsProductIdsForUpsellForRatePlan(u, count, mewsRate).length > 0) anyProductId = true;
  }
  if (!anyProductId) {
    return {
      effective: formatBookMoney(0, guestCode, locale),
      compareAt: null,
    };
  }

  const totals = accumulateMewsUpsellAddonAmountsInRoomCurrency({
    enhanceStayUpsells,
    upsellCounts: addonUpsellCounts,
    products,
    displayCurrency,
    roomCurrencyCode: guestCode,
    occupancy,
    chargedNights,
    ratePlanId,
  });
  if (!totals) return null;
  const { sellSum, listSum } = totals;
  const compareFmt = listSum > sellSum + 1e-6 ? formatBookMoney(listSum, guestCode, locale) : null;
  return {
    effective: formatBookMoney(sellSum, guestCode, locale),
    compareAt: compareFmt,
  };
}

function tryFormatMultiStayBookingSummaryInDisplayCurrency(params: {
  lines: (StaySummaryDisplayLines | null | undefined)[];
  /**
   * When true at index i, `lines[i]` must be non-null or we abandon display-currency aggregation
   * (prevents totals that omit a whole planned stay — e.g. missing `mewsPrice` on one row while another
   * stay shows massage add-ons only).
   */
  plannedStayByIndex: boolean[];
  locale: string;
}): {
  effectiveFormatted: string;
  compareAtFormatted: string | null;
  savingsFormatted: string | null;
  hasSavingsPill: boolean;
} | null {
  const { lines, plannedStayByIndex, locale } = params;
  if (lines.length !== plannedStayByIndex.length) return null;
  if (plannedStayByIndex.some((planned, i) => planned && lines[i] == null)) {
    return null;
  }
  const resolved = lines.filter((l): l is StaySummaryDisplayLines => l != null);
  if (!resolved.length) return null;
  const code = resolved[0]!.currencyCode;
  let eff = 0;
  let cmp = 0;
  for (const l of resolved) {
    if (l.currencyCode !== code) return null;
    eff += l.stayEffective;
    cmp += l.stayCompareAt;
  }
  const hasCompareAt = cmp > eff + 1e-6;
  const savingsVal = hasCompareAt ? cmp - eff : 0;
  return {
    effectiveFormatted: formatBookMoney(eff, code, locale),
    compareAtFormatted: hasCompareAt ? formatBookMoney(cmp, code, locale) : null,
    savingsFormatted: savingsVal > 0.005 ? formatBookMoney(savingsVal, code, locale) : null,
    hasSavingsPill: savingsVal > 0.005,
  };
}

function resolvePerStaySummaryLineMoney(
  displayLine: StaySummaryDisplayLines | null | undefined,
  apiRow:
    | {
        TotalAmount?: { GrossValue?: number; Currency?: string };
        ProductOrderPrices?: ProductOrderPricingInfo[];
      }
    | undefined,
  br: {
    roomTotal: number;
    browse: {
      totalFormatted: string;
      totalCompareAtFormatted: string;
    } | null;
    addonSell: number;
    addonList: number;
  },
  opts?: {
    displayCurrency: string;
    locale: string;
    products?: Product[];
    enhanceStayUpsells?: Upsell[];
    addonUpsellCounts?: Record<string, number>;
    occupancy?: { adults: number; children: number };
    chargedNights?: number;
    ratePlanId?: RatePlanId;
  },
): {
  room: { effective: string; compareAt: string | null };
  addons: { effective: string; compareAt: string | null };
} {
  if (displayLine) {
    return { room: displayLine.room, addons: displayLine.addons };
  }
  const gross = apiRow?.TotalAmount?.GrossValue;
  const hasApiGross = gross != null && Number.isFinite(gross);

  const base = getPerStayRoomLineMoneyLabels(apiRow, br, {
    displayCurrency: opts?.displayCurrency,
    locale: opts?.locale,
  });

  const ad =
    opts?.products &&
    opts.enhanceStayUpsells &&
    opts.addonUpsellCounts &&
    opts.displayCurrency &&
    opts.locale &&
    opts.occupancy &&
    opts.chargedNights != null
      ? tryFormatAddonBundleInDisplayCurrency({
          displayCurrency: opts.displayCurrency,
          locale: opts.locale,
          products: opts.products,
          enhanceStayUpsells: opts.enhanceStayUpsells,
          addonUpsellCounts: opts.addonUpsellCounts,
          occupancy: opts.occupancy,
          chargedNights: opts.chargedNights,
          ratePlanId: opts.ratePlanId ?? "standard",
        })
      : null;

  if (ad) {
    /** Prefer catalogue/browse display currency for the room row too when API returns CHF-only totals. */
    if (hasApiGross && br.browse != null && br.roomTotal > 1e-6) {
      return {
        room: {
          effective: br.browse.totalFormatted,
          compareAt: br.browse.totalCompareAtFormatted,
        },
        addons: ad,
      };
    }
    if (!hasApiGross) {
      return { room: base.room, addons: ad };
    }
  }

  return base;
}

/** Same pill as rate card: direct booker label + save line, responsive stack vs inline at `book:`. */
function SummaryDirectBookerPill({
  amountFormatted,
  book,
}: {
  amountFormatted: string;
  book: BookPageTranslations;
}) {
  const saveLine = book.rateCard.youSaved.replace("{{amount}}", amountFormatted);
  return (
    <div className="flex w-full justify-center pt-1">
      <div className="inline-flex min-w-0 max-w-full items-start gap-1.5 rounded-full border border-pine-green/25 bg-pine-green/[0.06] px-2.5 py-1.5 book:items-center">
        <span
          className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pine-green text-white book:mt-0"
          aria-hidden
        >
          <Check className="h-3 w-3 stroke-[3]" />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5 text-left book:hidden">
          <span className="text-[9px] font-bold uppercase leading-tight tracking-[0.06em] text-neutral-900 sm:text-[10px] sm:tracking-[0.06em]">
            {book.directBookerDiscountLabel}
          </span>
          <span className="text-[9px] font-medium uppercase leading-tight tracking-[0.06em] text-pine-green sm:text-[10px] sm:tracking-[0.06em]">
            {saveLine}
          </span>
        </span>
        <span className="hidden min-w-0 flex-1 flex-wrap items-center gap-x-1.5 text-left text-[10px] uppercase leading-tight tracking-[0.06em] text-neutral-900 book:inline-flex">
          <span className="font-bold">{book.directBookerDiscountLabel}</span>
          <span className="font-medium text-pine-green">{saveLine}</span>
        </span>
      </div>
    </div>
  );
}

function bookingLocale(language: Language): string {
  if (language === "de") return "de-CH";
  if (language === "fr") return "fr-CH";
  return "en-GB";
}

/** Splits "Main — Subtitle" into [main, subtitle]. Supports hyphen and en/em dash (DE/FR copy). */
const splitDisplayName = (displayName: string): [string, string | null] => {
  const separators = [" - ", " – ", " — "] as const;
  for (const sep of separators) {
    const idx = displayName.indexOf(sep);
    if (idx !== -1) {
      return [displayName.slice(0, idx).trim(), displayName.slice(idx + sep.length).trim()];
    }
  }
  return [displayName.trim(), null];
};

/** Renders room name as main title + optional subtitle (after " - "). `compact` keeps one size tier for summary rows. */
const RoomNameDisplay = ({
  displayName,
  className = "",
  variant = "default",
}: {
  displayName: string;
  className?: string;
  variant?: "default" | "compact";
}) => {
  const [main, subtitle] = splitDisplayName(displayName);
  return (
    <span className={className}>
      <span className={variant === "compact" ? "font-medium text-neutral-800" : undefined}>{main}</span>
      {subtitle != null && (
        <span
          className={cn(
            "mt-0.5 block font-normal leading-snug",
            variant === "compact"
              ? "text-xs text-neutral-500"
              : "pl-3 text-sm text-neutral-700",
          )}
        >
          {subtitle}
        </span>
      )}
    </span>
  );
};

function MultiStayRoomPriceLine({
  compareAt,
  effective,
  effectiveClassName = "font-medium tabular-nums text-pine-green",
}: {
  compareAt: string | null;
  effective: string;
  effectiveClassName?: string;
}) {
  return (
    <span className="flex flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0">
      {compareAt ? (
        <span className="tabular-nums text-rose-950/50 line-through decoration-rose-400/45">
          {compareAt}
        </span>
      ) : null}
      <span className={effectiveClassName}>{effective}</span>
    </span>
  );
}

type MultiRoomStayAddonLine = {
  id: string;
  name: string;
  count: number;
  sellFormatted: string;
};

function MultiRoomAddonsSummaryRow({
  lineMoney,
  addonLines,
  expanded,
  onToggle,
  label,
  toggleAriaLabel,
  rowClassName,
  effectiveClassName,
}: {
  lineMoney: { addons: { compareAt: string | null; effective: string } } | null;
  addonLines: MultiRoomStayAddonLine[];
  expanded: boolean;
  onToggle: () => void;
  label: string;
  toggleAriaLabel: string;
  rowClassName: string;
  effectiveClassName?: string;
}) {
  if (addonLines.length === 0) {
    return (
      <div className={cn("flex items-center justify-between", rowClassName)}>
        <span>{label}</span>
        {lineMoney ? (
          <MultiStayRoomPriceLine
            compareAt={lineMoney.addons.compareAt}
            effective={lineMoney.addons.effective}
            effectiveClassName={effectiveClassName}
          />
        ) : (
          <span className={cn("font-medium tabular-nums text-pine-green", effectiveClassName)}>—</span>
        )}
      </div>
    );
  }
  return (
    <>
      <button
        type="button"
        className={cn("flex w-full items-center justify-between gap-2 text-left", rowClassName)}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={toggleAriaLabel}
      >
        <span className="flex min-w-0 items-center gap-1">
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-neutral-500 transition-transform duration-200",
              expanded && "rotate-180",
            )}
            aria-hidden
          />
          <span className="min-w-0">{label}</span>
        </span>
        {lineMoney ? (
          <MultiStayRoomPriceLine
            compareAt={lineMoney.addons.compareAt}
            effective={lineMoney.addons.effective}
            effectiveClassName={effectiveClassName}
          />
        ) : (
          <span className={cn("font-medium tabular-nums text-pine-green", effectiveClassName)}>—</span>
        )}
      </button>
      {expanded ? (
        <div
          className="space-y-1 border-t border-pine-green/10 pt-1.5 pl-1 sm:pl-1.5"
          role="region"
          aria-label={label}
        >
          {addonLines.map((line) => (
            <div key={line.id} className="flex justify-between gap-2 text-xs text-neutral-700">
              <span className="min-w-0 leading-snug">
                {line.name}
                {line.count > 1 ? ` × ${line.count}` : ""}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-pine-green">{line.sellFormatted}</span>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const pickRoom = (id: Room["id"]): Room => {
  const found = ROOMS_DATA.find((r) => r.id === id);
  if (!found) throw new Error(`Room not found: ${id}`);
  return found;
};

// TODO: MEWS INTEGRATION - This ladder logic maps local content to Mews categories.
// Eventually, 'sourceRoom' will be matched against Mews availability response.
const ROOM_LADDER: BookingRoom[] = [
  // --- Suites ---
  {
    key: "suite",
    ladderLabel: "Suite",
    displayName: "Alpine Junior Suite - Panorama View",
    subtitle: "Separate living area • Panoramic Mountain View",
    recommended: true,
    sourceRoom: pickRoom("junior-suite"),
  },
  {
    key: "premium",
    ladderLabel: "Suite",
    displayName: "Heritage Family Suite - Jungfrau View",
    subtitle: "2 Bedrooms • Private Balcony • Panoramic View",
    sourceRoom: pickRoom("family-suite"),
  },

  // --- Double Rooms ---
  {
    key: "premium",
    ladderLabel: "Premium",
    displayName: "Jungfrau Superior Double - Mountain & Lake View",
    subtitle: "Balcony • Jungfrau + Lake Thun • Larger",
    recommended: true,
    sourceRoom: pickRoom("superior-double-room"),
  },
  {
    key: "best",
    ladderLabel: "Best",
    displayName: "Alpine Double/Twin - Mountain & Lake View Balcony",
    subtitle: "Balcony • Signature Jungfrau View",
    sourceRoom: pickRoom("double-twin-balcony-lake"),
  },
  {
    key: "better",
    ladderLabel: "Better",
    displayName: "Alpine Double/Twin - Village View Balcony",
    subtitle: "Flexible bedding (Double or Twin) • Balcony • Village Side",
    sourceRoom: pickRoom("double-twin-balcony-village"),
  },
  {
    key: "good",
    ladderLabel: "Good",
    displayName: "Classic Double/Twin",
    subtitle: "Comfortable retreat • Standard View",
    sourceRoom: pickRoom("double-twin-noBal"),
  },

  // --- Triple ---
  {
    key: "good",
    ladderLabel: "Good",
    displayName: "Classic Triple Room - Alpine Comfort",
    subtitle: "Spacious • Village View",
    sourceRoom: pickRoom("standard-triple-room"),
  },

  // --- Singles ---
  {
    key: "best",
    ladderLabel: "Best",
    displayName: "Jungfrau Single - Mountain & Lake View Balcony",
    subtitle: "Private Balcony • Majestic View",
    recommended: true,
    sourceRoom: pickRoom("single-balcony-lake"),
  },
  {
    key: "better",
    ladderLabel: "Better",
    displayName: "Heritage Single - Village View",
    subtitle: "Extra Space • Standard View",
    sourceRoom: pickRoom("heritage-single"),
  },
  {
    key: "good",
    ladderLabel: "Good",
    displayName: "Classic Single Room - Alpine Comfort",
    subtitle: "Essential Comfort • Standard View",
    sourceRoom: pickRoom("basic-single"),
  },
];

/**
 * Ladder rooms that fit `occupancy`, have Mews availability, and at least one rate mapped to a local plan
 * (same notion of “bookable” as standard price on room cards).
 */
function getBookableLadderLocalRoomIdsForOccupancy(
  normalized: Record<string, RoomAvailability>,
  occupancy: number,
  beds: Pick<
    ReturnType<typeof buildMewsCategoryBedMaps>,
    "normalByCategoryId" | "extraByCategoryId"
  >,
): string[] {
  const bookable: string[] = [];
  for (const room of ROOM_LADDER) {
    const id = room.sourceRoom.id;
    const fallback = room.sourceRoom.capacity.maxOccupancy;
    const mewsId = getMewsRoomCategoryId(id);
    const cap = getBookPageOccupancyCapacity(id, mewsId, beds, fallback);
    if (cap < occupancy) continue;

    const raw = normalized[id];
    if (!raw?.available || (raw.availableRoomCount ?? 0) <= 0) continue;
    const hasMappedRate = Object.entries(raw.ratesByRateId).some(([rateId, rate]) => {
      return mapMewsRateToLocalRatePlan(rateId, rate.rateName) !== null;
    });
    if (hasMappedRate) bookable.push(id);
  }
  return bookable;
}

// TODO: MEWS INTEGRATION - Rate plans will be fetched from Mews API.
// Remove mock multiplier logic when real pricing is available.
type RatePlanId = "standard" | "signature";

function staySummaryEnhancementDiscountLine(
  selectedRatePlanId: RatePlanId | null,
  summary: BookPageTranslations["summary"],
): string {
  if (selectedRatePlanId == null) return summary.enhancementDiscountNoRate;
  if (selectedRatePlanId === "signature") return summary.enhancementDiscountSignature;
  return summary.enhancementDiscountStandard;
}

type RatePlan = {
  id: RatePlanId;
  name: string;
  multiplier: number; // applied to nightly room rate
  included: string[];
};

const MAX_STAY_ROOMS = 5;
/** Max guests (adults + children) per stay row in criteria steppers. */
const MAX_GUESTS_PER_STAY_ROW = 10;
/** Per-axis caps match total cap so steppers are limited only by adults + children ≤ {@link MAX_GUESTS_PER_STAY_ROW}. */
const MAX_ADULTS_PER_STAY_ROW = MAX_GUESTS_PER_STAY_ROW;
const MAX_CHILDREN_PER_STAY_ROW = MAX_GUESTS_PER_STAY_ROW;

function clampStayRowOccupancy(
  adults: number,
  children: number,
): { adults: number; children: number } {
  let a = clamp(adults, 1, MAX_ADULTS_PER_STAY_ROW);
  let c = clamp(children, 0, MAX_CHILDREN_PER_STAY_ROW);
  if (a + c <= MAX_GUESTS_PER_STAY_ROW) return { adults: a, children: c };
  while (a + c > MAX_GUESTS_PER_STAY_ROW) {
    if (c > 0) c--;
    else if (a > 1) a--;
    else break;
  }
  return { adults: a, children: c };
}

/** One logical room for multi-room booking (Phase 1 — plan loop). */
type StayRoom = {
  id: string;
  /** Localized label, e.g. "Stay 1" — kept in sync with row index when rows change */
  label: string;
  adults: number;
  children: number;
  selectedRoomId: string | null;
  selectedRatePlanId: RatePlanId | null;
  selectedMewsRateId: string | null;
  mewsRoomCategoryId: string | null;
  /** Enhance step: upsell id → count (0 omitted). Drives `selectedProductIds`. */
  addonUpsellCounts: Record<string, number>;
  /** Mews ProductIds for this room’s add-ons (kept in sync with `addonUpsellCounts`). */
  selectedProductIds: string[];
};

function recomputeStayRoomSelectedProductIds(
  addonUpsellCounts: Record<string, number>,
  upsells: Upsell[],
  selectedRatePlanId: RatePlanId | null,
): string[] {
  const plan = selectedRatePlanId === "signature" ? "signature" : "standard";
  return upsells.flatMap((u) =>
    getMewsProductIdsForUpsellForRatePlan(u, addonUpsellCounts[u.id] ?? 0, plan),
  );
}

function withUpdatedStayRoomAddons(
  sr: StayRoom,
  upsellId: string,
  count: number,
  upsells: Upsell[],
): StayRoom {
  const nextCounts = { ...sr.addonUpsellCounts };
  if (count <= 0) delete nextCounts[upsellId];
  else nextCounts[upsellId] = count;
  return {
    ...sr,
    addonUpsellCounts: nextCounts,
    selectedProductIds: recomputeStayRoomSelectedProductIds(nextCounts, upsells, sr.selectedRatePlanId),
  };
}

/** Sum upsell counts across all stay rooms (summary / display-currency helpers). */
function mergeAddonUpsellCountsAcrossStayRooms(rooms: StayRoom[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const sr of rooms) {
    for (const [id, n] of Object.entries(sr.addonUpsellCounts)) {
      if (n > 0) out[id] = (out[id] ?? 0) + n;
    }
  }
  return out;
}

function createStayRoom(id: string, adults: number, children: number, label: string): StayRoom {
  const o = clampStayRowOccupancy(adults, children);
  return {
    id,
    label,
    adults: o.adults,
    children: o.children,
    selectedRoomId: null,
    selectedRatePlanId: null,
    selectedMewsRateId: null,
    mewsRoomCategoryId: null,
    addonUpsellCounts: {},
    selectedProductIds: [],
  };
}

function normalizeStayRoomLabels(rooms: StayRoom[], template: string): StayRoom[] {
  return rooms.map((sr, i) => ({
    ...sr,
    label: template.replace(/\{\{n\}\}/g, String(i + 1)),
  }));
}

function stayRoomsOccupancyKey(rooms: StayRoom[]): string {
  return rooms.map((r) => `${r.adults},${r.children}`).join("|");
}

/**
 * Dedupes `getAvailability` per logical stay room + dates + that row's occupancy + **all** criteria guest rows.
 * Including `criteriaOccupancyKey` avoids skipping the network call when another room's +/- or add/remove
 * changes while the active plan row's adults/children are unchanged (Mews still uses only this row's `OccupancyData`).
 */
function buildStayRoomAvailabilityFetchKey(
  stayRoomId: string,
  checkIn: string,
  checkOut: string,
  adults: number,
  children: number,
  criteriaOccupancyKey: string,
): string {
  return `${stayRoomId}|${checkIn}|${checkOut}|${adults}|${children}|${criteriaOccupancyKey}`;
}

function clearedStayRoomPlan(sr: StayRoom): StayRoom {
  return {
    ...sr,
    selectedRoomId: null,
    selectedRatePlanId: null,
    selectedMewsRateId: null,
    mewsRoomCategoryId: null,
    addonUpsellCounts: {},
    selectedProductIds: [],
  };
}

type ApplyOccupancySplitResult =
  | { ok: true; rooms: StayRoom[]; splitOccurred: boolean }
  | { ok: false; reason: "max_rooms" | "insufficient_adults" };

/**
 * First overlay Apply: expand rows with T > 3 per split-stay-occupancy rules (T === 3 stays one row until
 * a post-availability pass may split when only the family suite is bookable — see fetch handler).
 */
function applyOccupancySplitToStayRooms(
  prev: StayRoom[],
  labelTemplate: string,
  nextId: () => string,
  maxRooms: number,
): ApplyOccupancySplitResult {
  const newRooms: StayRoom[] = [];
  let splitOccurred = false;
  let anyStructureChange = false;

  for (let i = 0; i < prev.length; i++) {
    const sr = prev[i]!;
    const T = sr.adults + sr.children;
    const remainingOriginal = prev.length - i - 1;
    if (T <= 2) {
      newRooms.push(sr);
      continue;
    }
    if (T === 3) {
      newRooms.push(sr);
      continue;
    }
    const res = splitOccupancyIntoRooms({ adults: sr.adults, children: sr.children });
    if (!res.ok) return res;
    if (newRooms.length + res.rows.length + remainingOriginal > maxRooms) {
      return { ok: false, reason: "max_rooms" };
    }
    if (res.rows.length === 1) {
      newRooms.push(sr);
      continue;
    }
    splitOccurred = true;
    anyStructureChange = true;
    for (const occ of res.rows) {
      newRooms.push(
        clearedStayRoomPlan(createStayRoom(nextId(), occ.adults, occ.children, "")),
      );
    }
  }

  if (!anyStructureChange) {
    return { ok: true, rooms: prev, splitOccurred: false };
  }
  return {
    ok: true,
    rooms: normalizeStayRoomLabels(newRooms, labelTemplate),
    splitOccurred,
  };
}

/** After availability: split every stay row with exactly 3 guests into 2+1 (family-suite-only case). */
function splitAllThreeGuestStayRows(
  prev: StayRoom[],
  labelTemplate: string,
  nextId: () => string,
  maxRooms: number,
): ApplyOccupancySplitResult {
  const newRooms: StayRoom[] = [];
  let splitOccurred = false;
  let anyStructureChange = false;

  for (let i = 0; i < prev.length; i++) {
    const sr = prev[i]!;
    const T = sr.adults + sr.children;
    const remainingOriginal = prev.length - i - 1;
    if (T !== 3) {
      newRooms.push(sr);
      continue;
    }
    const res = splitOccupancyIntoRooms({ adults: sr.adults, children: sr.children });
    if (!res.ok) return res;
    if (newRooms.length + res.rows.length + remainingOriginal > maxRooms) {
      return { ok: false, reason: "max_rooms" };
    }
    if (res.rows.length === 1) {
      newRooms.push(sr);
      continue;
    }
    splitOccurred = true;
    anyStructureChange = true;
    for (const occ of res.rows) {
      newRooms.push(
        clearedStayRoomPlan(createStayRoom(nextId(), occ.adults, occ.children, "")),
      );
    }
  }

  if (!anyStructureChange) {
    return { ok: true, rooms: prev, splitOccurred: false };
  }
  return {
    ok: true,
    rooms: normalizeStayRoomLabels(newRooms, labelTemplate),
    splitOccurred,
  };
}

function countEarlierSameMewsCategorySelections(
  stayRooms: StayRoom[],
  planRoomIndex: number,
  mewsCategoryId: string,
): number {
  let n = 0;
  for (let i = 0; i < planRoomIndex; i++) {
    const sr = stayRooms[i];
    const cat =
      sr.mewsRoomCategoryId ??
      (sr.selectedRoomId ? getMewsRoomCategoryId(sr.selectedRoomId) : null);
    if (cat && cat === mewsCategoryId && cat !== "unmapped") n++;
  }
  return n;
}

/** Occupancy phrase for the active plan step subtitle (uses bar guest counter labels). */
function formatStayRoomOccupancySummary(sr: StayRoom, book: BookPageTranslations): string {
  const bar = book.bar;
  const a = sr.adults;
  const c = sr.children;
  const adultWord = a === 1 ? bar.guestCounterAdultSingular : bar.guestCounterAdultPlural;
  const childWord = c === 1 ? bar.guestCounterChildSingular : bar.guestCounterChildPlural;
  if (c === 0) return `${a} ${adultWord}`;
  return `${a} ${adultWord}, ${c} ${childWord}`;
}

function effectiveRoomAvailabilityForPlanStep(
  raw: RoomAvailability | undefined,
  stayRooms: StayRoom[],
  planRoomIndex: number,
): RoomAvailability | undefined {
  if (!raw) return undefined;
  const usedEarlier = countEarlierSameMewsCategorySelections(
    stayRooms,
    planRoomIndex,
    raw.mewsRoomCategoryId,
  );
  const rawCount = raw.availableRoomCount ?? 0;
  const effectiveCount = rawCount - usedEarlier;
  const hasRates = Object.keys(raw.ratesByRateId).length > 0;
  return {
    ...raw,
    available: effectiveCount > 0 && hasRates,
  };
}

function addonSellAndListForStayRoom(
  sr: StayRoom,
  upsells: Upsell[],
  products: Product[] | undefined,
  chargedNights: number,
  mewsPricing?: {
    displayCurrency: string;
    roomCurrencyCode: string;
    ratePlanId: RatePlanId;
  },
): { sell: number; list: number } {
  if (mewsPricing && products?.length) {
    const acc = accumulateMewsUpsellAddonAmountsInRoomCurrency({
      enhanceStayUpsells: upsells,
      upsellCounts: sr.addonUpsellCounts,
      products,
      displayCurrency: mewsPricing.displayCurrency,
      roomCurrencyCode: mewsPricing.roomCurrencyCode,
      occupancy: { adults: sr.adults, children: sr.children },
      chargedNights,
      ratePlanId: mewsPricing.ratePlanId,
    });
    if (acc) return { sell: acc.sellSum, list: acc.listSum };
  }

  const productById = new Map<string, Product>();
  for (const p of products ?? []) {
    productById.set(p.Id, p);
  }
  const nights = Math.max(1, chargedNights);

  let sell = 0;
  let list = 0;
  for (const u of upsells) {
    const count = sr.addonUpsellCounts[u.id] ?? 0;
    if (count <= 0) continue;

    if (u.mewsVariableCount) {
      sell += u.price * count;
      list += u.listPrice * count;
      continue;
    }

    const mainProd = u.mewsProductId ? productById.get(u.mewsProductId) : undefined;
    const mode = chargingModeLower(mainProd?.ChargingMode);
    const perHead = mode === "perperson" || mode === "perpersonpertimeunit";

    if (perHead && u.mewsProductId) {
      const timeFactor = mode === "perpersonpertimeunit" ? nights : 1;
      let childSell = u.price;
      let childList = u.listPrice;
      if (u.mewsChildProductId) {
        const cp = productById.get(u.mewsChildProductId);
        const chf = cp ? extractProductPrice(cp, "CHF") : null;
        if (chf != null && chf > 0) {
          childSell = chf;
          childList = Math.round(chf / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST);
        }
      }
      sell += (u.price * sr.adults + childSell * sr.children) * timeFactor;
      list += (u.listPrice * sr.adults + childList * sr.children) * timeFactor;
      continue;
    }

    sell += u.price;
    list += u.listPrice;
  }
  return { sell, list };
}

/** Per-upsell formatted line sell for one stay row (browse pricing; matches single-room summary math). */
function addonSellFormattedByUpsellIdForStayRoom(
  sr: StayRoom,
  upsellCounts: Record<string, number>,
  brMewsPrice: MewsMultiCurrencyPrice | null | undefined,
  enhanceStayUpsells: Upsell[],
  products: Product[] | undefined,
  displayCurrency: string,
  chargedNights: number,
  locale: string,
): Record<string, string> {
  const ratePlanId = sr.selectedRatePlanId;
  const roomPick = pickDisplayAmount(brMewsPrice, displayCurrency, MEWS_PICK_DISPLAY_OPTS);
  const code =
    roomPick?.currencyCode?.trim().toUpperCase() ?? displayCurrency.trim().toUpperCase();
  const out: Record<string, string> = {};
  for (const u of enhanceStayUpsells) {
    const count = upsellCounts[u.id] ?? 0;
    if (count <= 0) continue;

    let sellVal: number | null = null;
    if (ratePlanId && products?.length) {
      const acc = accumulateMewsUpsellAddonAmountsInRoomCurrency({
        enhanceStayUpsells: [u],
        upsellCounts: { [u.id]: count },
        products,
        displayCurrency,
        roomCurrencyCode: code,
        occupancy: { adults: sr.adults, children: sr.children },
        chargedNights,
        ratePlanId,
      });
      if (acc != null) sellVal = acc.sellSum;
    }
    if (sellVal == null) {
      const { sell } = addonSellAndListForStayRoom(
        { ...sr, addonUpsellCounts: { [u.id]: count } },
        [u],
        products,
        chargedNights,
        undefined,
      );
      sellVal = sell;
    }
    out[u.id] = formatBookMoney(sellVal, code, locale);
  }
  return out;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isIsoDateString(value: string | undefined | null): value is string {
  return !!value && ISO_DATE_RE.test(value);
}

function isoDateToUtcMidnight(date: string): string | null {
  if (!isIsoDateString(date)) return null;
  const utc = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(utc.getTime())) return null;
  return utc.toISOString();
}

const GUEST_DETAILS_VALIDATED_FIELDS = new Set<keyof GuestDetailsValidationErrors>([
  "firstName",
  "lastName",
  "email",
  "telephone",
  "bookerFirstName",
  "bookerLastName",
  "bookerEmail",
  "bookerTelephone",
]);

function buildStayRoomsReservationPriceRequests(args: {
  stayRooms: StayRoom[];
  availabilityByStayRoomId: Record<string, Record<string, RoomAvailability>>;
  checkIn: string;
  checkOut: string;
  ageCategories: AgeCategory[];
}): GetReservationPriceReservationRequest[] | null {
  const startUtc = isoDateToUtcMidnight(args.checkIn);
  const endUtc = isoDateToUtcMidnight(args.checkOut);
  if (!startUtc || !endUtc) return null;
  const out: GetReservationPriceReservationRequest[] = [];

  for (const sr of args.stayRooms) {
    if (!sr.selectedRoomId || !sr.selectedMewsRateId) return null;
    const raw = args.availabilityByStayRoomId[sr.id]?.[sr.selectedRoomId];
    const mewsRoomCategoryId =
      raw?.mewsRoomCategoryId ?? getMewsRoomCategoryId(sr.selectedRoomId);
    if (!mewsRoomCategoryId || mewsRoomCategoryId === "unmapped") return null;
    const occupancyData = convertToOccupancies(sr.adults, sr.children, args.ageCategories);
    if (!occupancyData.length) return null;
    const row: GetReservationPriceReservationRequest = {
      Identifier: sr.id,
      StartUtc: startUtc,
      EndUtc: endUtc,
      RoomCategoryId: mewsRoomCategoryId,
      RateId: sr.selectedMewsRateId,
      OccupancyData: occupancyData,
    };
    if (sr.selectedProductIds.length) row.ProductIds = sr.selectedProductIds;
    out.push(row);
  }
  return out;
}

/** Same room/rate/product mapping as `buildStayRoomsReservationPriceRequests`, for `reservationGroups/create` (AdultCount/ChildCount, no Identifier). */
function buildStayRoomsCreateGroupReservations(args: {
  stayRooms: StayRoom[];
  availabilityByStayRoomId: Record<string, Record<string, RoomAvailability>>;
  checkIn: string;
  checkOut: string;
  notes?: string;
}): CreateReservationGroupReservationData[] | null {
  const startUtc = isoDateToUtcMidnight(args.checkIn);
  const endUtc = isoDateToUtcMidnight(args.checkOut);
  if (!startUtc || !endUtc) return null;
  const notesTrim = args.notes?.trim() || undefined;
  const out: CreateReservationGroupReservationData[] = [];

  for (let i = 0; i < args.stayRooms.length; i++) {
    const sr = args.stayRooms[i];
    if (!sr.selectedRoomId || !sr.selectedMewsRateId) return null;
    const raw = args.availabilityByStayRoomId[sr.id]?.[sr.selectedRoomId];
    const mewsRoomCategoryId =
      raw?.mewsRoomCategoryId ?? getMewsRoomCategoryId(sr.selectedRoomId);
    if (!mewsRoomCategoryId || mewsRoomCategoryId === "unmapped") return null;
    const row: CreateReservationGroupReservationData = {
      RoomCategoryId: mewsRoomCategoryId,
      StartUtc: startUtc,
      EndUtc: endUtc,
      RateId: sr.selectedMewsRateId,
      AdultCount: sr.adults,
      ChildCount: sr.children,
    };
    if (sr.selectedProductIds.length) row.ProductIds = [...sr.selectedProductIds];
    const noteParts: string[] = [];
    if (sr.selectedRatePlanId === "signature") noteParts.push("Signature rate");
    if (notesTrim && i === 0) noteParts.push(notesTrim);
    if (noteParts.length) row.Notes = noteParts.join("\n");
    out.push(row);
  }
  return out;
}

const MEWS_PRODUCT_ID_TO_UPSELL_ID: Map<string, string> = (() => {
  const m = new Map<string, string>();
  for (const u of UPSELLS) {
    if (u.mewsProductId) m.set(u.mewsProductId, u.id);
    if (u.mewsProductIdSignature) m.set(u.mewsProductIdSignature, u.id);
    const v = u.mewsVariableCount;
    if (v) {
      m.set(v.base, u.id);
      m.set(v.count2, u.id);
      m.set(v.count3, u.id);
      m.set(v.count4, u.id);
    }
    const vs = u.mewsVariableCountSignature;
    if (vs) {
      m.set(vs.base, u.id);
      m.set(vs.count2, u.id);
      m.set(vs.count3, u.id);
      m.set(vs.count4, u.id);
    }
  }
  return m;
})();

/** City tax / Kurtaxe / breakfast Mews IDs — hide from confirmation when unmapped (would show generic "Additional service"). */
const CONFIRMATION_HIDDEN_ADDON_PRODUCT_IDS = new Set(
  [
    "cb34f52c-ae38-4881-87c0-b02200bd5346",
    "b08ae00e-4435-4531-b1d0-b3ef013a3021",
    "1ce83bea-34a3-4a88-ae67-b18c00fde1e9",
    "ff719dbd-9f62-4c25-a1e1-b3ef01391439",
    "1bdb1417-dbf5-405b-898c-b03500b0f843",
  ].map((id) => id.toLowerCase()),
);

function filterConfirmationVisibleProductIds(productIds: string[] | undefined): string[] {
  if (!productIds?.length) return [];
  return productIds.filter(
    (pid) => !CONFIRMATION_HIDDEN_ADDON_PRODUCT_IDS.has(pid.trim().toLowerCase()),
  );
}

function resolveBookedProductDisplayName(productId: string, book: BookPageTranslations): string {
  const upsellId = MEWS_PRODUCT_ID_TO_UPSELL_ID.get(productId);
  if (upsellId) {
    const u = UPSELLS.find((x) => x.id === upsellId);
    if (u) {
      return getLocalizedUpsellCopy(upsellId, { name: u.name, description: u.description }, book).name;
    }
  }
  return book.confirmation.unknownProduct;
}

type ConfirmationRailSummary = {
  checkIn: string;
  checkOut: string;
  totalNights: number;
  totalGuests: number;
  totalFormatted: string | null;
  rows: Array<{
    roomName: string;
    adults: number;
    children: number;
    productNames: string[];
  }>;
  isMultiRoom: boolean;
};

function nightsBetweenUtc(start: string, end: string): number {
  const a = new Date(start);
  const b = new Date(end);
  const diff = Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, Number.isFinite(diff) ? diff : 0);
}

function confirmationRoomDisplayName(roomCategoryId: string, book: BookPageTranslations): string {
  const localId = getLocalRoomId(roomCategoryId);
  const room = ROOM_LADDER.find((l) => l.sourceRoom.id === localId);
  if (!room) return book.confirmation.roomFallbackName;
  const meta = getRoomMetadata(room.sourceRoom.id);
  return getLocalizedBookRoomDisplay(room, meta, book).displayName;
}

/** Read-only stay summary from get reservation group (survives full page reload after Mews redirect). */
function buildConfirmationRailSummary(
  group: GetReservationGroupResponse | CreateReservationGroupResponse,
  book: BookPageTranslations,
  locale: string | undefined,
): ConfirmationRailSummary | null {
  const reservations = group.Reservations ?? [];
  if (!reservations.length) return null;

  const first = reservations[0];
  const totalNights = reservations.reduce(
    (sum, r) => sum + nightsBetweenUtc(r.StartUtc, r.EndUtc),
    0,
  );
  const totalGuests = reservations.reduce(
    (sum, r) => sum + (r.AdultCount ?? 0) + (r.ChildCount ?? 0),
    0,
  );

  let totalValue = group.TotalAmount?.GrossValue;
  let currency = group.TotalAmount?.Currency?.trim();
  if (totalValue == null || !Number.isFinite(totalValue)) {
    let sum = 0;
    for (const r of reservations) {
      const g = r.Amount?.GrossValue;
      if (g != null && Number.isFinite(g)) sum += g;
      const c = r.Amount?.Currency?.trim();
      if (c) currency = c;
    }
    if (sum > 0) totalValue = sum;
  }
  if (!currency) currency = "CHF";

  return {
    checkIn: first.StartUtc.slice(0, 10),
    checkOut: first.EndUtc.slice(0, 10),
    totalNights,
    totalGuests,
    totalFormatted:
      totalValue != null && Number.isFinite(totalValue) && totalValue > 0
        ? formatBookMoney(totalValue, currency, locale)
        : null,
    isMultiRoom: reservations.length > 1,
    rows: reservations.map((r) => ({
      roomName: confirmationRoomDisplayName(r.RoomCategoryId, book),
      adults: r.AdultCount ?? 0,
      children: r.ChildCount ?? 0,
      productNames: filterConfirmationVisibleProductIds(r.ProductIds).map((pid) =>
        resolveBookedProductDisplayName(pid, book),
      ),
    })),
  };
}

function reservationPriceResponseLooksValid(row: GetReservationPriceReservationResponse): boolean {
  const g = row.TotalAmount?.GrossValue;
  return typeof g === "number" && Number.isFinite(g) && g >= 0;
}

function alignReservationPriceResponses(
  requests: GetReservationPriceReservationRequest[],
  data: GetReservationPriceReservationResponse[] | undefined,
):
  | { ok: true; ordered: GetReservationPriceReservationResponse[] }
  | { ok: false; reason: "length" | "identifier" | "amount" } {
  if (!data || data.length !== requests.length) return { ok: false, reason: "length" };

  let orderMatches = true;
  for (let i = 0; i < requests.length; i++) {
    const rid = data[i]?.Identifier;
    if (rid != null && rid !== requests[i].Identifier) {
      orderMatches = false;
      break;
    }
  }

  const ordered: GetReservationPriceReservationResponse[] = [];
  if (orderMatches) {
    for (const row of data) {
      if (!reservationPriceResponseLooksValid(row)) return { ok: false, reason: "amount" };
      ordered.push(row);
    }
    return { ok: true, ordered };
  }

  const byId = new Map<string, GetReservationPriceReservationResponse>();
  for (const row of data) {
    const id = row.Identifier?.trim() ?? "";
    if (id) byId.set(id, row);
  }
  for (const req of requests) {
    const row = byId.get(req.Identifier);
    if (!row) return { ok: false, reason: "identifier" };
    if (!reservationPriceResponseLooksValid(row)) return { ok: false, reason: "amount" };
    ordered.push(row);
  }
  return { ok: true, ordered };
}

/** Hotel-local policy clock: Europe/Zurich (CET/CEST). */
const SIGNATURE_POLICY_TIME_ZONE = "Europe/Zurich";

/** Notice period (full calendar days before arrival) for BRS Signature; recurring yearly by arrival date. */
function getSignatureCancellationNoticeDays(checkInISO: string): number {
  const parts = checkInISO.split("-").map(Number);
  const month = parts[1];
  const day = parts[2];
  if (!month || !day) return 21;
  if (month === 1 && day >= 3) return 3;
  if (month === 2 || month === 3) return 3;
  if (month === 4) return 7;
  if (month === 10 || month === 11) return 3;
  if (month === 12 && day <= 14) return 3;
  return 21;
}

/** YYYY-MM-DD for the given instant in `timeZone` (civil calendar in that zone). */
function getCalendarDateISOInTimeZone(date: Date, timeZone: string): string {
  const s = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
  return s;
}

/** Last calendar date (YYYY-MM-DD) on which free cancellation applies: arrival date minus notice days. */
function getSignatureFreeCancellationDeadlineISO(checkInISO: string): string {
  const notice = getSignatureCancellationNoticeDays(checkInISO);
  const [y, m, d] = checkInISO.split("-").map(Number);
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() - notice);
  const yy = base.getUTCFullYear();
  const mm = String(base.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(base.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** True if today (CET/CEST, Zurich) is still on or before the last free-cancellation date. */
function isSignatureFreeCancellationStillOpen(checkInISO: string): boolean {
  const deadline = getSignatureFreeCancellationDeadlineISO(checkInISO);
  const today = getCalendarDateISOInTimeZone(new Date(), SIGNATURE_POLICY_TIME_ZONE);
  return today <= deadline;
}

/** Long formatted date for a YYYY-MM-DD civil date, labeled in Zurich time. */
function formatDeadlineDateForDisplayCET(deadlineISO: string, locale: string): string {
  const [y, m, d] = deadlineISO.split("-").map(Number);
  const utcNoon = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat(locale, {
    timeZone: SIGNATURE_POLICY_TIME_ZONE,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(utcNoon);
}

function formatSignatureDeadlineShortForSummary(deadlineISO: string, locale: string): string {
  const [y, m, d] = deadlineISO.split("-").map(Number);
  const utcNoon = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat(locale, {
    timeZone: SIGNATURE_POLICY_TIME_ZONE,
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(utcNoon);
}

/** Month + day in hotel policy TZ (e.g. Apr 2) for compact labels beside badges. */
function formatSignatureDeadlineMonthDay(deadlineISO: string, locale: string): string {
  const [y, m, d] = deadlineISO.split("-").map(Number);
  const utcNoon = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return new Intl.DateTimeFormat(locale, {
    timeZone: SIGNATURE_POLICY_TIME_ZONE,
    month: "short",
    day: "numeric",
  }).format(utcNoon);
}

/** Inline with rate name on mobile sticky summary (Signature only). */
function getSignatureRateSummaryTaglineMobile(
  checkInISO: string | null | undefined,
  sig: BookPageTranslations["signature"],
  locale: string
): string {
  if (!checkInISO) {
    return sig.taglineMobileNoDates;
  }
  if (!isSignatureFreeCancellationStillOpen(checkInISO)) {
    return sig.taglineMobilePastDeadline;
  }
  const deadline = getSignatureFreeCancellationDeadlineISO(checkInISO);
  return sig.taglineMobileUntil.replace("{{date}}", formatSignatureDeadlineShortForSummary(deadline, locale));
}

/** Sidebar / summary copy for Signature cancellation. */
function formatSignatureCancellationSummaryLine(
  checkInISO: string | null | undefined,
  sig: BookPageTranslations["signature"],
  locale: string
): string | null {
  if (!checkInISO) return null;
  if (!isSignatureFreeCancellationStillOpen(checkInISO)) {
    return sig.pastDeadlinePerkLine;
  }
  const deadline = getSignatureFreeCancellationDeadlineISO(checkInISO);
  return sig.summaryFreeUntil
    .replace("{{date}}", formatDeadlineDateForDisplayCET(deadline, locale))
    .replace("{{time}}", sig.cancelTimeLabel);
}

/** Banner copy next to the Signature pill on rate cards (full sentence). */
function getSignatureCancellationBannerLine(
  checkInISO: string | null | undefined,
  sig: BookPageTranslations["signature"],
  locale: string
): string {
  if (!checkInISO) {
    return sig.bannerSelectDates;
  }
  if (!isSignatureFreeCancellationStillOpen(checkInISO)) {
    return sig.pastDeadlinePerkLine;
  }
  const deadline = getSignatureFreeCancellationDeadlineISO(checkInISO);
  return sig.bannerFullyFlexibleUntil
    .replace("{{date}}", formatDeadlineDateForDisplayCET(deadline, locale))
    .replace("{{time}}", sig.cancelTimeStrip);
}

const RATE_PLAN_MULTIPLIERS: Record<RatePlanId, number> = {
  standard: 1,
  signature: 1.1,
};

function buildLocalizedRatePlans(book: BookPageTranslations): RatePlan[] {
  return [
    {
      id: "standard",
      name: book.ratePlans.standard.name,
      multiplier: RATE_PLAN_MULTIPLIERS.standard,
      included: [...book.ratePlans.standard.included],
    },
    {
      id: "signature",
      name: book.ratePlans.signature.name,
      multiplier: RATE_PLAN_MULTIPLIERS.signature,
      included: [...book.ratePlans.signature.included],
    },
  ];
}

const SectionTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div className="space-y-1">
    <div className="font-playfair text-lg sm:text-xl text-neutral-900">{title}</div>
    {subtitle ? <div className="text-sm text-neutral-600">{subtitle}</div> : null}
  </div>
);

/** Carousel: 640w mobile / 1080w book+ (940px). Lightbox: 1080w mobile / 1600w book+. */
const BookRoomImage = ({
  img,
  roomName,
  className,
  loading,
  mode,
}: {
  img: Room["images"][number];
  roomName: string;
  className: string;
  loading: "eager" | "lazy";
  mode: "carousel" | "lightbox";
}) => {
  const alt = img.alt || `${roomName} photo`;
  const ibe = img.ibeSrc;
  if (ibe) {
    // Single <img> with width descriptors: avoids <picture>/<source> quirks with encoded paths (%20, %26).
    if (mode === "carousel") {
      return (
        <img
          src={ibe.w1080}
          srcSet={`${ibe.w640} 640w, ${ibe.w1080} 1080w`}
          sizes="(max-width: 767px) 100vw, min(940px, 55vw)"
          alt={alt}
          className={className}
          loading={loading}
          decoding="async"
        />
      );
    }
    return (
      <img
        src={ibe.w1600}
        srcSet={`${ibe.w640} 640w, ${ibe.w1080} 1080w, ${ibe.w1600} 1600w`}
        sizes="(max-width: 767px) 100vw, min(100vw, 1400px)"
        alt={alt}
        className={className}
        loading={loading}
        decoding="async"
      />
    );
  }
  return (
    <img
      src={encodePublicPath(img.url)}
      alt={alt}
      className={className}
      loading={loading}
      decoding="async"
    />
  );
};

const RoomImageCarousel = ({ 
  images, 
  roomName, 
  aspectRatio = "16/9",
  mobileAspectRatio,
  className = "",
  dotsClassName,
  expandButtonClassName,
  topOverlay,
}: { 
  images: Room["images"]; 
  roomName: string;
  aspectRatio?: "16/9" | "4/3";
  mobileAspectRatio?: "4/3" | "3/2";
  className?: string;
  dotsClassName?: string;
  expandButtonClassName?: string;
  /** Rendered inside the slide stack (same `relative` layer as dots / expand). Use for room tags so mobile layout matches the image top edge. */
  topOverlay?: React.ReactNode;
}) => {
  const { translations } = useLanguage();
  const c = translations.book.carousel;
  const selected = images.slice(0, 6);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  if (selected.length === 0) return null;

  // Build aspect ratio classes: mobile first, then desktop
  const getAspectClass = () => {
    // If className includes h-full, we don't want aspect ratio forcing height on desktop
    if (className.includes("h-full")) {
      const mobileClass = mobileAspectRatio === "4/3" ? "aspect-[4/3]" : "aspect-[3/2]";
      return `${mobileClass} book:aspect-auto book:h-full`;
    }

    if (mobileAspectRatio) {
      const mobileClass = mobileAspectRatio === "4/3" ? "aspect-[4/3]" : "aspect-[3/2]";
      const desktopClass = aspectRatio === "4/3" ? "book:aspect-[4/3]" : "book:aspect-[16/9]";
      return `${mobileClass} ${desktopClass}`;
    }
    return aspectRatio === "16/9" ? "aspect-[16/9]" : "aspect-[4/3]";
  };

  const aspectClass = getAspectClass();

  return (
    <Dialog>
      <div className={cn("relative", className)}>
        <div
          className={cn(
            "relative w-full",
            className.includes("h-full") && "h-full min-h-0",
          )}
        >
          {topOverlay}
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: selected.length > 1 }}
            className="w-full h-full"
          >
            <CarouselContent
              className="ml-0 h-full"
              viewportClassName="rounded-none overflow-hidden h-full"
            >
              {selected.map((img, idx) => (
                <CarouselItem key={img.id} className="pl-0 h-full">
                  <div className={cn("w-full bg-gray-100", aspectClass)}>
                    <BookRoomImage
                      img={img}
                      roomName={roomName}
                      mode="carousel"
                      className="h-full w-full object-cover object-center"
                      loading={idx === 0 ? "eager" : "lazy"}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          {/* Dots */}
          <div className={cn("absolute bottom-3 left-0 right-0 flex justify-center gap-2", dotsClassName)}>
            {selected.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => api?.scrollTo(idx)}
                className={`h-2 w-2 rounded-full transition-all ${
                  current === idx ? "bg-white" : "bg-white/50 hover:bg-white/70"
                }`}
                aria-label={`${c.goToImage} ${idx + 1}`}
              />
            ))}
          </div>

          {/* Expand */}
          <DialogTrigger asChild>
            <button
              type="button"
              className={cn("absolute top-3 right-3 rounded-full bg-black/50 hover:bg-black/60 text-white p-2", expandButtonClassName)}
              aria-label={c.viewFullScreen}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </DialogTrigger>
        </div>
      </div>

      <DialogContent className="w-[100vw] max-w-none h-[100dvh] p-0 border-0 bg-black text-white">
        <DialogHeader className="sr-only">
          <DialogTitle>
            {roomName} {c.photosTitle}
          </DialogTitle>
          <DialogDescription>{c.photosDescription}</DialogDescription>
        </DialogHeader>

        <div className="h-full w-full flex items-center justify-center">
          <Carousel
            opts={{ align: "center", loop: selected.length > 1 }}
            className="w-full h-full"
          >
            <CarouselContent className="ml-0 h-full" viewportClassName="h-full">
              {selected.map((img) => (
                <CarouselItem key={`lightbox-${img.id}`} className="pl-0 h-full">
                  <div className="h-[100dvh] w-full flex items-center justify-center">
                    <BookRoomImage
                      img={img}
                      roomName={roomName}
                      mode="lightbox"
                      className="max-h-[100dvh] w-full object-contain"
                      loading="eager"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Icon Grid Component
const AmenityIconGrid = ({
  room,
  iconColor = "alpine-gold",
  occupancyDisplay,
}: {
  room: Room;
  iconColor?: "pine-green" | "alpine-gold";
  /** When set (e.g. Mews NormalBedCount or "8+2" with extra beds), shown under the occupancy icon instead of maxOccupancy */
  occupancyDisplay?: string;
}) => {
  const { translations } = useLanguage();
  const book = translations.book;
  const rc = book.roomCard;
  const iconClass =
    iconColor === "pine-green"
      ? "text-neutral-600"
      : "text-alpine-gold";

  const bedLabel = getBookPageBeddingLabel(room.id, room.bedding.configuration, book);

  const features = [
    { icon: OccupancyIcon, value: occupancyDisplay ?? String(room.capacity.maxOccupancy) },
    { icon: RoomSizeIcon, value: `${room.size.squareMeters} m²` },
    { icon: BedIcon, value: bedLabel },
    room.balcony
      ? { icon: DoorOpen, value: rc.privateBalcony }
      : { icon: Sparkles, value: rc.alpineComfort },
    { icon: Sofa, value: rc.elegantSeating },
    { icon: Coffee, value: rc.ensuiteCoffee },
  ];

  const toTitleCase = (s: string) =>
    s.replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="grid grid-cols-3 book:flex book:flex-row book:justify-between gap-3 book:gap-4 py-4">
      {features.map((feature, idx) => {
        const Icon = feature.icon;
        return (
          <div key={idx} className="flex flex-col items-center text-center group book:flex-1">
            <Icon
              className={`${iconClass} w-7 h-7 md:w-8 md:h-8 book:w-6 book:h-6 mb-1.5 transition-transform duration-300 group-hover:scale-110`}
              strokeWidth={1}
            />
            <div className="text-[11px] md:text-[11.5px] book:text-[11px] text-neutral-900 font-medium leading-tight break-words min-w-0">
              {idx === 0 || idx === 2 ? feature.value : toTitleCase(feature.value.toLowerCase())}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** “From” row for room cards when nightly price comes from MEWS: struck compare-at, then MEWS rate + label. */
const RoomCardDirectBookerFromPrice = ({
  mewsPrice,
  chfPricePerNight,
  chfTotalAmount,
  displayCurrency,
  chargedNights,
  variant,
}: {
  mewsPrice?: MewsMultiCurrencyPrice | null;
  chfPricePerNight: number;
  chfTotalAmount: number;
  displayCurrency: string;
  chargedNights: number;
  variant: "premium" | "compact";
}) => {
  const { translations, language } = useLanguage();
  const b = translations.book;
  const locale = bookingLocale(language);
  const bp = browseMewsPricing(
    mewsPrice,
    displayCurrency,
    chfPricePerNight,
    chfTotalAmount,
    chargedNights,
    locale,
  );
  if (variant === "premium") {
    return (
      <div className="flex w-full min-w-0 flex-col items-end gap-1">
        <div className="flex w-full min-w-0 flex-col items-end gap-0">
          <div className="inline-flex max-w-full flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0.5">
            <span className="text-[10px] uppercase tracking-[0.1em] text-neutral-400 font-bold leading-none">{b.roomCard.from}</span>
            <span className="text-sm font-medium text-rose-950/50 line-through decoration-rose-400/45 leading-tight">
              {bp.nightlyCompareAtFormatted}
            </span>
          </div>
          <div className="flex w-full min-w-0 flex-wrap items-baseline justify-end gap-x-1.5">
            <span className="min-w-0 text-xl font-semibold leading-tight text-pine-green">{bp.nightlyFormatted}</span>
            <span className="shrink-0 whitespace-nowrap text-[10px] lowercase italic leading-none text-neutral-400">
              {b.roomCard.perNight}
            </span>
          </div>
        </div>
        <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-alpine-gold">{b.directBookerDiscountLabel}</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className="flex w-full min-w-0 flex-col items-end gap-0">
        <div className="inline-flex max-w-full flex-wrap items-baseline justify-end gap-x-1">
          <span className="text-[11px] text-neutral-500 leading-none">{b.roomCard.from}</span>
          <span className="text-xs font-medium text-rose-950/50 line-through decoration-rose-400/45 leading-none">
            {bp.nightlyCompareAtFormatted}
          </span>
        </div>
        <div className="flex w-full items-baseline justify-end gap-x-2">
          <span className="text-lg font-semibold leading-none text-pine-green sm:text-xl">{bp.nightlyFormatted}</span>
          <span className="shrink-0 whitespace-nowrap text-right text-[11px] leading-none text-neutral-500">{b.roomCard.perNightSlash}</span>
        </div>
      </div>
      <span className="text-[9px] font-semibold uppercase tracking-[0.1em] text-alpine-gold">{b.directBookerDiscountLabel}</span>
    </div>
  );
};

type BookingRatePlanCardRateOption = {
  rateId: string;
  pricePerNight: number;
  totalAmount: number;
  mewsPrice?: MewsMultiCurrencyPrice;
};

function getRateOptionsFromRoomAvailability(roomAvailability: RoomAvailability | undefined): {
  standard?: BookingRatePlanCardRateOption;
  signature?: BookingRatePlanCardRateOption;
} {
  if (!roomAvailability) return {};
  const rateOptionsByPlanId: {
    standard?: BookingRatePlanCardRateOption;
    signature?: BookingRatePlanCardRateOption;
  } = {};
  Object.entries(roomAvailability.ratesByRateId).forEach(([rateId, rate]) => {
    const planId = mapMewsRateToLocalRatePlan(rateId, rate.rateName);
    if (planId === "standard") {
      if (
        !rateOptionsByPlanId.standard ||
        rate.pricePerNight < rateOptionsByPlanId.standard.pricePerNight
      ) {
        rateOptionsByPlanId.standard = {
          rateId,
          pricePerNight: rate.pricePerNight,
          totalAmount: rate.totalAmount,
          ...(rate.mewsPrice ? { mewsPrice: rate.mewsPrice } : {}),
        };
      }
    } else if (planId === "signature") {
      if (
        !rateOptionsByPlanId.signature ||
        rate.pricePerNight < rateOptionsByPlanId.signature.pricePerNight
      ) {
        rateOptionsByPlanId.signature = {
          rateId,
          pricePerNight: rate.pricePerNight,
          totalAmount: rate.totalAmount,
          ...(rate.mewsPrice ? { mewsPrice: rate.mewsPrice } : {}),
        };
      }
    }
  });
  return rateOptionsByPlanId;
}

function normalizeRateInclusionLine(line: string): string {
  return line
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-");
}

/** Resort amenity discount bullets (Standard vs Signature) — use MdOutlineDiscount in the rate card. */
function isRateCardDiscountPerkLine(line: string): boolean {
  const t = normalizeRateInclusionLine(line);
  return (
    (t.includes("booked online") && t.includes("resort")) ||
    t.includes("all resort amenities during") ||
    (t.includes("online-buchung") && t.includes("resort")) ||
    (t.includes("wahrend") && t.includes("aufenthalt") && t.includes("annehmlichkeit")) ||
    (t.includes("en ligne") && t.includes("prestations du resort")) ||
    t.includes("toutes les prestations du resort")
  );
}

function iconForRateInclusion(line: string): LucideIcon {
  const t = normalizeRateInclusionLine(line);
  if (
    t.includes("late checkout") ||
    t.includes("spaterer check-out") ||
    t.includes("depart tardif") ||
    t.includes("check-out")
  ) {
    return Clock;
  }
  if (
    t.includes("breakfast") ||
    t.includes("fruhstuck") ||
    t.includes("frühstück") ||
    t.includes("petit-dejeuner") ||
    t.includes("petit dejeuner")
  ) {
    return UtensilsCrossed;
  }
  if (t.includes("reschedule") || t.includes("umbuch") || t.includes("report")) {
    return CalendarIcon;
  }
  if (t.includes("wi-fi") || t.includes("wifi") || t.includes("wlan")) {
    return Wifi;
  }
  return Sparkles;
}

/** Policy pills on rate cards: alpine gold with shadow + ring so both labels read clearly on light / gold-tinted surfaces. */
const rateCardPillGold =
  "border-transparent shrink-0 bg-alpine-gold px-3 py-1 text-[10px] font-bold uppercase tracking-[0.04em] text-white shadow-md ring-1 ring-white/45 sm:text-xs sm:tracking-[0.05em]";

function BookingRatePlanCard({
  plan,
  planSelected,
  selectedRatePlanId,
  hasMewsPricing,
  rateOption,
  nightly,
  chargedNights,
  displayCurrency,
  checkIn,
  onCardActivate,
  onSelectRate,
  dense,
}: {
  plan: RatePlan;
  planSelected: boolean;
  /** Room’s chosen plan (global on /book); used to style the non-selected card’s CTA. */
  selectedRatePlanId: RatePlanId | null;
  hasMewsPricing: boolean;
  rateOption: BookingRatePlanCardRateOption | undefined;
  nightly: number;
  chargedNights: number;
  displayCurrency: string;
  checkIn: string;
  onCardActivate: () => void;
  onSelectRate: (e: React.MouseEvent) => void;
  dense?: boolean;
}) {
  const { translations, language } = useLanguage();
  const b = translations.book;
  const sig = b.signature;
  const rc = b.rateCard;
  const locale = bookingLocale(language);
  const titleClass = dense
    ? "font-playfair text-base font-semibold text-neutral-900 break-words"
    : "font-playfair text-base font-semibold sm:text-lg book:text-base text-neutral-900 break-words";
  const amenityTextClass = dense ? "text-[13px] text-neutral-700 leading-snug" : "text-sm text-neutral-700 leading-snug";
  const iconClass = dense ? "h-5 w-5 text-neutral-500 shrink-0 mt-0.5" : "h-5 w-5 text-neutral-500 shrink-0 mt-0.5";

  const compareAtNightly = hasMewsPricing ? compareAtPriceForMewsAmount(nightly) : 0;
  const staySavings =
    hasMewsPricing && rateOption
      ? compareAtPriceForMewsAmount(rateOption.totalAmount) - rateOption.totalAmount
      : 0;

  const browse =
    hasMewsPricing && rateOption
      ? browseMewsPricing(
          rateOption.mewsPrice,
          displayCurrency,
          rateOption.pricePerNight,
          rateOption.totalAmount,
          chargedNights,
          locale,
        )
      : null;

  const nightStayLabel =
    chargedNights === 1 ? rc.nightStayOne : rc.nightStayMany.replace("{{n}}", String(chargedNights));

  /** One horizontal row (badge | divider | text); -mt matches both plans so strips stay aligned; pulls strip toward amenity shell. */
  const policyRowClass =
    "w-full flex flex-nowrap items-center gap-2 sm:gap-2.5 min-h-[3.125rem] book:min-h-[3.375rem] -mt-1";

  /** Fixed-height amenity region (both plans): shared h tight to signature list + overflow-y safety; less slack above policy. */
  const amenityShellClass = dense
    ? "h-[10.25rem]"
    : "h-[11.25rem] book:h-[10.25rem]";

  const isSignaturePlan = plan.id === "signature";

  return (
    <div
      role="button"
      tabIndex={hasMewsPricing ? 0 : undefined}
      className={cn(
        "flex h-full min-h-0 min-w-0 flex-col gap-1 self-stretch rounded-2xl border",
        !dense && "sm:gap-1.5 book:gap-0.5",
        dense ? "p-3" : "p-4 sm:p-4",
        isSignaturePlan
          ? planSelected
            ? "border-alpine-gold bg-gradient-to-b from-alpine-gold/15 to-alpine-gold/6 shadow-md ring-1 ring-alpine-gold/25"
            : "border-alpine-gold/45 bg-gradient-to-b from-white via-amber-50/40 to-stone-100/90 shadow-[0_3px_14px_-5px_rgba(55,48,35,0.09)]"
          : planSelected
            ? "border-alpine-gold bg-alpine-gold/10 shadow-sm"
            : "border-alpine-gold/45 bg-stone-50 shadow-sm",
        hasMewsPricing ? "cursor-pointer" : "",
        !hasMewsPricing ? "opacity-60" : "",
      )}
      onClick={hasMewsPricing ? onCardActivate : undefined}
      onKeyDown={
        hasMewsPricing
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardActivate();
              }
            }
          : undefined
      }
    >
      <h3
        className={cn(
          titleClass,
          "mb-0 shrink-0 leading-tight",
          /** Two lines of the title font so policy rows stay aligned; lh avoids extra space vs fixed rem */
          "min-h-[2lh]",
        )}
      >
        {plan.name}
      </h3>

      {!hasMewsPricing ? (
        <p className="text-xs text-neutral-500 shrink-0">{rc.pricingNotAvailable}</p>
      ) : null}

      <div
        className={cn(
          "flex min-h-0 w-full shrink-0 flex-col overflow-y-auto",
          /** Desktop: contain vertical overscroll inside the shell; mobile (dense) uses default so the page can scroll. */
          !dense && "overscroll-y-contain",
          amenityShellClass,
          !dense && "book:-mt-1",
        )}
      >
        <ul className={cn("shrink-0 space-y-2", !dense && "book:space-y-1")}>
          {plan.id === "signature" ? (
            <>
              <li className="flex gap-3 items-start">
                <Check className={iconClass} strokeWidth={1.25} aria-hidden />
                <span className={amenityTextClass}>{b.ratePlans.signature.breakfastWifiLine}</span>
              </li>
              {plan.included.map((line, index) => {
                const Icon = index === 1 ? GlassWater : iconForRateInclusion(line);
                const discount = isRateCardDiscountPerkLine(line);
                return (
                  <li key={line} className="flex gap-3 items-start">
                    {discount ? (
                      <MdOutlineDiscount className={iconClass} aria-hidden />
                    ) : (
                      <Icon className={iconClass} strokeWidth={1.25} aria-hidden />
                    )}
                    <span className={amenityTextClass}>{line}</span>
                  </li>
                );
              })}
              <li className="flex gap-3 items-start">
                <Gift className={iconClass} strokeWidth={1.25} aria-hidden />
                <div className="min-w-0 flex-1 flex flex-col gap-1">
                  <span className={cn(amenityTextClass, "font-medium text-neutral-900")}>
                    {b.ratePlans.signature.thermalPackageWorthTemplate}
                  </span>
                  <span
                    className={cn(
                      dense ? "text-[13px]" : "text-sm",
                      "text-neutral-600 leading-snug",
                    )}
                  >
                    {b.ratePlans.signature.thermalPackageFacilitiesLine}
                  </span>
                </div>
              </li>
            </>
          ) : (
            plan.included.map((line) => {
              const Icon = iconForRateInclusion(line);
              const discount = isRateCardDiscountPerkLine(line);
              return (
                <li key={line} className="flex gap-3 items-start">
                  {discount ? (
                    <MdOutlineDiscount className={iconClass} aria-hidden />
                  ) : (
                    <Icon className={iconClass} strokeWidth={1.25} aria-hidden />
                  )}
                  <span className={amenityTextClass}>{line}</span>
                </li>
              );
            })
          )}
        </ul>
        <div className="min-h-0 min-w-0 flex-1" aria-hidden />
      </div>

      {plan.id === "signature" ? (
        <div className={cn(policyRowClass, "shrink-0")}>
          <Badge className={rateCardPillGold}>
            {checkIn && !isSignatureFreeCancellationStillOpen(checkIn) ? sig.badgeRecommended : sig.badgeFreeCancellation}
          </Badge>
          <div className="w-px h-5 shrink-0 bg-pine-green/25 rounded-full" aria-hidden />
          <p className="text-xs text-neutral-700 leading-snug min-w-0 flex-1">
            {getSignatureCancellationBannerLine(checkIn, sig, locale)}
          </p>
        </div>
      ) : plan.id === "standard" ? (
        <div className={cn(policyRowClass, "shrink-0")}>
          <Badge className={rateCardPillGold}>{sig.badgeLowestPrice}</Badge>
          <div className="w-px h-5 shrink-0 bg-pine-green/25 rounded-full" aria-hidden />
          <p className="text-xs text-neutral-700 leading-snug min-w-0 flex-1">{sig.standardPolicyStrip}</p>
        </div>
      ) : null}

      {hasMewsPricing && rateOption ? (
        <div className="w-full shrink-0 space-y-1.5 pb-2 text-center">
          <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0">
            <span className="text-[11px] sm:text-xs text-neutral-500 whitespace-nowrap">
              {rc.totalForStay} {nightStayLabel}
            </span>
            <span
              className={cn(
                "font-semibold text-pine-green tracking-tight tabular-nums",
                dense ? "text-xl sm:text-2xl" : "text-2xl sm:text-2xl",
              )}
            >
              {browse?.totalFormatted ?? formatCHF(rateOption.totalAmount)}
            </span>
          </div>
          {staySavings > 0 ? (
            <div className="flex justify-center pt-0.5">
              <div className="inline-flex min-w-0 max-w-full items-start gap-1.5 rounded-full border border-pine-green/25 bg-pine-green/[0.06] px-2.5 py-1.5 book:items-center">
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pine-green text-white book:mt-0"
                  aria-hidden
                >
                  <Check className="h-3 w-3 stroke-[3]" />
                </span>
                <span className="flex min-w-0 flex-col gap-0.5 text-left book:hidden">
                  <span className="text-[9px] font-bold uppercase leading-tight tracking-[0.06em] text-neutral-900 sm:text-[10px] sm:tracking-[0.06em]">
                    {b.directBookerDiscountLabel}
                  </span>
                  <span className="text-[9px] font-medium uppercase leading-tight tracking-[0.06em] text-pine-green sm:text-[10px] sm:tracking-[0.06em]">
                    {rc.youSaved.replace("{{amount}}", browse?.savingsFormatted ?? formatCHF(staySavings))}
                  </span>
                </span>
                <span className="hidden min-w-0 flex-1 flex-wrap items-center gap-x-1.5 text-left text-[10px] uppercase leading-tight tracking-[0.06em] text-neutral-900 book:inline-flex">
                  <span className="font-bold">{b.directBookerDiscountLabel}</span>
                  <span className="font-medium text-pine-green">
                    {rc.youSaved.replace("{{amount}}", browse?.savingsFormatted ?? formatCHF(staySavings))}
                  </span>
                </span>
              </div>
            </div>
          ) : null}
          <p className="text-center text-xs sm:text-sm text-neutral-600 leading-snug">
            <span className="flex flex-col items-center gap-0.5 book:hidden">
              <span className="inline-flex flex-wrap items-baseline justify-center gap-x-1.5 gap-y-0">
                <span>{rc.perNightLabel}</span>
                <span className="text-neutral-500">
                  <span className="sr-only">
                    {rc.previously}{" "}
                  </span>
                  <span className="text-rose-950/50 line-through decoration-rose-400/45">
                    {browse?.nightlyCompareAtFormatted ?? formatCHF(compareAtNightly)}
                  </span>
                </span>
              </span>
              <span
                className={cn(
                  "font-semibold text-pine-green",
                  dense && "text-sm tabular-nums sm:text-base",
                )}
              >
                {browse?.nightlyFormatted ?? formatCHF(nightly)}
              </span>
            </span>
            <span className="hidden book:inline">
              {rc.perNightLabel}{" "}
              <span className="font-semibold text-pine-green">
                {browse?.nightlyFormatted ?? formatCHF(nightly)}
              </span>{" "}
              <span className="text-neutral-500">
                ({rc.previouslyParen}{" "}
                <span className="text-rose-950/50 line-through decoration-rose-400/45">
                  {browse?.nightlyCompareAtFormatted ?? formatCHF(compareAtNightly)}
                </span>
                )
              </span>
            </span>
          </p>
        </div>
      ) : (
        <div
          className={cn(
            "rounded-xl border border-dashed border-gray-200 bg-pine-green/[0.03] px-3 py-4 text-center shrink-0",
            dense && "py-3.5",
          )}
        >
          <p className="text-sm font-semibold text-neutral-500">—</p>
          <p className="text-xs text-neutral-500 mt-1">{rc.selectDatesForPricing}</p>
        </div>
      )}

      <div className="flex shrink-0 justify-center">
        <Button
          variant="outline"
          className={cn(
            "h-10 min-h-10 w-full max-w-[13.5rem] rounded-full font-bold transition-all duration-300",
            "border-2 border-alpine-gold bg-white text-alpine-gold shadow-none",
            "hover:border-alpine-gold hover:bg-gray-200 hover:text-pine-green",
            "active:border-alpine-gold active:bg-gray-200 active:text-pine-green",
            planSelected && "text-pine-green hover:text-pine-green active:text-pine-green",
          )}
          disabled={!hasMewsPricing}
          onClick={(e) => {
            e.stopPropagation();
            onSelectRate(e);
          }}
        >
          {planSelected ? (
            <>
              <Check className="h-4 w-4 shrink-0 stroke-[2.5]" aria-hidden />
              {rc.selected}
            </>
          ) : (
            rc.selectRate
          )}
        </Button>
      </div>

      {/* Absorbs equal-height grid slack below the CTA so amenities/policy stay tight */}
      <div className="min-h-0 min-w-0 flex-1" aria-hidden />
    </div>
  );
}

const RoomRatesToggleButton = ({
  ratesTrayOpen,
  onClick,
  disabled,
  showLabel,
  hideLabel,
}: {
  ratesTrayOpen: boolean;
  onClick: () => void;
  disabled?: boolean;
  showLabel: string;
  hideLabel: string;
}) => (
  <Button
    type="button"
    variant="ghost"
    className={cn(
      "w-full h-auto min-h-0 py-1.5 px-1 rounded-none border-0 shadow-none ring-offset-0",
      "bg-transparent text-pine-green hover:bg-transparent hover:text-pine-green active:bg-transparent",
      "text-[11px] font-semibold uppercase tracking-[0.12em] gap-2",
      "focus-visible:ring-1 focus-visible:ring-pine-green/25",
    )}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="leading-tight">{ratesTrayOpen ? hideLabel : showLabel}</span>
    {ratesTrayOpen ? (
      <ChevronUp className="!h-3.5 !w-3.5 shrink-0 text-neutral-500" strokeWidth={2.25} aria-hidden />
    ) : (
      <ChevronDown className="!h-3.5 !w-3.5 shrink-0 text-neutral-500" strokeWidth={2.25} aria-hidden />
    )}
  </Button>
);

// Premium Room Card Component (split layout)
const PremiumRoomCard = ({
  room,
  selected,
  ratesTrayOpen,
  onViewRates,
  roomAvailability,
  standardPricePerNight,
  criteriaConfirmed,
  displayCurrency,
  chargedNights,
  cheapestMappedRate,
  noBottomRadius = false,
  occupancyDisplay,
}: {
  room: BookingRoom;
  selected: boolean;
  ratesTrayOpen?: boolean;
  onViewRates: () => void;
  roomAvailability?: RoomAvailability;
  standardPricePerNight: number | null;
  criteriaConfirmed: boolean;
  displayCurrency: string;
  chargedNights: number;
  cheapestMappedRate: RoomAvailabilityRate | null;
  noBottomRadius?: boolean;
  /** Mews NormalBedCount, or "N+E" when ExtraBedCount > 0; else maxOccupancy fallback */
  occupancyDisplay: string;
}) => {
  const { translations } = useLanguage();
  const b = translations.book;
  const fallbackPriceFrom =
    room.sourceRoom.pricing?.seasonalPricing?.shoulder ?? room.sourceRoom.pricing?.basePrice ?? 0;

  const hasLiveAvailability = criteriaConfirmed && !!roomAvailability;
  const hasStandardRate = standardPricePerNight != null;
  const isUnavailableForDates = hasLiveAvailability && (!roomAvailability?.available || !hasStandardRate);

  const displayPrice = hasLiveAvailability && hasStandardRate ? standardPricePerNight! : fallbackPriceFrom;

  const meta = getRoomMetadata(room.sourceRoom.id);
  const { displayName: localizedRoomTitle, description } = getLocalizedBookRoomDisplay(room, meta, b);
  const localizedBedding = getBookPageBeddingLabel(
    room.sourceRoom.id,
    room.sourceRoom.bedding.configuration,
    b,
  );
  const tag = meta?.tag;
  const viewBadges = meta?.viewBadges ?? [];
  const primaryViewBadge = viewBadges[0]; // For amenities fallback

  return (
    <Card className={cn(
      "overflow-hidden border-0 backdrop-blur-[10px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] bg-[rgba(253,253,253,0.65)]",
      "rounded-[32px]",
      noBottomRadius && "rounded-b-none"
    )}>
      <div className="relative flex flex-col book:flex-row h-auto book:h-[540px]">
        {/* Carousel: on desktop 68% width with small overlap under text panel; right edge fades */}
        <div
          className={cn(
            "w-full book:w-[68%] relative h-auto overflow-visible book:h-full book:overflow-hidden transition-[border-radius] duration-500",
            noBottomRadius ? "rounded-bl-none book:rounded-br[8px]" : "rounded-bl-[32px] book:rounded-br-none",
            "book:rounded-tl-[32px] book:rounded-tr-none",
            "book:[mask-image:linear-gradient(to_right,black_95.6%,transparent_100%)] book:[webkit-mask-image:linear-gradient(to_right,black_95.6%,transparent_100%)] book:[mask-size:100%_100%] book:[webkit-mask-size:100%_100%]"
          )}
        >
        <RoomImageCarousel 
          images={room.sourceRoom.images} 
          roomName={localizedRoomTitle} 
          aspectRatio="4/3"
          mobileAspectRatio="3/2"
          className="h-full"
          dotsClassName="book:right-[8%]"
          expandButtonClassName="book:right-[10%]"
        />
          
          {/* Overlay Badges (View Type + Balcony) */}
          <div className="absolute top-4 left-4 z-20 flex flex-wrap gap-2">
            {viewBadges.map((badge) => (
              <Badge key={badge} className="bg-white/90 text-neutral-900 backdrop-blur-sm shadow-sm border-0 font-medium tracking-wide text-xs px-2.5 py-1">
                {b.viewBadges[badge as ViewBadgeType] ?? badge}
              </Badge>
            ))}
            {room.sourceRoom.balcony && (
              <Badge className="bg-white/90 text-neutral-900 backdrop-blur-sm shadow-sm border-0 font-medium tracking-wide text-xs px-2.5 py-1">
                {b.roomCard.balconyBadge}
              </Badge>
            )}
          </div>
        </div>

        {/* Info panel: fixed title top, fixed button bottom, flexible middle. Min height on mobile for consistent layout. */}
        <div className="w-full min-h-[440px] book:min-h-0 book:absolute book:right-0 book:top-0 book:bottom-0 book:w-[35%] book:border-r-2 book:border-l-0 rounded-tr-[32px] rounded-br-[32px] border-white overflow-hidden p-6 lg:p-8 flex flex-col bg-[rgba(253,253,253,0.65)] book:backdrop-blur-[07px]">
        {/* Gradient "borders" top and bottom: soft over blur, solid white toward the right */}
          <div
            className="absolute left-0 right-0 top-0 h-[2px] z-[2] pointer-events-none rounded-tr-[32px]"
            style={{
              background: "linear-gradient(to right, transparent 0%, rgba(255,255,255,0.4) 15%, white 28% 100%)",
            }}
            aria-hidden
          />
          <div
            className="absolute left-0 right-0 bottom-0 h-[2px] z-[2] pointer-events-none rounded-br-[32px]"
            style={{
              background: "linear-gradient(to right, transparent 0%, rgba(255,255,255,0.4) 15%, white 28% 100%)",
            }}
            aria-hidden
          />

          {/* Fixed at top: Tag + Room name */}
          <div className="relative z-10 flex-shrink-0">
            <div className="flex flex-col gap-1">
              {tag && (
                <div className="text-[11px] uppercase tracking-[0.3em] text-alpine-gold font-bold mb-0.5">
                  {b.roomTags[tag] ?? tag}
                </div>
              )}
              <h3 className="font-playfair text-2xl text-neutral-900 leading-tight">
                <RoomNameDisplay displayName={localizedRoomTitle} />
              </h3>
            </div>
          </div>

          {/* Flexible middle: price, description, amenities – scrolls if overflow */}
          <div className="relative z-10 flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto mt-4">
            <div className="flex w-full min-w-0 flex-shrink-0 flex-col items-end space-y-3">
              {isUnavailableForDates ? (
                <div className="w-full text-right text-[11px] font-semibold text-neutral-600">{b.roomCard.notAvailableDates}</div>
              ) : hasLiveAvailability && hasStandardRate ? (
                <RoomCardDirectBookerFromPrice
                  mewsPrice={cheapestMappedRate?.mewsPrice}
                  chfPricePerNight={displayPrice}
                  chfTotalAmount={cheapestMappedRate?.totalAmount ?? displayPrice * chargedNights}
                  displayCurrency={displayCurrency}
                  chargedNights={chargedNights}
                  variant="premium"
                />
              ) : (
                <div className="flex w-full min-w-0 flex-col items-end gap-0.5">
                  <span className="text-[10px] uppercase tracking-[0.1em] font-bold leading-none text-neutral-400">
                    {b.roomCard.from}
                  </span>
                  <div className="flex w-full min-w-0 flex-wrap items-baseline justify-end gap-x-1.5">
                    <span className="min-w-0 text-xl font-semibold leading-tight text-pine-green">{formatCHF(displayPrice)}</span>
                    <span className="shrink-0 whitespace-nowrap text-[10px] lowercase italic leading-none text-neutral-400">
                      {b.roomCard.perNight}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 flex min-h-0 items-start">
              <div className="w-full">
                <div className="grid grid-cols-3 gap-y-4 gap-x-2 py-4 border-t border-b border-pine-green/5 w-full">
                  <div className="flex flex-col items-center text-center group">
                    <OccupancyIcon className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 book:w-6 book:h-6" />
                    <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-tight">
                      {occupancyDisplay}
                    </div>
                  </div>

                  <div className="flex flex-col items-center text-center group">
                    <RoomSizeIcon className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 book:w-6 book:h-6" />
                    <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-tight">
                      {room.sourceRoom.size.squareMeters} m²
                    </div>
                  </div>

                  <div className="flex flex-col items-center text-center group min-w-0">
                    <BedIcon className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 shrink-0 book:w-6 book:h-6" />
                    <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-snug text-center min-w-0 max-w-full break-words hyphens-auto px-0.5">
                      {localizedBedding}
                    </div>
                  </div>

                  <div className="flex flex-col items-center text-center group">
                    {room.sourceRoom.balcony ? (
                      <>
                        <DoorOpen className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 book:w-6 book:h-6" strokeWidth={1} />
                        <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-tight text-center">{b.roomCard.privateBalcony}</div>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 book:w-6 book:h-6" strokeWidth={1} />
                        <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-tight text-center">{b.roomCard.alpineComfort}</div>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col items-center text-center group">
                    <Sofa className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 book:w-6 book:h-6" strokeWidth={1} />
                    <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-tight text-center">{b.roomCard.elegantSeating}</div>
                  </div>

                  <div className="flex flex-col items-center text-center group">
                    <Coffee className="w-8 h-8 text-alpine-gold mb-2 transition-transform duration-300 group-hover:scale-110 book:w-6 book:h-6" strokeWidth={1} />
                    <div className="text-[11px] md:text-[11.5px] book:text-[10px] text-neutral-900 font-medium leading-tight text-center">{b.roomCard.ensuiteCoffee}</div>
                  </div>
                </div>

                <p className="mt-4 px-4 sm:px-6 text-base text-neutral-800 leading-relaxed font-medium text-center lg:text-left">
                  {description}
                </p>
              </div>
            </div>
          </div>

          {/* Fixed at bottom: rates toggle — ghost + chevron; divider above */}
          <div className="relative z-10 mt-auto flex w-full shrink-0 flex-col pt-2">
            <div className="w-full border-t border-pine-green/15" aria-hidden />
            <div className="mt-2">
              <RoomRatesToggleButton
                ratesTrayOpen={!!ratesTrayOpen}
                onClick={onViewRates}
                disabled={isUnavailableForDates}
                showLabel={b.roomCard.showRatesAndOffers}
                hideLabel={b.roomCard.hideRatesAndOffers}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const RoomCard = ({
  room,
  selected,
  ratesTrayOpen,
  onViewRates,
  testMobileAspectRatio = false,
  roomAvailability,
  standardPricePerNight,
  criteriaConfirmed,
  displayCurrency,
  chargedNights,
  cheapestMappedRate,
  noBottomRadius = false,
  occupancyDisplay,
}: {
  room: BookingRoom;
  selected: boolean;
  ratesTrayOpen?: boolean;
  onViewRates: () => void;
  testMobileAspectRatio?: boolean;
  roomAvailability?: RoomAvailability;
  standardPricePerNight: number | null;
  criteriaConfirmed: boolean;
  displayCurrency: string;
  chargedNights: number;
  cheapestMappedRate: RoomAvailabilityRate | null;
  noBottomRadius?: boolean;
  /** Mews NormalBedCount, or "N+E" when ExtraBedCount > 0; else maxOccupancy fallback */
  occupancyDisplay: string;
}) => {
  const { translations } = useLanguage();
  const b = translations.book;
  const fallbackPriceFrom =
    room.sourceRoom.pricing?.seasonalPricing?.shoulder ?? room.sourceRoom.pricing?.basePrice ?? 0;

  const hasLiveAvailability = criteriaConfirmed && !!roomAvailability;
  const hasStandardRate = standardPricePerNight != null;
  const isUnavailableForDates = hasLiveAvailability && (!roomAvailability?.available || !hasStandardRate);

  const displayPrice = hasLiveAvailability && hasStandardRate ? standardPricePerNight! : fallbackPriceFrom;
  const meta = getRoomMetadata(room.sourceRoom.id);
  const { displayName: localizedRoomTitle, description } = getLocalizedBookRoomDisplay(room, meta, b);
  const roomTag = meta?.tag;
  const localizedRoomTag =
    roomTag != null ? (b.roomTags[roomTag] ?? roomTag) : null;

  return (
    <Card className={cn(
      "relative flex min-h-[17.5rem] flex-col overflow-hidden rounded-2xl border border-white/50 backdrop-blur-[10px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] bg-[rgba(253,253,253,0.8)]",
      noBottomRadius && "rounded-b-none"
    )}>
      <CardContent className="flex min-h-0 flex-1 flex-col space-y-0 px-0 pb-2 !pt-0">
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="relative w-full shrink-0">
            <RoomImageCarousel
              images={room.sourceRoom.images}
              roomName={localizedRoomTitle}
              aspectRatio="16/9"
              mobileAspectRatio={testMobileAspectRatio ? "3/2" : undefined}
              topOverlay={
                localizedRoomTag ? (
                  <div
                    className="pointer-events-none absolute left-4 top-0 z-30 max-w-[min(78%,calc(100%-5rem))] max-sm:-mt-1"
                    data-oid="room-card-room-tag"
                  >
                    <div className="inline-flex max-w-full items-center rounded-t-none rounded-b-xl border-b border-l border-r border-white/30 bg-black/55 px-3 py-1.5 shadow-sm backdrop-blur-md sm:rounded-b-2xl sm:px-3.5 sm:py-1.5">
                      <span className="whitespace-nowrap font-sans text-[10px] font-bold uppercase leading-none tracking-[0.28em] text-alpine-gold antialiased sm:text-[13px] sm:tracking-[0.3em]">
                        {localizedRoomTag}
                      </span>
                    </div>
                  </div>
                ) : null
              }
            />
          </div>

          <CardHeader className="shrink-0 border-0 px-3 pb-2 pt-3 book:pb-3 sm:px-6 sm:pt-4">
            <div className="flex items-start justify-between gap-3 sm:gap-4">
              <CardTitle className="font-playfair text-lg sm:text-xl book:text-xl text-neutral-900 flex-1 min-w-[11rem] sm:min-w-[13rem]">
                <RoomNameDisplay displayName={localizedRoomTitle} />
              </CardTitle>
              <div className="text-right shrink-0 min-w-0 max-w-[52%] sm:max-w-none">
                {isUnavailableForDates ? (
                  <span className="text-xs font-semibold text-neutral-600">{b.roomCard.notAvailableDates}</span>
                ) : hasLiveAvailability && hasStandardRate ? (
                  <RoomCardDirectBookerFromPrice
                    mewsPrice={cheapestMappedRate?.mewsPrice}
                    chfPricePerNight={displayPrice}
                    chfTotalAmount={cheapestMappedRate?.totalAmount ?? displayPrice * chargedNights}
                    displayCurrency={displayCurrency}
                    chargedNights={chargedNights}
                    variant="compact"
                  />
                ) : (
                  <div className="flex w-full min-w-0 flex-col items-end gap-0.5">
                    <span className="text-[11px] leading-none text-neutral-500">{b.roomCard.from}</span>
                    <div className="flex w-full items-baseline justify-end gap-x-2">
                      <span className="text-lg font-semibold leading-none text-pine-green sm:text-xl">{formatCHF(displayPrice)}</span>
                      <span className="shrink-0 whitespace-nowrap text-right text-[11px] leading-none text-neutral-500">
                        {b.roomCard.perNightSlash}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          
          <div className={`${testMobileAspectRatio ? "pt-3 book:pt-0" : ""} px-3 sm:px-6`}>
            <AmenityIconGrid
              room={room.sourceRoom}
              iconColor="alpine-gold"
              occupancyDisplay={occupancyDisplay}
            />
          </div>

          {/* Mobile & tablet description below icons */}
          <p className="mt-3 px-4 sm:px-6 text-base text-neutral-800 leading-relaxed font-medium text-center lg:hidden">
            {description}
          </p>
        </div>

        <div className="mt-auto w-full shrink-0 px-3 pt-3 sm:px-6">
          <div className="w-full border-t border-pine-green/15" aria-hidden />
          <div className="mt-2">
            <RoomRatesToggleButton
              ratesTrayOpen={!!ratesTrayOpen}
              onClick={onViewRates}
              disabled={isUnavailableForDates}
              showLabel={b.roomCard.showRatesAndOffers}
              hideLabel={b.roomCard.hideRatesAndOffers}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Book = () => {
  const mainRef = useRef<HTMLDivElement | null>(null);
  const ratesRef = useRef<HTMLDivElement | null>(null);
  const enhanceRef = useRef<HTMLDivElement | null>(null);
  const paymentRef = useRef<HTMLDivElement | null>(null);
  /** Primary confirm CTA in details step; sticky bar triggers this to avoid duplicating submit logic. */
  const confirmBookingButtonRef = useRef<HTMLButtonElement | null>(null);
  /** Scroll areas for date bar + criteria guest pickers — scroll to end after “Add room”. */
  const datePickerGuestsScrollRef = useRef<HTMLDivElement | null>(null);
  const criteriaOverlayGuestsScrollRef = useRef<HTMLDivElement | null>(null);

  const {
    configuration,
    hotels,
    paymentConfiguration,
    isLoading: isMewsConfigLoading,
    error: mewsConfigError,
  } = useMewsConfiguration();

  const { t, language, setLanguage, translations } = useLanguage();
  const b = translations.book;
  const brgHref = useMemo(
    () => getLocalizedUrl("/best-rate-guarantee", language),
    [language],
  );
  const locale = bookingLocale(language);
  const ratePlans = useMemo(() => buildLocalizedRatePlans(b), [b]);
  const guestValidationMessages = useMemo(
    (): GuestDetailsValidationMessages => ({
      firstNameRequired: b.validation.firstNameRequired,
      lastNameRequired: b.validation.lastNameRequired,
      emailRequired: b.validation.emailRequired,
      emailInvalid: b.validation.emailInvalid,
      telephoneRequired: b.validation.telephoneRequired,
      telephoneTooShort: b.validation.telephoneTooShort,
      telephoneTooLong: b.validation.telephoneTooLong,
      telephoneInvalidLength: b.validation.telephoneInvalidLength,
      telephoneLengthInvalid: b.validation.telephoneLengthInvalid,
      telephoneInvalid: b.validation.telephoneInvalid,
      bookerFirstNameRequired: b.validation.bookerFirstNameRequired,
      bookerLastNameRequired: b.validation.bookerLastNameRequired,
      bookerEmailRequired: b.validation.bookerEmailRequired,
      bookerEmailInvalid: b.validation.bookerEmailInvalid,
      bookerTelephoneRequired: b.validation.bookerTelephoneRequired,
    }),
    [b],
  );

  /** Upsells with price/listPrice from MEWS when available; listPrice = selling price / 0.9. */
  const resolvedUpsells = useMemo(() => {
    const map = hotels?.Products ? buildProductPriceMap(hotels.Products, "CHF") : new Map<string, number>();
    return UPSELLS.map((u) => ({ ...u, ...getResolvedUpsellPrices(u, map) }));
  }, [hotels?.Products]);

  /** Shown on "Enhance your stay" only — hidden ids/categories stay in UPSELLS for pricing and other refs. */
  const enhanceStayUpsells = useMemo(
    () =>
      resolvedUpsells.filter(
        (u) =>
          u.category !== "Sauna & Thermal" &&
          u.category !== "Spa & Massage" &&
          u.id !== "dinner-5-course",
      ),
    [resolvedUpsells],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Dates (mock)
  const todayISO = new Date().toISOString().slice(0, 10);
  const tomorrowISO = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  }, []);

  const getISO = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
  const initialCheckIn = getISO(searchParams.get("checkIn"));
  const initialCheckOut = getISO(searchParams.get("checkOut"));

  // Overlay calendar: no default; first click = one end, second = other, third = fresh first, fourth = other, etc.
  const [overlayFirstClick, setOverlayFirstClick] = useState<Date | null>(null);
  const [overlaySecondClick, setOverlaySecondClick] = useState<Date | null>(null);

  const handleOverlayDatesSelect = useCallback((range: DateRange | undefined) => {
    if (!range) {
      setOverlayFirstClick(null);
      setOverlaySecondClick(null);
      return;
    }
    if (range.from && !range.to) {
      setOverlayFirstClick(range.from);
      setOverlaySecondClick(null);
      return;
    }
    if (range.from && range.to) {
      // Already have both ends (full range): this click starts a new range (3rd, 5th, … click)
      if (overlaySecondClick !== null) {
        const newDate = range.from.getTime() === overlayFirstClick.getTime() ? range.to : range.from;
        setOverlayFirstClick(newDate);
        setOverlaySecondClick(null);
        return;
      }
      // One end set: this is the second click (2nd, 4th, …) — set the other end
      if (overlayFirstClick) {
        const other = range.from.getTime() === overlayFirstClick.getTime() ? range.to : range.from;
        setOverlaySecondClick(other);
      }
    }
  }, [overlayFirstClick, overlaySecondClick]);

  const initialAdultsParam = Number(searchParams.get("adults"));
  const initialChildrenParam = Number(searchParams.get("children"));

  const [checkIn, setCheckIn] = useState<string>(initialCheckIn ?? todayISO);
  const [checkOut, setCheckOut] = useState<string>(initialCheckOut ?? tomorrowISO);

  // Ensure at least 1 night stay (auto-fix 0-night or invalid ranges)
  useEffect(() => {
    if (!checkIn || !checkOut) return;
    const inDate = new Date(checkIn);
    const outDate = new Date(checkOut);
    if (Number.isNaN(inDate.getTime()) || Number.isNaN(outDate.getTime())) return;
    if (outDate <= inDate) {
      const next = new Date(inDate);
      next.setDate(next.getDate() + 1);
      const nextISO = next.toISOString().slice(0, 10);
      if (nextISO !== checkOut) setCheckOut(nextISO);
    }
  }, [checkIn, checkOut, setCheckOut]);

  // Date bar: first click = one end, second = other, third = fresh first, fourth = other (same as overlay)
  const handleDatesSelect = useCallback((range: DateRange | undefined) => {
    if (!range) {
      setCheckIn("");
      setCheckOut("");
      return;
    }
    if (range.from && !range.to) {
      setCheckIn(format(range.from, "yyyy-MM-dd"));
      setCheckOut("");
      return;
    }
    if (range.from && range.to) {
      if (checkOut) {
        // Already have full range: this click starts a new range (3rd, 5th, …)
        const checkInTime = checkIn ? new Date(checkIn).getTime() : null;
        const newDate = (checkInTime !== null && range.from.getTime() === checkInTime) ? range.to : range.from;
        setCheckIn(format(newDate, "yyyy-MM-dd"));
        setCheckOut("");
        return;
      }
      const a = range.from.getTime();
      const b = range.to.getTime();
      const [earlier, later] = a <= b ? [range.from, range.to] : [range.to, range.from];
      setCheckIn(format(earlier, "yyyy-MM-dd"));
      setCheckOut(format(later, "yyyy-MM-dd"));
    }
  }, [checkIn, checkOut]);

  const stayRoomIdSeqRef = useRef(1);
  const [stayRooms, setStayRooms] = useState<StayRoom[]>(() => {
    const a0 = Number.isFinite(initialAdultsParam) && initialAdultsParam > 0 ? initialAdultsParam : 2;
    const c0 = Number.isFinite(initialChildrenParam) && initialChildrenParam >= 0 ? initialChildrenParam : 0;
    return [createStayRoom("sr-0", a0, c0, "Stay 1")];
  });
  const [planRoomIndex, setPlanRoomIndex] = useState(0);
  const [enhanceRoomIndex, setEnhanceRoomIndex] = useState(0);
  const planningStayRoom = stayRooms[planRoomIndex] ?? stayRooms[0];
  const enhancingStayRoom = stayRooms[enhanceRoomIndex] ?? stayRooms[0];
  const bookingLeadStayRoom = stayRooms[0] ?? planningStayRoom;
  const stayRoomsRef = useRef(stayRooms);
  stayRoomsRef.current = stayRooms;

  useEffect(() => {
    setStayRooms((prev) => normalizeStayRoomLabels(prev, b.bar.roomLabelTemplate));
  }, [b.bar.roomLabelTemplate]);

  const totalGuestsAcrossRooms = useMemo(
    () => stayRooms.reduce((s, r) => s + r.adults + r.children, 0),
    [stayRooms],
  );
  const occupancy = totalGuestsAcrossRooms;
  const activePlanOccupancy =
    (planningStayRoom?.adults ?? 0) + (planningStayRoom?.children ?? 0);

  const patchPlanningStayRoom = useCallback(
    (patch: Partial<StayRoom>) => {
      setStayRooms((prev) =>
        prev.map((sr, i) => {
          if (i !== planRoomIndex) return sr;
          const merged: StayRoom = { ...sr, ...patch };
          return {
            ...merged,
            selectedProductIds: recomputeStayRoomSelectedProductIds(
              merged.addonUpsellCounts,
              enhanceStayUpsells,
              merged.selectedRatePlanId,
            ),
          };
        }),
      );
    },
    [planRoomIndex, enhanceStayUpsells],
  );

  const patchStayRoomAt = useCallback(
    (rowIndex: number, patch: Partial<StayRoom>) => {
      setStayRooms((prev) =>
        normalizeStayRoomLabels(
          prev.map((sr, i) => {
            if (i !== rowIndex) return sr;
            const merged = { ...sr, ...patch };
            let next: StayRoom = merged;
            if (patch.adults !== undefined || patch.children !== undefined) {
              const o = clampStayRowOccupancy(merged.adults, merged.children);
              next = { ...merged, adults: o.adults, children: o.children };
            }
            return {
              ...next,
              selectedProductIds: recomputeStayRoomSelectedProductIds(
                next.addonUpsellCounts,
                enhanceStayUpsells,
                next.selectedRatePlanId,
              ),
            };
          }),
          b.bar.roomLabelTemplate,
        ),
      );
    },
    [b.bar.roomLabelTemplate, enhanceStayUpsells],
  );

  const scrollGuestPickersToEnd = useCallback(() => {
    const run = () => {
      for (const el of [datePickerGuestsScrollRef.current, criteriaOverlayGuestsScrollRef.current]) {
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, []);

  const addStayRoom = useCallback(() => {
    setStayRooms((prev) => {
      if (prev.length >= MAX_STAY_ROOMS) return prev;
      const id = `sr-${stayRoomIdSeqRef.current++}`;
      return normalizeStayRoomLabels(
        [...prev, createStayRoom(id, 2, 0, "")],
        b.bar.roomLabelTemplate,
      );
    });
    scrollGuestPickersToEnd();
  }, [b.bar.roomLabelTemplate, scrollGuestPickersToEnd]);

  const patchEnhancingStayRoomAddon = useCallback(
    (upsellId: string, count: number) => {
      setStayRooms((prev) =>
        normalizeStayRoomLabels(
          prev.map((sr, i) =>
            i === enhanceRoomIndex
              ? withUpdatedStayRoomAddons(sr, upsellId, count, enhanceStayUpsells)
              : sr,
          ),
          b.bar.roomLabelTemplate,
        ),
      );
    },
    [enhanceRoomIndex, enhanceStayUpsells, b.bar.roomLabelTemplate],
  );

  const aggregateAddonUpsellCounts = useMemo(
    () => mergeAddonUpsellCountsAcrossStayRooms(stayRooms),
    [stayRooms],
  );

  const planningRoomComplete = Boolean(
    planningStayRoom?.selectedRoomId &&
      planningStayRoom?.selectedRatePlanId &&
      planningStayRoom?.selectedMewsRateId,
  );

  const continuePlanStepOrEnhance = useCallback(() => {
    if (!planningRoomComplete) return;
    // Per-room flow: plan room k → enhance room k → plan room k+1 → …
    setEnhanceRoomIndex(planRoomIndex);
    setCurrentStep("addons");
    setRatesSectionExpanded(false);
    mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [planningRoomComplete, planRoomIndex]);

  const isMobile = useIsMobile();

  /** Under 768px width: centered sheet (Dialog). Tablet and desktop use the Popover dropdown (same threshold as `useIsMobile`). */
  const NARROW_BOOK_BAR_MAX_PX = 767;
  const [narrowBookBar, setNarrowBookBar] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= NARROW_BOOK_BAR_MAX_PX : true,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${NARROW_BOOK_BAR_MAX_PX}px)`);
    const onChange = () => setNarrowBookBar(mql.matches);
    mql.addEventListener("change", onChange);
    setNarrowBookBar(mql.matches);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // Two-step entry: if criteria not present in URL, user confirms via bar dropdowns
  const [criteriaConfirmed, setCriteriaConfirmed] = useState<boolean>(
    Boolean(initialCheckIn && initialCheckOut),
  );
  const criteriaConfirmedRef = useRef(criteriaConfirmed);
  useEffect(() => {
    criteriaConfirmedRef.current = criteriaConfirmed;
  }, [criteriaConfirmed]);

  /** Inline + toast when occupancy split fails (max rooms / insufficient adults). Cleared when guests or dates change. */
  const [criteriaOccupancySplitError, setCriteriaOccupancySplitError] = useState<
    "max_rooms" | "insufficient_adults" | null
  >(null);

  const [datesPopoverOpen, setDatesPopoverOpen] = useState<boolean>(false);
  /** Synced when the bar date popover/dialog opens so the calendar shows the month of the current selection. */
  const [barPickerDisplayedMonth, setBarPickerDisplayedMonth] = useState<Date>(() => new Date());
  /** Booking-bar dropdown: show split hint at most twice (decrement each time popover opens with any row T > 2). */
  const [barSplitGuestHintRemaining, setBarSplitGuestHintRemaining] = useState(2);
  const prevDatesPopoverOpenRef = useRef(false);
  const [bookingBarMenuOpen, setBookingBarMenuOpen] = useState<boolean>(false);

  // Rooms
  const [showAllRooms, setShowAllRooms] = useState<boolean>(false);

  // Rates section: expand/collapse when "VIEW RATES & OFFERS" is clicked (per-room toggle)
  const [ratesSectionExpanded, setRatesSectionExpanded] = useState(false);

  // Add-ons: filter tag (All or category). Sauna and massage packages are not offered on /book enhance step.
  const [addonFilter, setAddonFilter] = useState<"All" | "Dining" | "Romance">("All");

  const [currentStep, setCurrentStep] = useState<BookingStep>("rooms");
  const criteriaOverlayVisible = currentStep === "rooms" && !criteriaConfirmed;
  const prevCriteriaOverlayVisibleRef = useRef(false);
  /** Criteria overlay calendar: jump to selection month when overlay appears / reappears. */
  const [criteriaOverlayPickerMonth, setCriteriaOverlayPickerMonth] = useState<Date>(() => new Date());
  /** Multi-room plan / enhance step: occupancy editor next to subtitle */
  const [multiStepOccupancyOpen, setMultiStepOccupancyOpen] = useState(false);
  useEffect(() => {
    setMultiStepOccupancyOpen(false);
  }, [currentStep, planRoomIndex, enhanceRoomIndex]);

  const contextStayRoom =
    currentStep === "rooms"
      ? planningStayRoom
      : currentStep === "addons"
        ? enhancingStayRoom
        : bookingLeadStayRoom;
  const selectedRoomId = contextStayRoom?.selectedRoomId ?? null;
  const selectedRatePlanId = contextStayRoom?.selectedRatePlanId ?? null;

  /** Mobile/tablet bottom bar: collapsed minimal strip vs full booking summary. */
  const [mobileSummaryExpanded, setMobileSummaryExpanded] = useState(false);
  /** Multi-room stay cards: expanded add-on line items (desktop rail + mobile sheet + details breakdown). */
  const [multiRoomAddonDetailsExpandedByStayId, setMultiRoomAddonDetailsExpandedByStayId] = useState<
    Record<string, boolean>
  >({});

  // Guest details (Step 4.1) – collected on "details" step, validated against Configuration RequiredFields
  const [guestDetails, setGuestDetails] = useState<GuestDetailsForm>(defaultGuestDetailsForm);
  const [guestDetailsErrors, setGuestDetailsErrors] = useState<GuestDetailsValidationErrors>({});

  // RequiredFields from primary Configuration (e.g. ["Telephone"])
  const mewsRequiredFields: RequiredField[] = useMemo(() => {
    const configs = configuration?.Configurations;
    const primary = configs?.[0];
    const fields = primary?.RequiredFields;
    return Array.isArray(fields) ? (fields as RequiredField[]) : [];
  }, [configuration]);

  /** Mews bed data from distributor configuration (base-only capacity; junior-suite also uses extra beds). */
  const mewsCategoryBeds = useMemo(() => {
    const m = buildMewsCategoryBedMaps(configuration, hotels);
    return {
      totalByCategoryId: m.totalByCategoryId,
      normalByCategoryId: m.normalByCategoryId,
      // HMR can briefly retain a memoized object from an older module shape without this map
      extraByCategoryId: m.extraByCategoryId ?? new Map<string, number>(),
    };
  }, [configuration, hotels]);
  const occupancyDisplayForLocalRoom = useCallback(
    (localRoomId: string, fallbackMaxOccupancy: number): string =>
      getBookPageOccupancyDisplay(
        localRoomId,
        getMewsRoomCategoryId(localRoomId),
        mewsCategoryBeds,
        fallbackMaxOccupancy,
      ),
    [mewsCategoryBeds],
  );

  // Step 4.2: Reservation creation state
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [createReservationError, setCreateReservationError] = useState<string | null>(null);
  const [createReservationResult, setCreateReservationResult] =
    useState<CreateReservationGroupResponse | null>(null);

  const bookingSessionCommitted = createReservationResult != null;
  const hasBookingProgress = !bookingSessionCommitted && criteriaConfirmed;

  /** Target URL when user confirms leaving via intercepted `<a href>`, or `BOOK_LEAVE_POPSTATE` after Back. */
  const leaveGuardPendingHrefRef = useRef<string | null>(null);
  /** When true, skip native `beforeunload` so only our leave modal runs (user already confirmed). */
  const bypassBeforeUnloadRef = useRef(false);
  /** When true, skip popstate leave trap (intentional redirect to Mews payment / 3DS). */
  const leaveForExternalPaymentRef = useRef(false);
  /** Until this time (performance.now()), trailing `popstate` re-pushes trap only — no modal (post-close race). */
  const leaveGuardPostClosePopstateUntilRef = useRef(0);
  /** After "Yes" on the back-button guard: ignore trap/modal until URL leaves `/book` (see watchdog timeout). */
  const leaveViaConfirmedBackRef = useRef(false);
  const leaveDismissHandledRef = useRef(false);
  const [leaveGuardOpen, setLeaveGuardOpen] = useState(false);
  const [bookContactConciergeOpen, setBookContactConciergeOpen] = useState(false);
  const leaveGuardOpenRef = useRef(false);
  useEffect(() => {
    leaveGuardOpenRef.current = leaveGuardOpen;
  }, [leaveGuardOpen]);

  useLayoutEffect(() => {
    bookLeaveTrace("leaveGuardOpen → layout", {
      open: leaveGuardOpen,
      refOpen: leaveGuardOpenRef.current,
    });
  }, [leaveGuardOpen]);

  useLayoutEffect(() => {
    bookLeaveTrace("hasBookingProgress → layout", { hasBookingProgress });
  }, [hasBookingProgress]);

  const handleLeaveGuardStay = useCallback(() => {
    leaveDismissHandledRef.current = true;
    leaveGuardPendingHrefRef.current = null;
    leaveGuardOpenRef.current = false;
    leaveGuardPostClosePopstateUntilRef.current =
      performance.now() + LEAVE_GUARD_POST_CLOSE_POPSTATE_MS;
    setLeaveGuardOpen(false);
  }, []);

  /** Bypass leave guard before navigating to Mews payment or card-authorization (not a abandon-booking exit). */
  const beginExternalPaymentNavigation = useCallback(() => {
    bypassBeforeUnloadRef.current = true;
    leaveForExternalPaymentRef.current = true;
  }, []);

  const handleLeaveGuardLeave = useCallback(() => {
    const url = leaveGuardPendingHrefRef.current;
    leaveGuardPendingHrefRef.current = null;
    leaveDismissHandledRef.current = true;
    leaveGuardOpenRef.current = false;
    setLeaveGuardOpen(false);
    bypassBeforeUnloadRef.current = true;
    if (url === BOOK_LEAVE_POPSTATE) {
      leaveViaConfirmedBackRef.current = true;
      navigate(-2);
      window.setTimeout(() => {
        if (!leaveViaConfirmedBackRef.current) return;
        if (pathIsBookRoute(window.location.pathname)) {
          bookLeaveTrace("confirmed-back: still on book, retry navigate(-1)");
          navigate(-1);
        }
      }, LEAVE_GUARD_CONFIRM_BACK_RETRY_MS);
      window.setTimeout(() => {
        if (leaveViaConfirmedBackRef.current) {
          leaveViaConfirmedBackRef.current = false;
          bookLeaveTrace("confirmed-back: watchdog cleared flag");
        }
      }, LEAVE_GUARD_CONFIRM_BACK_WATCHDOG_MS);
    } else if (url) {
      window.location.assign(url);
    }
  }, [navigate]);

  const onLeaveGuardOpenChange = useCallback((open: boolean) => {
    bookLeaveTrace("Dialog onOpenChange", {
      open,
      dismissHandled: leaveDismissHandledRef.current,
    });
    if (!open) {
      if (leaveDismissHandledRef.current) {
        leaveDismissHandledRef.current = false;
        return;
      }
      // Radix is controlled: sync state when the user dismisses (overlay, Escape, etc.). Omitting this
      // can desync Presence / RemoveScroll and read as a flash.
      leaveGuardPendingHrefRef.current = null;
      leaveGuardOpenRef.current = false;
      leaveGuardPostClosePopstateUntilRef.current =
        performance.now() + LEAVE_GUARD_POST_CLOSE_POPSTATE_MS;
      setLeaveGuardOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!hasBookingProgress) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (bypassBeforeUnloadRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasBookingProgress]);

  /** Back/forward: duplicate history entry + `popstate` (BrowserRouter has no `useBlocker`). Ignore synthetic `popstate` from `LanguageContext`. */
  useEffect(() => {
    if (!hasBookingProgress) return;
    bookLeaveTrace("trap effect: push initial trap");
    window.history.pushState(historyStateWithBookLeaveTrap(), "", window.location.href);

    const onPopState = (e: PopStateEvent) => {
      if (!e.isTrusted) return;
      if (leaveForExternalPaymentRef.current) {
        bookLeaveTrace("popstate: external payment redirect — ignore trap");
        return;
      }
      const now = performance.now();
      if (leaveViaConfirmedBackRef.current) {
        bookLeaveTrace("popstate: confirmed-back in progress — no trap/modal");
        if (!pathIsBookRoute(window.location.pathname)) {
          leaveViaConfirmedBackRef.current = false;
        }
        return;
      }
      if (leaveGuardOpenRef.current) {
        bookLeaveTrace("popstate: guard already open → re-push trap");
        window.history.pushState(historyStateWithBookLeaveTrap(), "", window.location.href);
        return;
      }
      if (now < leaveGuardPostClosePopstateUntilRef.current) {
        bookLeaveTrace("popstate: post-close cooldown → re-push trap only");
        window.history.pushState(historyStateWithBookLeaveTrap(), "", window.location.href);
        return;
      }
      bookLeaveTrace("popstate: open leave dialog");
      leaveGuardPendingHrefRef.current = BOOK_LEAVE_POPSTATE;
      leaveGuardOpenRef.current = true;
      setLeaveGuardOpen(true);
      window.history.pushState(historyStateWithBookLeaveTrap(), "", window.location.href);
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      bookLeaveTrace("trap effect: cleanup (remove popstate listener)");
      window.removeEventListener("popstate", onPopState);
    };
  }, [hasBookingProgress]);

  useEffect(() => {
    if (!hasBookingProgress) return;
    const onCaptureClick = (e: MouseEvent) => {
      if (leaveGuardOpenRef.current) return;
      const el = (e.target as HTMLElement | null)?.closest?.("a[href]");
      if (!el) return;
      const a = el as HTMLAnchorElement;
      if (a.target === "_blank" || a.hasAttribute("download")) return;
      let url: URL;
      try {
        url = new URL(a.href, window.location.origin);
      } catch {
        return;
      }
      if (!["http:", "https:"].includes(url.protocol)) return;
      if (url.origin !== window.location.origin) return;
      if (!pathIsBookRoute(window.location.pathname)) return;
      const raw = a.getAttribute("href")?.trim() ?? "";
      if (raw.startsWith("#")) return;
      const here = window.location.href.split("#")[0];
      const there = url.href.split("#")[0];
      if (here === there) return;
      e.preventDefault();
      e.stopPropagation();
      leaveGuardPendingHrefRef.current = url.href;
      leaveGuardOpenRef.current = true;
      setLeaveGuardOpen(true);
    };
    document.addEventListener("click", onCaptureClick, true);
    return () => document.removeEventListener("click", onCaptureClick, true);
  }, [hasBookingProgress]);

  // Step 4.3: Final price verification (before payment)
  const [finalPriceData, setFinalPriceData] = useState<GetReservationPriceResponse | null>(null);

  // Step 4.4 Option B: Payment card form (Task 5) — non-secure fields only; card number/CVC in Secure Fields
  const [cardExpiration, setCardExpiration] = useState<string>("");
  const [cardHolderName, setCardHolderName] = useState<string>("");
  const [cardFormErrors, setCardFormErrors] = useState<{ expiration?: string; holderName?: string }>({});
  const [secureFieldsReady, setSecureFieldsReady] = useState(false);
  /** Hosted fields: PAN + CVV both reported valid by PCI Proxy (change/validate). */
  const [secureFieldsNumberAndCvvValid, setSecureFieldsNumberAndCvvValid] = useState(false);
  const secureFieldsRef = useRef<SecureFieldsCardInputRef | null>(null);

  // Task 6b: Return from payment redirect — load reservation by reservationGroupId from URL
  const [confirmationLoadError, setConfirmationLoadError] = useState<string | null>(null);
  const [isLoadingConfirmation, setIsLoadingConfirmation] = useState(false);
  const [paymentVerificationOutcome, setPaymentVerificationOutcome] = useState<PaymentOutcome | null>(
    null,
  );
  const [confirmationGroupExtent, setConfirmationGroupExtent] =
    useState<GetReservationGroupResponse | null>(null);
  const [isRedirectingToPayment, setIsRedirectingToPayment] = useState(false);

  const persistBookPurchaseBeforeRedirect = useCallback(
    async (reservationGroupId: string, group?: BookPurchaseGroup | null) => {
      await stashBookPurchaseEmailHash(
        reservationGroupId,
        purchaseEmailFromGuestDetails(guestDetails),
      );
      if (group) {
        const itemNameByRoomCategoryId: Record<string, string> = {};
        for (const reservation of group.Reservations ?? []) {
          const categoryId = reservation.RoomCategoryId?.trim();
          if (!categoryId) continue;
          itemNameByRoomCategoryId[categoryId] = confirmationRoomDisplayName(categoryId, b);
        }
        stashBookPurchaseChfSnapshot(reservationGroupId, group, itemNameByRoomCategoryId);
      }
      if (!readStashedPurchaseChfSnapshot(reservationGroupId)) {
        const charge = getAmountToChargeOnConfirmation(finalPriceData);
        if (charge) {
          stashBookPurchaseChfAmount(reservationGroupId, charge.grossValue, charge.currency);
        }
      }
    },
    [guestDetails, b, finalPriceData],
  );

  const redirectToExternalPayment = useCallback(
    async (
      url: string,
      options?: {
        beforeNavigate?: () => void;
        reservationGroupId?: string;
        group?: BookPurchaseGroup | null;
      },
    ) => {
      if (options?.reservationGroupId) {
        await persistBookPurchaseBeforeRedirect(options.reservationGroupId, options.group);
      }
      beginExternalPaymentNavigation();
      options?.beforeNavigate?.();
      setIsRedirectingToPayment(true);
      window.location.href = url;
    },
    [beginExternalPaymentNavigation, persistBookPurchaseBeforeRedirect],
  );

  /** Sojern/GTM `brs_book_*` dedupe keys (docs/Sojern/sojern-book-page-implementation-plan.md). */
  const sojernRoomsCriteriaKeyFiredRef = useRef<string | null>(null);
  const sojernAddonsCriteriaKeyFiredRef = useRef<string | null>(null);
  const sojernDetailsPriceKeyFiredRef = useRef<string | null>(null);
  const sojernConfirmationGroupIdFiredRef = useRef<string | null>(null);
  /** Ads `purchase` same-session guard; sessionStorage covers confirmation reloads. */
  const purchaseGroupIdFiredRef = useRef<string | null>(null);

  /** Converts MM/YY (or MMYY) to YYYY-MM for Mews API. Returns empty string if invalid. */
  const mmYyToYyyyMm = useCallback((mmYy: string): string => {
    const digits = mmYy.replace(/\D/g, "");
    if (digits.length !== 4) return "";
    const mm = digits.slice(0, 2);
    const yy = digits.slice(2, 4);
    const m = parseInt(mm, 10);
    if (m < 1 || m > 12) return "";
    return `20${yy}-${mm}`;
  }, []);

  /** Re-validate phone on blur so length/format errors show per country without submitting. */
  const updateGuestDetailsField = useCallback(
    <K extends keyof GuestDetailsForm>(field: K, value: GuestDetailsForm[K]) => {
      setGuestDetails((prev) => ({ ...prev, [field]: value }));
      if (GUEST_DETAILS_VALIDATED_FIELDS.has(field as keyof GuestDetailsValidationErrors)) {
        setGuestDetailsErrors((prev) => {
          const key = field as keyof GuestDetailsValidationErrors;
          if (prev[key] == null) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        setCreateReservationError((prev) =>
          prev === b.validation.completeGuestDetails ? null : prev,
        );
      }
    },
    [b.validation.completeGuestDetails],
  );

  const handleGuestDetailsPhoneBlur = useCallback(
    (field: "telephone" | "bookerTelephone") => {
      const errors = validateGuestDetails(guestDetails, mewsRequiredFields, guestValidationMessages);
      setGuestDetailsErrors((prev) => {
        const next = { ...prev };
        const message = errors[field];
        if (message) {
          next[field] = message;
        } else {
          delete next[field];
        }
        return next;
      });
    },
    [guestDetails, mewsRequiredFields, guestValidationMessages],
  );

  /** Dismiss stale guest-details banner once the form validates (e.g. fields already filled after a failed submit). */
  useEffect(() => {
    if (currentStep !== "details") return;
    setCreateReservationError((prev) => {
      if (prev !== b.validation.completeGuestDetails) return prev;
      const errors = validateGuestDetails(
        guestDetails,
        mewsRequiredFields,
        guestValidationMessages,
      );
      return Object.keys(errors).length === 0 ? null : prev;
    });
  }, [
    currentStep,
    guestDetails,
    mewsRequiredFields,
    guestValidationMessages,
    b.validation.completeGuestDetails,
  ]);

  /** Validates Option B card form: expiration MM/YY format and future date; holder name non-empty. */
  const validateCardForm = useCallback(
    (expiration: string, holderName: string): { expiration?: string; holderName?: string } => {
      const v = b.validation;
      const errors: { expiration?: string; holderName?: string } = {};
      const expTrim = expiration.trim();
      if (!expTrim) {
        errors.expiration = v.expirationRequired;
      } else {
        const digits = expTrim.replace(/\D/g, "");
        if (digits.length !== 4) {
          errors.expiration = v.expirationFormat;
        } else {
          const mm = digits.slice(0, 2);
          const yy = digits.slice(2, 4);
          const m = parseInt(mm, 10);
          if (m < 1 || m > 12) {
            errors.expiration = v.monthRange;
          } else {
            const yyyyMm = `20${yy}-${mm}`;
            const expDate = new Date(parseInt(yyyyMm.slice(0, 4), 10), parseInt(mm, 10) - 1, 1);
            const now = new Date();
            const endOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            if (expDate <= endOfThisMonth) {
              errors.expiration = v.cardExpired;
            }
          }
        }
      }
      if (!holderName.trim()) {
        errors.holderName = v.holderNameRequired;
      }
      return errors;
    },
    [b.validation],
  );

  const handleSecureFieldsValidityChange = useCallback(
    (state: { cardNumberValid: boolean; cvvValid: boolean }) => {
      setSecureFieldsNumberAndCvvValid(state.cardNumberValid && state.cvvValid);
    },
    [],
  );

  /** True when Option B card form is valid, Secure Fields are ready, and PAN + CVV are complete per provider. */
  const isOptionBCardFormValid = useMemo(() => {
    if (!canUseOptionB(paymentConfiguration)) return false;
    const errors = validateCardForm(cardExpiration, cardHolderName);
    if (Object.keys(errors).length > 0) return false;
    return secureFieldsReady && secureFieldsNumberAndCvvValid;
  }, [
    paymentConfiguration,
    cardExpiration,
    cardHolderName,
    validateCardForm,
    secureFieldsReady,
    secureFieldsNumberAndCvvValid,
  ]);

  const isOnSessionMode = isOnSessionPaymentMode();
  const amountToChargeOnConfirmation = useMemo(
    () => getAmountToChargeOnConfirmation(finalPriceData),
    [finalPriceData],
  );
  const onSessionEligible = isOnSessionMode && canUseOnSessionPayment(finalPriceData);
  const optionBActive = !isOnSessionMode && canUseOptionB(paymentConfiguration);
  const stayDatesIncomplete = !checkIn || !checkOut;
  const paymentStepActive = onSessionEligible || optionBActive;

  /** Resume unpaid on-session checkout after browser Back from Mews or full reload without confirmation params. */
  useEffect(() => {
    if (!isOnSessionMode) return;
    const step = searchParams.get("step");
    const urlGroupId = searchParams.get("reservationGroupId")?.trim();
    if (step === "confirmation" && urlGroupId) return;

    const pendingId = getPendingOnSessionReservationGroupId();
    if (!pendingId) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("step", "confirmation");
        next.set("reservationGroupId", pendingId);
        return next;
      },
      { replace: true },
    );
  }, [isOnSessionMode, searchParams, setSearchParams]);

  const isVerifyingOnSessionReturn =
    isOnSessionMode &&
    searchParams.get("step") === "confirmation" &&
    !!searchParams.get("reservationGroupId")?.trim();

  const confirmationPaid =
    createReservationResult != null &&
    !isLoadingConfirmation &&
    (paymentVerificationOutcome == null || paymentVerificationOutcome.status === "paid");

  const confirmationPaymentReceived = useMemo(() => {
    if (!confirmationGroupExtent) return null;
    return getChargedAmountFromGroup(confirmationGroupExtent);
  }, [confirmationGroupExtent]);

  const handleRetryPayment = useCallback(() => {
    const reservationGroupId = searchParams.get("reservationGroupId")?.trim();
    if (
      !reservationGroupId ||
      paymentVerificationOutcome?.status !== "pending"
    ) {
      return;
    }
    const returnUrl = buildPaymentConfirmationReturnUrl(reservationGroupId);
    void redirectToExternalPayment(
      buildPaymentRequestRedirectUrl(paymentVerificationOutcome.paymentRequestId, returnUrl),
      { reservationGroupId, group: confirmationGroupExtent },
    );
  }, [searchParams, paymentVerificationOutcome, redirectToExternalPayment, confirmationGroupExtent]);

  // Automatically scroll to the top of the content when step changes
  useEffect(() => {
    if (criteriaConfirmed) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep, criteriaConfirmed]);

  useEffect(() => {
    setMobileSummaryExpanded(false);
  }, [currentStep]);

  useEffect(() => {
    if (!criteriaConfirmed) setMobileSummaryExpanded(false);
  }, [criteriaConfirmed]);

  useEffect(() => {
    if (!mobileSummaryExpanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileSummaryExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileSummaryExpanded]);

  // Fix for sticky sidebar: ensure overflow is visible so position: sticky works
  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflowX;
    const originalBodyOverflow = document.body.style.overflowX;

    document.documentElement.style.overflowX = "visible";
    document.body.style.overflowX = "visible";

    return () => {
      document.documentElement.style.overflowX = originalHtmlOverflow;
      document.body.style.overflowX = originalBodyOverflow;
    };
  }, []);

  const nights = useMemo(() => {
    const a = new Date(checkIn);
    const b = new Date(checkOut);
    const diffDays = Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
    return clamp(Number.isFinite(diffDays) ? diffDays : 0, 0, 30);
  }, [checkIn, checkOut]);

  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState<string | null>(null);
  const [lastSearchCriteria, setLastSearchCriteria] = useState<{
    checkIn: string;
    checkOut: string;
    occupancyKey: string;
  } | null>(() => {
    if (!initialCheckIn || !initialCheckOut) return null;
    const a0 = Number.isFinite(initialAdultsParam) && initialAdultsParam > 0 ? initialAdultsParam : 2;
    const c0 = Number.isFinite(initialChildrenParam) && initialChildrenParam >= 0 ? initialChildrenParam : 0;
    const o0 = clampStayRowOccupancy(a0, c0);
    return {
      checkIn: initialCheckIn,
      checkOut: initialCheckOut,
      occupancyKey: stayRoomsOccupancyKey([
        {
          id: "sr-0",
          label: "",
          adults: o0.adults,
          children: o0.children,
          selectedRoomId: null,
          selectedRatePlanId: null,
          selectedMewsRateId: null,
          mewsRoomCategoryId: null,
          addonUpsellCounts: {},
          selectedProductIds: [],
        },
      ]),
    };
  });
  const [availabilityByStayRoomId, setAvailabilityByStayRoomId] = useState<
    Record<string, Record<string, RoomAvailability>>
  >({});

  const removeStayRoom = useCallback(
    (index: number) => {
      if (index <= 0) return;
      const prev = stayRoomsRef.current;
      if (prev.length <= 1 || index >= prev.length) return;
      const removedId = prev[index].id;
      const newLength = prev.length - 1;
      setPlanRoomIndex((pi) => {
        if (index < pi) return pi - 1;
        if (index === pi) return Math.min(pi, newLength - 1);
        return pi;
      });
      setEnhanceRoomIndex((ei) => {
        if (index < ei) return ei - 1;
        if (index === ei) return Math.min(ei, newLength - 1);
        return ei;
      });
      setAvailabilityByStayRoomId((a) => {
        const next = { ...a };
        delete next[removedId];
        return next;
      });
      setStayRooms(
        normalizeStayRoomLabels(
          prev.filter((_, i) => i !== index),
          b.bar.roomLabelTemplate,
        ),
      );
    },
    [b.bar.roomLabelTemplate],
  );

  const planningStayRoomId = planningStayRoom?.id ?? "";
  /** All rows' adults/children — must trigger `getAvailability` when Apply clears cache after editing any guest block or room count, not only the active plan row. */
  const stayRoomsCriteriaOccupancyKey = useMemo(
    () => stayRoomsOccupancyKey(stayRooms),
    [stayRooms],
  );

  const overlayCalendarFingerprint = useMemo(
    () =>
      `${overlayFirstClick?.getTime() ?? ""}|${overlaySecondClick?.getTime() ?? ""}`,
    [overlayFirstClick, overlaySecondClick],
  );

  useEffect(() => {
    setCriteriaOccupancySplitError(null);
  }, [stayRoomsCriteriaOccupancyKey, checkIn, checkOut, overlayCalendarFingerprint]);

  useEffect(() => {
    const justOpened = datesPopoverOpen && !prevDatesPopoverOpenRef.current;
    prevDatesPopoverOpenRef.current = datesPopoverOpen;
    if (justOpened) {
      setBarPickerDisplayedMonth(checkIn ? new Date(checkIn) : new Date());
    }
    if (!justOpened) return;
    const over = stayRoomsRef.current.some((r) => r.adults + r.children > 2);
    if (!over) return;
    setBarSplitGuestHintRemaining((r) => (r <= 0 ? 0 : r - 1));
  }, [datesPopoverOpen, checkIn]);

  useEffect(() => {
    const becameVisible =
      criteriaOverlayVisible && !prevCriteriaOverlayVisibleRef.current;
    prevCriteriaOverlayVisibleRef.current = criteriaOverlayVisible;
    if (!becameVisible) return;
    const anchor = overlayFirstClick ?? (checkIn ? new Date(checkIn) : null);
    setCriteriaOverlayPickerMonth(anchor ? new Date(anchor) : new Date());
  }, [criteriaOverlayVisible, checkIn, overlayFirstClick]);

  const hasOverTwoGuestsInSomeStayRow = useMemo(
    () => stayRooms.some((r) => r.adults + r.children > 2),
    [stayRooms],
  );

  const availabilityByLocalRoomId = availabilityByStayRoomId[planningStayRoomId] ?? {};
  /** ISO codes present on latest successful `getAvailability` price maps (union across rooms). */
  const [availabilityCurrencyKeys, setAvailabilityCurrencyKeys] = useState<string[]>([]);

  const productCurrencyKeys = useMemo(
    () => collectCurrencyKeysFromProducts(hotels?.Products ?? []),
    [hotels?.Products],
  );

  const payloadCurrencyKeys = useMemo(() => {
    const merged = new Set<string>(
      [...availabilityCurrencyKeys, ...productCurrencyKeys].map((c) => c.trim().toUpperCase()),
    );
    return sortCurrencySelectorOptions([...merged], configuration?.CurrencyCodes);
  }, [availabilityCurrencyKeys, productCurrencyKeys, configuration?.CurrencyCodes]);

  const {
    displayCurrency,
    setDisplayCurrency,
    selectorOptions,
    showCurrencyPicker,
  } = useBookDisplayCurrency({
    payloadCurrencyKeys,
    enterpriseCurrencyCodes: configuration?.CurrencyCodes,
    acceptedCurrencyCodes: hotels?.AcceptedCurrencyCodes,
    defaultCurrencyCode: hotels?.DefaultCurrencyCode,
  });

  const lastAvailabilityFetchKeyByStayRoomRef = useRef<Record<string, string>>({});
  /** Incremented when applied criteria change so late `getAvailability` responses are ignored. */
  const availabilityCriteriaGenRef = useRef(0);

  const activatePlanningRatePlan = useCallback(
    (planId: RatePlanId, localRoomId: string, rateOption: BookingRatePlanCardRateOption | undefined) => {
      const raw = availabilityByStayRoomId[planningStayRoomId]?.[localRoomId];
      patchPlanningStayRoom({
        selectedRatePlanId: planId,
        selectedMewsRateId: rateOption?.rateId ?? null,
        mewsRoomCategoryId:
          raw?.mewsRoomCategoryId ?? getMewsRoomCategoryId(localRoomId) ?? null,
      });
    },
    [availabilityByStayRoomId, patchPlanningStayRoom, planningStayRoomId],
  );

  /** Lock category + rate for the active plan room; advancing to the next room is only via Continue (task 1.7). */
  const finalizePlanningRatePlan = useCallback(
    (planId: RatePlanId, localRoomId: string, rateOption: BookingRatePlanCardRateOption | undefined) => {
      const raw = availabilityByStayRoomId[planningStayRoomId]?.[localRoomId];
      patchPlanningStayRoom({
        selectedRoomId: localRoomId,
        selectedRatePlanId: planId,
        selectedMewsRateId: rateOption?.rateId ?? null,
        mewsRoomCategoryId:
          raw?.mewsRoomCategoryId ?? getMewsRoomCategoryId(localRoomId) ?? null,
      });
    },
    [availabilityByStayRoomId, patchPlanningStayRoom, planningStayRoomId],
  );

  // Filter Logic
  const [activeFilter, setActiveFilter] = useState<FilterCategory>("All");

  const ladderRooms = useMemo(() => {
    return ROOM_LADDER;
  }, []);

  /** Logic/relevance-based filter relevance: higher = better match for active filter. */
  const getFilterRelevance = useCallback(
    (room: (typeof ladderRooms)[0], filter: FilterCategory): number => {
      if (filter === "All") return 0;
      const meta = ROOM_METADATA[room.sourceRoom.id];
      if (!meta || !meta.filterCategories.includes(filter)) return 0;
      // Strong match: view filter and viewBadges includes it
      if (["Eiger, Mönch & Jungfrau View", "Lake View", "Beatenberg View"].includes(filter)) {
        return meta.viewBadges.includes(filter as (typeof meta.viewBadges)[number]) ? 2 : 1;
      }
      if (filter === "Balcony") return room.sourceRoom.balcony ? 2 : 1;
      if (filter === "No balcony") return !room.sourceRoom.balcony ? 2 : 1;
      return 1; // Suites
    },
    [],
  );

  const filteredRooms = useMemo(() => {
    let rooms = [...ladderRooms];

    const occupancyCapacityForRoom = (r: BookingRoom): number =>
      getBookPageOccupancyCapacity(
        r.sourceRoom.id,
        getMewsRoomCategoryId(r.sourceRoom.id),
        mewsCategoryBeds,
        r.sourceRoom.capacity.maxOccupancy,
      );

    const lowestMappedPriceForSort = (r: BookingRoom): number => {
      const roomAvailability = availabilityByLocalRoomId[r.sourceRoom.id];
      if (roomAvailability) {
        let minMappedPrice: number | null = null;

        Object.entries(roomAvailability.ratesByRateId).forEach(([rateId, rate]) => {
          const planId = mapMewsRateToLocalRatePlan(rateId, rate.rateName);
          if (planId === null) return;

          if (minMappedPrice === null || rate.pricePerNight < minMappedPrice) {
            minMappedPrice = rate.pricePerNight;
          }
        });

        if (minMappedPrice !== null) return minMappedPrice;
      }

      return r.sourceRoom.pricing?.seasonalPricing?.shoulder ?? r.sourceRoom.pricing?.basePrice ?? Number.MAX_SAFE_INTEGER;
    };

    // 1. Filter by Occupancy (unless "Show all" is checked) — active planning room only
    // Uses Mews configuration bed totals when mapped; falls back to rooms.ts maxOccupancy.
    if (!showAllRooms) {
      rooms = rooms.filter(
        (r) => occupancyCapacityForRoom(r) >= activePlanOccupancy,
      );
    }

    // 2. Filter by Category Tab (All, Best Views, Suites, Classic)
    if (activeFilter !== "All") {
      rooms = rooms.filter((r) => {
        const meta = ROOM_METADATA[r.sourceRoom.id];
        if (!meta) return false; // Exclude rooms without metadata
        return meta.filterCategories.includes(activeFilter);
      });
    }

    // 3. Filter by MEWS availability (only after we have availability data and criteria are confirmed)
    const hasAvailabilityData = Object.keys(availabilityByLocalRoomId).length > 0;
    if (criteriaConfirmed && hasAvailabilityData) {
      rooms = rooms.filter((r) => {
        const localRoomId = r.sourceRoom.id;
        const raw = availabilityByLocalRoomId[localRoomId];
        const effective = effectiveRoomAvailabilityForPlanStep(
          raw,
          stayRooms,
          planRoomIndex,
        );
        return Boolean(effective?.available);
      });
    }

    // 4. Sort by occupancy fit (exact X, then X+1, X+2…), then price, then category relevance
    rooms.sort((a, b) => {
      const fitA = bookPageOccupancyFitRank(
        occupancyCapacityForRoom(a),
        activePlanOccupancy,
      );
      const fitB = bookPageOccupancyFitRank(
        occupancyCapacityForRoom(b),
        activePlanOccupancy,
      );
      if (fitA !== fitB) return fitA - fitB;

      const priceA = lowestMappedPriceForSort(a);
      const priceB = lowestMappedPriceForSort(b);
      if (priceA !== priceB) return priceA - priceB;

      const relevanceA = getFilterRelevance(a, activeFilter);
      const relevanceB = getFilterRelevance(b, activeFilter);
      if (relevanceA !== relevanceB) return relevanceB - relevanceA;

      return a.sourceRoom.id.localeCompare(b.sourceRoom.id);
    });

    return rooms;
  }, [
    ladderRooms,
    activePlanOccupancy,
    showAllRooms,
    activeFilter,
    availabilityByLocalRoomId,
    criteriaConfirmed,
    getFilterRelevance,
    stayRooms,
    planRoomIndex,
    mewsCategoryBeds,
  ]);

  // Clear active plan selection if it doesn't match the current filter
  useEffect(() => {
    if (!selectedRoomId || activeFilter === "All" || !planningStayRoom) return;
    const selectedLadder = ladderRooms.find((r) => r.sourceRoom.id === selectedRoomId);
    if (!selectedLadder) return;
    const meta = ROOM_METADATA[selectedLadder.sourceRoom.id];
    if (meta && meta.filterCategories.includes(activeFilter)) return;
    const pid = planningStayRoom.id;
    setStayRooms((prev) =>
      prev.map((sr) =>
        sr.id === pid
          ? clearedStayRoomPlan(sr)
          : sr,
      ),
    );
  }, [activeFilter, selectedRoomId, ladderRooms, planningStayRoom]);

  const selectedRoom = useMemo(() => {
    if (!selectedRoomId) return null;
    return ladderRooms.find((r) => r.sourceRoom.id === selectedRoomId) ?? null;
  }, [ladderRooms, selectedRoomId]);

  const selectedRoomLocalized = useMemo(() => {
    if (!selectedRoom) return null;
    const meta = getRoomMetadata(selectedRoom.sourceRoom.id);
    return getLocalizedBookRoomDisplay(selectedRoom, meta, b);
  }, [selectedRoom, b]);

  const selectedRatePlan = useMemo(() => {
    if (!selectedRatePlanId) return null;
    return ratePlans.find((p) => p.id === selectedRatePlanId) ?? null;
  }, [selectedRatePlanId, ratePlans]);

  const planRoomProgressPhrase = useMemo(
    () =>
      b.steps.stayStepRoomProgress
        .replace(/\{\{current\}\}/g, String(planRoomIndex + 1))
        .replace(/\{\{total\}\}/g, String(stayRooms.length)),
    [b.steps.stayStepRoomProgress, planRoomIndex, stayRooms.length],
  );

  const enhanceRoomProgressPhrase = useMemo(
    () =>
      b.steps.stayStepRoomProgress
        .replace(/\{\{current\}\}/g, String(enhanceRoomIndex + 1))
        .replace(/\{\{total\}\}/g, String(stayRooms.length)),
    [b.steps.stayStepRoomProgress, enhanceRoomIndex, stayRooms.length],
  );

  const planStepToolbarAriaLabel = useMemo(() => {
    if (stayRooms.length <= 1) return b.steps.chooseViewTitle;
    if (!planningStayRoom) return b.steps.chooseViewTitle;
    const occ = formatStayRoomOccupancySummary(planningStayRoom, b);
    return `${b.steps.chooseViewTitle} (${planRoomProgressPhrase}, ${occ})`;
  }, [stayRooms.length, planningStayRoom, b, planRoomProgressPhrase, b.steps.chooseViewTitle]);

  const enhanceStepToolbarAriaLabel = useMemo(() => {
    if (stayRooms.length <= 1) return b.steps.enhanceTitle;
    if (!enhancingStayRoom) return b.steps.enhanceTitle;
    const occ = formatStayRoomOccupancySummary(enhancingStayRoom, b);
    return `${b.steps.enhanceTitle} (${enhanceRoomProgressPhrase}, ${occ})`;
  }, [stayRooms.length, enhancingStayRoom, b, enhanceRoomProgressPhrase, b.steps.enhanceTitle]);

  const allStayRoomsPlanned = stayRooms.every(
    (sr) => sr.selectedRoomId && sr.selectedRatePlanId && sr.selectedMewsRateId,
  );

  /** Collapsed mobile bar: Stay (k/n) on plan/add-ons when booking multiple rooms. */
  const mobileCollapsedMultiRoomStayKn = useMemo(() => {
    if (stayRooms.length <= 1) return null;
    if (currentStep !== "rooms" && currentStep !== "addons") return null;
    const n = stayRooms.length;
    const k = currentStep === "addons" ? enhanceRoomIndex + 1 : planRoomIndex + 1;
    return { k, n };
  }, [stayRooms.length, currentStep, planRoomIndex, enhanceRoomIndex]);

  /** Collapsed mobile bar on details: counts matching rate-card badges (Signature free cancel vs Standard lowest price). */
  const multiRoomMobilePolicyCounts = useMemo(() => {
    if (stayRooms.length <= 1) return { freeCancellation: 0, lowestPrice: 0 };
    let freeCancellation = 0;
    let lowestPrice = 0;
    for (const sr of stayRooms) {
      if (sr.selectedRatePlanId === "standard") {
        lowestPrice += 1;
      } else if (sr.selectedRatePlanId === "signature") {
        if (!checkIn || isSignatureFreeCancellationStillOpen(checkIn)) {
          freeCancellation += 1;
        }
      }
    }
    return { freeCancellation, lowestPrice };
  }, [stayRooms, checkIn]);

  /** Collapsed mobile multi-room details: “till MMM d” beside free-cancellation badge. */
  const mobileFreeCancellationPillDeadlinePhrase = useMemo(() => {
    if (!checkIn || !isSignatureFreeCancellationStillOpen(checkIn)) return null;
    const deadline = getSignatureFreeCancellationDeadlineISO(checkIn);
    const date = formatSignatureDeadlineMonthDay(deadline, locale);
    return b.summary.mobileFreeCancellationPillTillDate.replace(/\{\{date\}\}/g, date);
  }, [checkIn, locale, b.summary.mobileFreeCancellationPillTillDate]);

  const detailsConfirmBookingDisabled =
    isCreatingReservation ||
    !selectedRoom ||
    !selectedRatePlan ||
    !contextStayRoom?.selectedMewsRateId ||
    !allStayRoomsPlanned ||
    (onSessionEligible ? false : optionBActive && !isOptionBCardFormValid) ||
    (isOnSessionMode && !onSessionEligible);

  const baseNightlyForRoom = (r: BookingRoom) =>
    r.sourceRoom.pricing?.seasonalPricing?.shoulder ?? r.sourceRoom.pricing?.basePrice ?? 0;

  const getStandardPricePerNightForRoom = useCallback(
    (localRoomId: string): number | null => {
      const roomAvailability = availabilityByLocalRoomId[localRoomId];
      if (!roomAvailability) return null;

      let minMappedPrice: number | null = null;

      Object.entries(roomAvailability.ratesByRateId).forEach(([rateId, rate]) => {
        const planId = mapMewsRateToLocalRatePlan(rateId, rate.rateName);
        // Only consider rates that map to a local plan (standard or signature)
        if (planId === null) return;

        if (minMappedPrice === null || rate.pricePerNight < minMappedPrice) {
          minMappedPrice = rate.pricePerNight;
        }
      });

      return minMappedPrice;
    },
    [availabilityByLocalRoomId],
  );

  // Build rate options (Standard & Signature) from MEWS availability data (active plan row’s cache).
  const getRateOptionsByPlanId = useCallback(
    (localRoomId: string) =>
      getRateOptionsFromRoomAvailability(availabilityByLocalRoomId[localRoomId]),
    [availabilityByLocalRoomId],
  );

  /** Addon filter options for the chip row */
  const ADDON_FILTER_OPTIONS = ["All", "Dining", "Romance"] as const;
  const filteredAddonUpsells = useMemo(() => {
    if (addonFilter === "All") return enhanceStayUpsells;
    return enhanceStayUpsells.filter((u) => u.category === addonFilter);
  }, [addonFilter, enhanceStayUpsells]);

  const chargedNights = Math.max(1, nights || 1);

  /** Per logical room: browse-time room total, add-on sell/list, and display-currency rate strings (each row’s availability cache). */
  const perStayRoomBrowseBreakdown = useMemo(() => {
    return stayRooms.map((sr) => {
      const ladderEntry = sr.selectedRoomId
        ? ladderRooms.find((r) => r.sourceRoom.id === sr.selectedRoomId)
        : null;
      const meta = ladderEntry ? getRoomMetadata(ladderEntry.sourceRoom.id) : null;
      const localized =
        ladderEntry && meta ? getLocalizedBookRoomDisplay(ladderEntry, meta, b) : null;
      const ratePlan = sr.selectedRatePlanId
        ? ratePlans.find((p) => p.id === sr.selectedRatePlanId)
        : null;
      const roomAvail =
        sr.selectedRoomId ? availabilityByStayRoomId[sr.id]?.[sr.selectedRoomId] : undefined;
      const opts = getRateOptionsFromRoomAvailability(roomAvail);
      const ro =
        sr.selectedRatePlanId === "standard"
          ? opts.standard
          : sr.selectedRatePlanId === "signature"
            ? opts.signature
            : undefined;
      const effectiveBase = ladderEntry ? baseNightlyForRoom(ladderEntry) : 0;
      const mult = ratePlan?.multiplier ?? 1;
      /**
       * After “Check availability”, cache is cleared until `getAvailability` returns. Using the ladder
       * mock here flashes a wrong total before Mews prices land — show 0 for that row until we have
       * availability for the selected room on this stay.
       */
      const rowRoomTotal =
        ro?.totalAmount ??
        (sr.selectedRoomId && sr.selectedRatePlanId
          ? criteriaConfirmed && roomAvail === undefined
            ? 0
            : mult * effectiveBase * chargedNights
          : 0);
      const rowPricePerNight =
        ro?.pricePerNight ??
        (sr.selectedRatePlanId
          ? criteriaConfirmed && roomAvail === undefined
            ? 0
            : mult * effectiveBase
          : 0);
      const browse =
        ro && sr.selectedRatePlanId
          ? browseMewsPricing(
              ro.mewsPrice,
              displayCurrency,
              rowPricePerNight,
              rowRoomTotal,
              chargedNights,
              locale,
            )
          : null;
      const roomPickRow = ro?.mewsPrice
        ? pickDisplayAmount(ro.mewsPrice, displayCurrency, MEWS_PICK_DISPLAY_OPTS)
        : null;
      const rowRoomCurrency =
        roomPickRow?.currencyCode?.trim().toUpperCase() ??
        displayCurrency.trim().toUpperCase();
      const { sell: addonSell, list: addonList } = addonSellAndListForStayRoom(
        sr,
        enhanceStayUpsells,
        hotels?.Products,
        chargedNights,
        sr.selectedRatePlanId && hotels?.Products?.length
          ? {
              displayCurrency,
              roomCurrencyCode: rowRoomCurrency,
              ratePlanId: sr.selectedRatePlanId,
            }
          : undefined,
      );
      return {
        stayRoomId: sr.id,
        label: sr.label,
        localized,
        ratePlan,
        roomTotal: rowRoomTotal,
        pricePerNight: rowPricePerNight,
        browse,
        mewsPrice: ro?.mewsPrice,
        addonSell,
        addonList,
      };
    });
  }, [
    stayRooms,
    ladderRooms,
    ratePlans,
    availabilityByStayRoomId,
    criteriaConfirmed,
    b,
    chargedNights,
    displayCurrency,
    locale,
    enhanceStayUpsells,
    hotels?.Products,
  ]);

  const addonTotal = useMemo(
    () => perStayRoomBrowseBreakdown.reduce((s, row) => s + row.addonSell, 0),
    [perStayRoomBrowseBreakdown],
  );

  /** List-price subtotal for selected add-ons (10% “online” discount vs room’s 15% direct-booker model). */
  const addonListTotal = useMemo(
    () => perStayRoomBrowseBreakdown.reduce((s, row) => s + row.addonList, 0),
    [perStayRoomBrowseBreakdown],
  );

  const aggregatedBrowseRoomTotal = useMemo(
    () => perStayRoomBrowseBreakdown.reduce((s, row) => s + row.roomTotal, 0),
    [perStayRoomBrowseBreakdown],
  );

  const effectiveBaseNightly = useMemo(() => {
    if (!selectedRoom) return 0;
    return baseNightlyForRoom(selectedRoom);
  }, [selectedRoom]);

  const activeStayRoomBrowseIndex = useMemo(() => {
    if (!contextStayRoom) return 0;
    const i = stayRooms.findIndex((r) => r.id === contextStayRoom.id);
    return i >= 0 ? i : 0;
  }, [contextStayRoom, stayRooms]);

  const activeStayRoomBrowseRow =
    perStayRoomBrowseBreakdown[activeStayRoomBrowseIndex] ?? perStayRoomBrowseBreakdown[0];
  const perStayRoomDisplayMoneyLines = useMemo(() => {
    return stayRooms.map((sr, i) => {
      const br = perStayRoomBrowseBreakdown[i];
      if (!sr.selectedRoomId || !sr.selectedRatePlanId) return null;
      return tryFormatStayRoomLinesInDisplayCurrency({
        mewsPrice: br?.mewsPrice,
        settlementRoomGrossChf: br && br.roomTotal > 1e-6 ? br.roomTotal : undefined,
        displayCurrency,
        locale,
        products: hotels?.Products,
        enhanceStayUpsells,
        addonUpsellCounts: sr.addonUpsellCounts,
        adults: sr.adults,
        children: sr.children,
        chargedNights,
        ratePlanId: sr.selectedRatePlanId ?? "standard",
      });
    });
  }, [
    stayRooms,
    perStayRoomBrowseBreakdown,
    displayCurrency,
    locale,
    hotels?.Products,
    enhanceStayUpsells,
    chargedNights,
  ]);

  const multiRoomPlannedStayMask = useMemo(
    () =>
      stayRooms.map((sr) =>
        Boolean(sr.selectedRoomId && sr.selectedRatePlanId && sr.selectedMewsRateId),
      ),
    [stayRooms],
  );

  const multiRoomBookingSummaryDisplayMoney = useMemo(() => {
    if (stayRooms.length <= 1) return null;
    return tryFormatMultiStayBookingSummaryInDisplayCurrency({
      lines: perStayRoomDisplayMoneyLines,
      plannedStayByIndex: multiRoomPlannedStayMask,
      locale,
    });
  }, [stayRooms.length, perStayRoomDisplayMoneyLines, multiRoomPlannedStayMask, locale]);

  /** Collapsed mobile bar (multi-room plan/add-ons): this stay’s room+add-on total vs compare-at. */
  const mobileMultiRoomPlanEnhanceStayPriceLabels = useMemo(() => {
    if (stayRooms.length <= 1) return null;
    const idx = activeStayRoomBrowseIndex;
    const displayLine = perStayRoomDisplayMoneyLines[idx];
    if (displayLine) {
      return {
        effectiveFormatted: displayLine.stayEffectiveFormatted,
        compareAtFormatted: displayLine.stayCompareAtFormatted,
        hasCompare: displayLine.stayCompareAtFormatted != null,
      };
    }
    const br = activeStayRoomBrowseRow;
    if (!br) return null;
    const effectiveNum = br.roomTotal + br.addonSell;
    const compareAtNum = bookingSummaryCompareAtTotal({
      grossFromApi: undefined,
      productOrderPrices: undefined,
      roomTotalLocal: br.roomTotal,
      addonTotalLocal: br.addonSell,
      addonListTotalLocal: br.addonList,
    });
    const hasCompare = compareAtNum > effectiveNum + 1e-6;
    const cur = displayCurrency;
    if (effectiveNum <= 0 && !br.ratePlan) {
      return {
        effectiveFormatted: "—",
        compareAtFormatted: null as string | null,
        hasCompare: false,
      };
    }
    return {
      effectiveFormatted: formatBookingAmount(Math.max(0, effectiveNum), cur),
      compareAtFormatted: hasCompare ? formatBookingAmount(compareAtNum, cur) : null,
      hasCompare,
    };
  }, [
    stayRooms.length,
    activeStayRoomBrowseIndex,
    perStayRoomDisplayMoneyLines,
    activeStayRoomBrowseRow,
    displayCurrency,
  ]);
  const roomTotal = activeStayRoomBrowseRow?.roomTotal ?? 0;
  const roomPricePerNight = activeStayRoomBrowseRow?.pricePerNight ?? 0;
  const selectedRateBrowse = activeStayRoomBrowseRow?.browse ?? null;

  const total = aggregatedBrowseRoomTotal + addonTotal;

  /** Effective total, compare-at (split room vs add-on discount models), and savings for summary UIs. */
  const bookingSummaryAmounts = useMemo(() => {
    // Single room: need a selected ladder room. Multi-room: context room may be unselected while planning
    // the next stay; still aggregate browse totals from rooms that are already planned.
    if (stayRooms.length <= 1 && !selectedRoom) return null;
    // Single room: need full plan. Multi-room: allow partial totals while interleaving plan → enhance per room.
    if (stayRooms.length <= 1 && !allStayRoomsPlanned) return null;
    if (
      stayRooms.length > 1 &&
      !stayRooms.some(
        (sr) => sr.selectedRoomId && sr.selectedRatePlanId && sr.selectedMewsRateId,
      )
    ) {
      return null;
    }

    if (
      finalPriceData &&
      finalPriceData.length === stayRooms.length &&
      finalPriceData.length === perStayRoomBrowseBreakdown.length
    ) {
      let sumGross = 0;
      let currency = "CHF";
      let compareAtSum = 0;
      for (let i = 0; i < stayRooms.length; i++) {
        const row = finalPriceData[i];
        const gross = row.TotalAmount?.GrossValue;
        if (gross == null || !Number.isFinite(gross)) return null;
        sumGross += gross;
        currency = row.TotalAmount?.Currency ?? currency;
        const br = perStayRoomBrowseBreakdown[i];
        compareAtSum += bookingSummaryCompareAtTotal({
          grossFromApi: gross,
          productOrderPrices: row.ProductOrderPrices,
          roomTotalLocal: br?.roomTotal ?? 0,
          addonTotalLocal: br?.addonSell ?? 0,
          addonListTotalLocal: br?.addonList ?? 0,
        });
      }
      if (sumGross <= 0) return null;
      const hasCompareAt = compareAtSum > sumGross + 1e-6;
      return {
        effective: sumGross,
        compareAt: hasCompareAt ? compareAtSum : null,
        savings: hasCompareAt ? compareAtSum - sumGross : 0,
        currency,
      };
    }

    if (stayRooms.length > 1) {
      // Never use finalPriceData[0] here: when N changes, API rows can be stale until the effect
      // clears them — a one-room gross would flash as the “multi-room” total.
      const currency = displayCurrency.trim() || "CHF";
      const effective = total > 0 ? total : null;
      if (effective == null || effective <= 0) return null;
      let compareAt = 0;
      for (let i = 0; i < stayRooms.length; i++) {
        const br = perStayRoomBrowseBreakdown[i];
        compareAt += bookingSummaryCompareAtTotal({
          grossFromApi: undefined,
          productOrderPrices: undefined,
          roomTotalLocal: br?.roomTotal ?? 0,
          addonTotalLocal: br?.addonSell ?? 0,
          addonListTotalLocal: br?.addonList ?? 0,
        });
      }
      const hasCompareAt = compareAt > effective + 1e-6;
      return {
        effective,
        compareAt: hasCompareAt ? compareAt : null,
        savings: hasCompareAt ? compareAt - effective : 0,
        currency,
      };
    }

    const apiRowAligned =
      finalPriceData &&
      finalPriceData.length === 1 &&
      stayRooms.length === 1;
    const gross = apiRowAligned ? finalPriceData[0]?.TotalAmount?.GrossValue : undefined;
    const currency =
      (apiRowAligned && finalPriceData[0]?.TotalAmount?.Currency?.trim()) ||
      displayCurrency.trim() ||
      "CHF";
    const effective = gross ?? (total > 0 ? total : null);
    if (effective == null || effective <= 0) return null;
    const compareAt = bookingSummaryCompareAtTotal({
      grossFromApi: apiRowAligned ? gross : undefined,
      productOrderPrices: apiRowAligned ? finalPriceData[0]?.ProductOrderPrices : undefined,
      roomTotalLocal: roomTotal,
      addonTotalLocal: addonTotal,
      addonListTotalLocal: addonListTotal,
    });
    const hasCompareAt = compareAt > effective + 1e-6;
    return {
      effective,
      compareAt: hasCompareAt ? compareAt : null,
      savings: hasCompareAt ? compareAt - effective : 0,
      currency,
    };
  }, [
    selectedRoom,
    allStayRoomsPlanned,
    stayRooms,
    finalPriceData,
    perStayRoomBrowseBreakdown,
    total,
    roomTotal,
    addonTotal,
    addonListTotal,
    displayCurrency,
  ]);

  const confirmationRailSummary = useMemo(() => {
    if (currentStep !== "confirmation") return null;
    const group = confirmationGroupExtent ?? createReservationResult;
    if (!group) return null;
    return buildConfirmationRailSummary(group, b, locale);
  }, [currentStep, confirmationGroupExtent, createReservationResult, b, locale]);

  const useConfirmationRailSummary = Boolean(
    currentStep === "confirmation" && confirmationRailSummary && !bookingSummaryAmounts,
  );

  // Sojern: GTM Custom Event payloads (`brs_book_*`) — phases 2–4, sojern-book-page-implementation-plan.md
  useEffect(() => {
    if (searchParams.get("step") === "confirmation") return;
    if (!criteriaConfirmed || currentStep !== "rooms") return;
    if (isCheckingAvailability || availabilityError) return;
    if (!checkIn || !checkOut) return;
    const pid = planningStayRoom?.id;
    if (!pid) return;
    const availForRow = availabilityByStayRoomId[pid];
    if (!availForRow || Object.keys(availForRow).length === 0) return;
    const criteriaKey = `${checkIn}|${checkOut}|${stayRoomsCriteriaOccupancyKey}`;
    if (sojernRoomsCriteriaKeyFiredRef.current === criteriaKey) return;
    const travelers = stayRooms.reduce((s, r) => s + r.adults + r.children, 0);
    if (
      pushSojernBookEvent({
        pageName: "rooms",
        checkInDate: checkIn,
        checkOutDate: checkOut,
        numberOfNights: nights,
        numberOfRooms: stayRooms.length,
        numberOfTravelers: travelers >= 1 ? travelers : undefined,
      })
    ) {
      sojernRoomsCriteriaKeyFiredRef.current = criteriaKey;
    }
  }, [
    searchParams,
    criteriaConfirmed,
    currentStep,
    isCheckingAvailability,
    availabilityError,
    checkIn,
    checkOut,
    planningStayRoom?.id,
    availabilityByStayRoomId,
    stayRoomsCriteriaOccupancyKey,
    stayRooms,
    nights,
  ]);

  useEffect(() => {
    if (currentStep !== "addons") return;
    if (!criteriaConfirmed || !checkIn || !checkOut) return;
    const criteriaKey = `${checkIn}|${checkOut}|${stayRoomsCriteriaOccupancyKey}`;
    if (sojernAddonsCriteriaKeyFiredRef.current === criteriaKey) return;
    const travelers = stayRooms.reduce((s, r) => s + r.adults + r.children, 0);
    if (
      pushSojernBookEvent({
        pageName: "addons",
        checkInDate: checkIn,
        checkOutDate: checkOut,
        numberOfNights: nights,
        numberOfRooms: stayRooms.length,
        numberOfTravelers: travelers >= 1 ? travelers : undefined,
      })
    ) {
      sojernAddonsCriteriaKeyFiredRef.current = criteriaKey;
    }
  }, [
    currentStep,
    criteriaConfirmed,
    checkIn,
    checkOut,
    stayRoomsCriteriaOccupancyKey,
    stayRooms,
    nights,
  ]);

  useEffect(() => {
    if (currentStep !== "details") return;
    if (!bookingSummaryAmounts) return;
    const { effective, currency } = bookingSummaryAmounts;
    const priceKey = `${checkIn}|${checkOut}|${stayRoomsCriteriaOccupancyKey}|${effective}|${currency}`;
    if (sojernDetailsPriceKeyFiredRef.current === priceKey) return;
    const travelers = stayRooms.reduce((s, r) => s + r.adults + r.children, 0);
    if (
      pushSojernBookEvent({
        pageName: "details",
        checkInDate: checkIn,
        checkOutDate: checkOut,
        price: effective,
        currency,
        numberOfNights: nights,
        numberOfRooms: stayRooms.length,
        numberOfTravelers: travelers >= 1 ? travelers : undefined,
      })
    ) {
      sojernDetailsPriceKeyFiredRef.current = priceKey;
    }
  }, [
    currentStep,
    bookingSummaryAmounts,
    checkIn,
    checkOut,
    stayRoomsCriteriaOccupancyKey,
    stayRooms,
    nights,
  ]);

  useEffect(() => {
    if (currentStep !== "confirmation") return;
    if (isLoadingConfirmation) return;
    if (confirmationLoadError) return;
    if (paymentVerificationOutcome && paymentVerificationOutcome.status !== "paid") return;
    const res = createReservationResult;
    if (!res?.Id) return;
    if (sojernConfirmationGroupIdFiredRef.current === res.Id) return;

    const numbers = res.Reservations.map((r) => r.Number).filter(Boolean);
    const confirmationNumber = numbers.join(" | ");
    if (!confirmationNumber) return;

    let price = res.TotalAmount?.GrossValue;
    let currency = res.TotalAmount?.Currency?.trim();
    if (price == null || !Number.isFinite(price)) {
      let sum = 0;
      for (const r of res.Reservations) {
        const g = r.Amount?.GrossValue;
        if (g != null && Number.isFinite(g)) sum += g;
      }
      if (sum > 0) price = sum;
    }
    if (!currency) {
      for (const r of res.Reservations) {
        const c = r.Amount?.Currency?.trim();
        if (c) {
          currency = c;
          break;
        }
      }
    }
    if (price == null || !Number.isFinite(price) || !currency?.trim()) {
      const charged = getChargedAmountFromGroup(
        confirmationGroupExtent ?? (res as GetReservationGroupResponse),
      );
      if (charged) {
        price = charged.grossValue;
        currency = charged.currency;
      }
    }
    // getReservationGroup often omits TotalAmount / Amount — fall back to the same totals used on details (Sojern).
    if (price == null || !Number.isFinite(price) || !currency?.trim()) {
      const summary = bookingSummaryAmounts;
      if (
        summary &&
        Number.isFinite(summary.effective) &&
        summary.effective > 0 &&
        summary.currency?.trim()
      ) {
        price = summary.effective;
        currency = summary.currency.trim();
      } else if (total > 0 && displayCurrency?.trim()) {
        price = total;
        currency = displayCurrency.trim();
      }
    }
    if (price == null || !Number.isFinite(price) || !currency?.trim()) return;

    const travelers = stayRooms.reduce((s, r) => s + r.adults + r.children, 0);
    const nRooms = res.Reservations.length || stayRooms.length;
    if (
      pushSojernBookEvent({
        pageName: "confirmation",
        checkInDate: checkIn,
        checkOutDate: checkOut,
        price,
        currency,
        confirmationNumber,
        numberOfNights: nights,
        numberOfRooms: nRooms >= 1 ? nRooms : undefined,
        numberOfTravelers: travelers >= 1 ? travelers : undefined,
      })
    ) {
      sojernConfirmationGroupIdFiredRef.current = res.Id;
    }
  }, [
    currentStep,
    isLoadingConfirmation,
    confirmationLoadError,
    paymentVerificationOutcome,
    createReservationResult,
    confirmationGroupExtent,
    checkIn,
    checkOut,
    nights,
    stayRooms,
    bookingSummaryAmounts,
    total,
    displayCurrency,
  ]);

  // Google Ads / GTM `purchase` — same paid-confirmation gates as Sojern; CHF only; no plaintext email.
  useEffect(() => {
    if (currentStep !== "confirmation") return;
    if (isLoadingConfirmation) return;
    if (confirmationLoadError) return;
    if (paymentVerificationOutcome && paymentVerificationOutcome.status !== "paid") return;
    const res = createReservationResult;
    if (!res?.Id) return;
    if (purchaseGroupIdFiredRef.current === res.Id) return;
    if (hasBookPurchaseFired(res.Id)) {
      purchaseGroupIdFiredRef.current = res.Id;
      return;
    }

    const itemNameByRoomCategoryId: Record<string, string> = {};
    for (const reservation of res.Reservations ?? []) {
      const categoryId = reservation.RoomCategoryId?.trim();
      if (!categoryId) continue;
      itemNameByRoomCategoryId[categoryId] = confirmationRoomDisplayName(categoryId, b);
    }

    let cancelled = false;
    void pushBookPurchaseEvent({
      reservationGroupId: res.Id,
      group: res,
      itemNameByRoomCategoryId,
      email: purchaseEmailFromGuestDetails(guestDetails),
    }).then((ok) => {
      if (cancelled || !ok) return;
      purchaseGroupIdFiredRef.current = res.Id;
    });
    return () => {
      cancelled = true;
    };
  }, [
    currentStep,
    isLoadingConfirmation,
    confirmationLoadError,
    paymentVerificationOutcome,
    createReservationResult,
    guestDetails,
    b,
  ]);

  const bookingSummaryDisplayMoney = useMemo(() => {
    if (stayRooms.length > 1) {
      return multiRoomBookingSummaryDisplayMoney;
    }
    if (!selectedRoom || !selectedRatePlanId) return null;
    const opts = getRateOptionsByPlanId(selectedRoom.sourceRoom.id);
    const ro = selectedRatePlanId === "standard" ? opts.standard : opts.signature;
    const head = stayRooms[0];
    return tryFormatBookingSummaryInDisplayCurrency({
      roomMewsPrice: ro?.mewsPrice,
      displayCurrency,
      locale,
      products: hotels?.Products,
      enhanceStayUpsells,
      selectedUpsells: aggregateAddonUpsellCounts,
      occupancy: { adults: head?.adults ?? 1, children: head?.children ?? 0 },
      chargedNights,
      ratePlanId: selectedRatePlanId,
    });
  }, [
    stayRooms,
    multiRoomBookingSummaryDisplayMoney,
    selectedRoom,
    selectedRatePlanId,
    getRateOptionsByPlanId,
    displayCurrency,
    locale,
    hotels?.Products,
    enhanceStayUpsells,
    aggregateAddonUpsellCounts,
    chargedNights,
  ]);

  const bookingSummaryTotalLabels = useMemo(() => {
    const effectiveFormatted =
      bookingSummaryDisplayMoney?.effectiveFormatted ??
      (bookingSummaryAmounts
        ? formatBookingAmount(bookingSummaryAmounts.effective, bookingSummaryAmounts.currency)
        : confirmationRailSummary?.totalFormatted ?? null);
    const compareAtFormatted =
      bookingSummaryDisplayMoney?.compareAtFormatted ??
      (bookingSummaryAmounts?.compareAt != null
        ? formatBookingAmount(bookingSummaryAmounts.compareAt, bookingSummaryAmounts.currency)
        : null);
    return {
      effectiveFormatted: effectiveFormatted ?? "—",
      compareAtFormatted,
    };
  }, [bookingSummaryDisplayMoney, bookingSummaryAmounts, confirmationRailSummary?.totalFormatted]);

  const bookingSummarySavingsPillFormatted = useMemo(() => {
    if (bookingSummaryDisplayMoney?.hasSavingsPill && bookingSummaryDisplayMoney.savingsFormatted) {
      return bookingSummaryDisplayMoney.savingsFormatted;
    }
    if (bookingSummaryAmounts && bookingSummaryAmounts.savings > 0.005) {
      return formatBookingAmount(bookingSummaryAmounts.savings, bookingSummaryAmounts.currency);
    }
    return null;
  }, [bookingSummaryDisplayMoney, bookingSummaryAmounts]);

  /** Enhance-step cards: per-upsell sell + list labels from product `Pricing.Value` in display currency (no client FX). */
  const upsellCardMoneyByUpsellId = useMemo(() => {
    const browsePlan =
      enhancingStayRoom.selectedRatePlanId === "signature" ? "signature" : "standard";
    const enhanceBr = perStayRoomBrowseBreakdown[enhanceRoomIndex];
    const roomPickEnhance = pickDisplayAmount(
      enhanceBr?.mewsPrice,
      displayCurrency,
      MEWS_PICK_DISPLAY_OPTS,
    );
    const roomCurrencyCode =
      roomPickEnhance?.currencyCode?.trim().toUpperCase() ??
      displayCurrency.trim().toUpperCase();
    const productById = new Map<string, Product>();
    for (const p of hotels?.Products ?? []) {
      productById.set(p.Id, p);
    }
    const out: Record<string, { sellFormatted: string; listFormatted: string | null }> = {};
    for (const u of enhanceStayUpsells) {
      const sellBaseId = upsellPrimaryMewsProductIdForBrowse(u, browsePlan);
      const listAnchorBaseId = upsellPrimaryMewsProductIdForBrowse(u, "standard");
      const listAnchorProd = listAnchorBaseId ? productById.get(listAnchorBaseId) : undefined;
      if (!sellBaseId) {
        out[u.id] = {
          sellFormatted: formatBookMoney(u.price, "CHF", locale),
          listFormatted:
            u.listPrice > u.price + 1e-6 ? formatBookMoney(u.listPrice, "CHF", locale) : null,
        };
        continue;
      }
      const sellPicked = pickAbsoluteUpsellGrossForEnhanceCard(
        productById.get(sellBaseId),
        displayCurrency,
        roomCurrencyCode,
        listAnchorProd,
      );
      const listAnchorPicked = listAnchorProd
        ? pickAbsoluteUpsellGrossForEnhanceCard(
            listAnchorProd,
            displayCurrency,
            roomCurrencyCode,
            listAnchorProd,
          )
        : null;
      if (sellPicked) {
        const listGrossSource = listAnchorPicked ?? sellPicked;
        const listVal = listGrossSource.value / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST;
        const listCurrencyCode = listAnchorPicked?.currencyCode ?? sellPicked.currencyCode;
        const showStrike = listVal > sellPicked.value + 1e-6;
        out[u.id] = {
          sellFormatted: formatBookMoney(sellPicked.value, sellPicked.currencyCode, locale),
          listFormatted: showStrike
            ? formatBookMoney(listVal, listCurrencyCode, locale)
            : null,
        };
      } else {
        const chfFb = resolveEnhanceCardChfSellAndListAnchor(
          sellBaseId,
          listAnchorBaseId,
          productById,
          hotels?.Products,
        );
        if (chfFb) {
          const listVal = chfFb.listChf / MEWS_PRODUCT_SELL_AS_FRACTION_OF_LIST;
          const showStrike = listVal > chfFb.sellChf + 1e-6;
          out[u.id] = {
            sellFormatted: formatBookMoney(chfFb.sellChf, "CHF", locale),
            listFormatted: showStrike ? formatBookMoney(listVal, "CHF", locale) : null,
          };
        } else {
          out[u.id] = {
            sellFormatted: formatBookMoney(u.price, "CHF", locale),
            listFormatted:
              u.listPrice > u.price + 1e-6 ? formatBookMoney(u.listPrice, "CHF", locale) : null,
          };
        }
      }
    }
    return out;
  }, [
    enhanceStayUpsells,
    enhancingStayRoom.selectedRatePlanId,
    enhanceRoomIndex,
    perStayRoomBrowseBreakdown,
    hotels?.Products,
    displayCurrency,
    locale,
  ]);

  /** Micro-copy for PerPerson / PerPersonPerTimeUnit products (task 2.6). */
  const upsellPricedForRoomGuestsHintById = useMemo(() => {
    const browsePlan =
      enhancingStayRoom.selectedRatePlanId === "signature" ? "signature" : "standard";
    const productById = new Map<string, Product>();
    for (const p of hotels?.Products ?? []) {
      productById.set(p.Id, p);
    }
    const hint = b.upsellCard.pricedForRoomGuests;
    const out: Record<string, string | undefined> = {};
    for (const u of enhanceStayUpsells) {
      const baseId = upsellPrimaryMewsProductIdForBrowse(u, browsePlan);
      if (!baseId) continue;
      const mode = productById.get(baseId)?.ChargingMode?.trim().toLowerCase();
      if (mode === "perperson" || mode === "perpersonpertimeunit") {
        out[u.id] = hint;
      }
    }
    return out;
  }, [
    hotels?.Products,
    enhanceStayUpsells,
    enhancingStayRoom.selectedRatePlanId,
    b.upsellCard.pricedForRoomGuests,
  ]);

  const canContinue = planningRoomComplete;

  /** Mobile sticky bar: show Back whenever `goBack` does something (incl. room 2+ plan → previous enhance). */
  const showBookingStickyBack =
    currentStep === "addons" ||
    currentStep === "details" ||
    (currentStep === "rooms" && planRoomIndex > 0);

  // Step 4.3: Fetch final price when on details step (before confirming booking) — one call, N reservations (Phase 3).
  useEffect(() => {
    if (currentStep !== "details" || createReservationResult) {
      setFinalPriceData(null);
      return;
    }
    if (!configuration || !allStayRoomsPlanned) return;
    if (!checkIn || !checkOut) {
      setFinalPriceData(null);
      return;
    }
    const ageCategories = configuration.AgeCategories ?? [];
    const reservations = buildStayRoomsReservationPriceRequests({
      stayRooms,
      availabilityByStayRoomId,
      checkIn,
      checkOut,
      ageCategories,
    });
    if (!reservations || reservations.length !== stayRooms.length) {
      setFinalPriceData(null);
      return;
    }

    let cancelled = false;
    getReservationPrice({
      reservations,
      currencyCode: "CHF",
    }).then((response) => {
      if (cancelled) return;
      if (response.error) {
        setFinalPriceData(null);
        return;
      }
      if (!response.data) {
        setFinalPriceData(null);
        return;
      }
      const aligned = alignReservationPriceResponses(reservations, response.data);
      if (!aligned.ok) {
        setFinalPriceData(null);
        // API was OK: fall back to browse-time totals in the summary. Don't alarm users when mapping
        // fails (shape/identifier edge cases); still log in dev.
        if (
          (aligned.reason === "length" || aligned.reason === "identifier") &&
          import.meta.env.DEV
        ) {
          console.warn("[Book] getReservationPrice row alignment skipped:", aligned.reason, {
            requested: reservations.length,
            rows: Array.isArray(response.data) ? response.data.length : response.data,
          });
        }
        return;
      }
      setFinalPriceData(aligned.ordered);
    });
    return () => {
      cancelled = true;
    };
  }, [
    currentStep,
    createReservationResult,
    configuration,
    allStayRoomsPlanned,
    stayRooms,
    availabilityByStayRoomId,
    checkIn,
    checkOut,
  ]);

  // Task 4: Load PCI Proxy Secure Fields script when on details step and Option B is eligible
  useEffect(() => {
    if (currentStep !== "details" || isOnSessionMode || !canUseOptionB(paymentConfiguration)) return;
    loadPciProxySecureFieldsScript().catch((err) => {
      if (import.meta.env.DEV) {
        console.warn("[Book] PCI Proxy Secure Fields script load failed:", err);
      }
    });
  }, [currentStep, paymentConfiguration, isOnSessionMode]);

  // Reset Secure Fields ready / hosted-field validity when leaving details step
  useEffect(() => {
    if (currentStep !== "details") {
      setSecureFieldsReady(false);
      setSecureFieldsNumberAndCvvValid(false);
    }
  }, [currentStep]);

  // On return from payment redirect — verify on-session payment or load reservation (Option B)
  useEffect(() => {
    const step = searchParams.get("step");
    const reservationGroupId = searchParams.get("reservationGroupId");
    if (step !== "confirmation" || !reservationGroupId?.trim()) return;
    let cancelled = false;
    setConfirmationLoadError(null);
    setPaymentVerificationOutcome(null);
    setConfirmationGroupExtent(null);
    setCreateReservationResult(null);
    setIsLoadingConfirmation(true);
    setCurrentStep("confirmation");

    const trimmedGroupId = reservationGroupId.trim();
    const fetchParams = isOnSessionMode
      ? {
          reservationGroupId: trimmedGroupId,
          extent: { PaymentRequests: true, Payments: true },
        }
      : { reservationGroupId: trimmedGroupId };

    getReservationGroup(fetchParams)
      .then((res) => {
        if (cancelled) return;
        if (res.error) {
          setConfirmationLoadError(res.error.Message ?? translations.book.errors.loadReservation);
          return;
        }
        if (!res.data) return;

        if (isOnSessionMode) {
          const group = res.data;
          setConfirmationGroupExtent(group);
          const outcome = resolvePaymentOutcome(group);
          setPaymentVerificationOutcome(outcome);
          if (outcome.status === "paid") {
            clearPendingOnSessionReservationGroupId();
            setCreateReservationResult(group as CreateReservationGroupResponse);
            const url = new URL(window.location.href);
            url.searchParams.set("step", "confirmation");
            url.searchParams.set("reservationGroupId", trimmedGroupId);
            window.history.replaceState(null, "", `${url.pathname}?${url.searchParams.toString()}`);
            return;
          }
          if (outcome.status === "failed") {
            clearPendingOnSessionReservationGroupId();
          }
          return;
        }

        setCreateReservationResult(res.data as CreateReservationGroupResponse);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingConfirmation(false);
      });
    return () => {
      cancelled = true;
    };
  }, [searchParams, translations.book.errors.loadReservation, isOnSessionMode]);

  /**
   * One `hotels/getAvailability` call for a single logical stay row (`OccupancyData` = that row only).
   * Results are stored under `availabilityByStayRoomId[stayRoomId]` so other rooms' caches stay intact.
   */
  const fetchAvailabilityForStayRoom = useCallback(
    async (args: {
      stayRoomId: string;
      adults: number;
      children: number;
      checkIn: string;
      checkOut: string;
      criteriaGen: number;
      criteriaOccupancyKey: string;
    }) => {
      const fetchKey = buildStayRoomAvailabilityFetchKey(
        args.stayRoomId,
        args.checkIn,
        args.checkOut,
        args.adults,
        args.children,
        args.criteriaOccupancyKey,
      );
      if (lastAvailabilityFetchKeyByStayRoomRef.current[args.stayRoomId] === fetchKey) {
        return;
      }

      if (!configuration) {
        setAvailabilityError(b.errors.loadAvailabilityConfig);
        return;
      }

      const ageCategories = configuration.AgeCategories ?? [];
      if (!ageCategories.length) {
        setAvailabilityError(b.errors.loadAvailabilityAge);
        return;
      }

      const startUtc = isoDateToUtcMidnight(args.checkIn);
      const endUtc = isoDateToUtcMidnight(args.checkOut);
      if (!startUtc || !endUtc) return;

      const occupancyData = convertToOccupancies(args.adults, args.children, ageCategories);
      if (!occupancyData.length) {
        setAvailabilityError(b.errors.occupancyInvalid);
        return;
      }

      setIsCheckingAvailability(true);
      setAvailabilityError(null);

      loadRoomMappingFromEnv();

      const stayNights = Math.max(
        1,
        Math.min(
          30,
          Math.round(
            (new Date(`${args.checkOut}T00:00:00Z`).getTime() -
              new Date(`${args.checkIn}T00:00:00Z`).getTime()) /
              86400000,
          ),
        ),
      );

      let rateIds: string[] | undefined;
      try {
        const mappingJson = import.meta.env.VITE_MEWS_RATE_MAPPING_JSON;
        if (mappingJson && typeof mappingJson === "string") {
          const mapping = JSON.parse(mappingJson) as Record<string, string>;
          rateIds = Object.keys(mapping);
          if (rateIds.length === 0) rateIds = undefined;
        }
      } catch {
        rateIds = undefined;
      }

      const response = await getAvailability({
        startUtc,
        endUtc,
        occupancyData,
        rateIds,
      });

      if (args.criteriaGen !== availabilityCriteriaGenRef.current) {
        setIsCheckingAvailability(false);
        return;
      }

      if (response.error || !response.data) {
        setAvailabilityError(response.error?.Message ?? b.errors.retrieveAvailability);
        setIsCheckingAvailability(false);
        return;
      }

      const rateNameById: Record<string, string | undefined> = {};
      (response.data.Rates ?? []).forEach((rate) => {
        const preferredLanguages = ["en-US", "de-CH", "de-DE", "en-GB"];
        let name = "";
        for (const lang of preferredLanguages) {
          if (rate.Name[lang]) {
            name = rate.Name[lang];
            break;
          }
        }
        if (!name) {
          const firstKey = Object.keys(rate.Name)[0];
          if (firstKey) {
            name = rate.Name[firstKey];
          }
        }
        rateNameById[rate.Id] = name;
      });

      const roomCategoryAvailabilities = response.data.RoomCategoryAvailabilities ?? [];

      const currencyKeyAccumulator = new Set<string>();
      const normalized: Record<string, RoomAvailability> = {};

      roomCategoryAvailabilities.forEach((category) => {
        const mewsRoomCategoryId = category.RoomCategoryId;
        const availCount = category.AvailableRoomCount ?? 0;

        let localRoomId = getLocalRoomId(mewsRoomCategoryId);
        if (!localRoomId) {
          const mapped = ladderRooms.find(
            (room) => getMewsRoomCategoryId(room.sourceRoom.id) === mewsRoomCategoryId,
          );
          if (mapped) {
            localRoomId = mapped.sourceRoom.id;
          }
        }

        if (!localRoomId) {
          if (import.meta.env.DEV) {
            console.warn(
              "[Availability] Unmapped Mews RoomCategoryId – skipping:",
              mewsRoomCategoryId,
            );
          }
          return;
        }

        const ratesByRateId: Record<string, RoomAvailabilityRate> = {};

        category.RoomOccupancyAvailabilities?.forEach((occ) => {
          occ.Pricing?.forEach((pricing) => {
            const priceObj = pricing.Price as MewsMultiCurrencyPrice | undefined;
            if (priceObj) {
              for (const k of collectCurrencyKeysFromPrice(priceObj)) {
                currencyKeyAccumulator.add(k);
              }
            }
            const totals = pricing.Price?.Total;
            if (!totals) return;
            const preferredCurrency = "CHF";
            const currency =
              totals[preferredCurrency] !== undefined ? preferredCurrency : Object.keys(totals)[0];
            if (!currency) return;
            const totalAmount = totals[currency] ?? 0;
            if (!totalAmount || stayNights <= 0) return;
            const pricePerNight = totalAmount / stayNights;

            if (import.meta.env.DEV && localRoomId === "junior-suite") {
              console.log("[Pricing Debug] Junior Suite:", {
                rateId: pricing.RateId,
                currency,
                totalAmount,
                stayNights,
                pricePerNight,
                rateName: rateNameById[pricing.RateId],
              });
            }

            const existing = ratesByRateId[pricing.RateId];
            if (!existing || totalAmount < existing.totalAmount) {
              ratesByRateId[pricing.RateId] = {
                pricePerNight,
                totalAmount,
                rateName: rateNameById[pricing.RateId],
                ...(priceObj ? { mewsPrice: priceObj } : {}),
              };
            }
          });
        });

        normalized[localRoomId] = {
          available: availCount > 0,
          availableRoomCount: availCount,
          mewsRoomCategoryId,
          ratesByRateId,
        };
      });

      ladderRooms.forEach((room) => {
        const localRoomId = room.sourceRoom.id;
        if (!normalized[localRoomId]) {
          const mappedCategoryId = getMewsRoomCategoryId(localRoomId);
          if (!mappedCategoryId && import.meta.env.DEV) {
            console.warn("[Availability] No Mews RoomCategoryId mapping for local room:", localRoomId);
          }
          normalized[localRoomId] = {
            available: false,
            availableRoomCount: 0,
            mewsRoomCategoryId: mappedCategoryId ?? "unmapped",
            ratesByRateId: {},
          };
        }
      });

      if (args.criteriaGen !== availabilityCriteriaGenRef.current) {
        setIsCheckingAvailability(false);
        return;
      }

      const totalGuestsForFetch = args.adults + args.children;
      if (totalGuestsForFetch === 3) {
        const bookable = getBookableLadderLocalRoomIdsForOccupancy(
          normalized,
          3,
          mewsCategoryBeds,
        );
        if (
          bookable.length === 1 &&
          bookable[0] === "family-suite" &&
          stayRoomsRef.current.some((r) => r.adults + r.children === 3)
        ) {
          const splitRes = splitAllThreeGuestStayRows(
            stayRoomsRef.current,
            b.bar.roomLabelTemplate,
            () => `sr-${stayRoomIdSeqRef.current++}`,
            MAX_STAY_ROOMS,
          );
          if (splitRes.ok && splitRes.splitOccurred) {
            availabilityCriteriaGenRef.current += 1;
            const nextGen = availabilityCriteriaGenRef.current;
            stayRoomsRef.current = splitRes.rooms;
            setStayRooms(splitRes.rooms);
            setPlanRoomIndex(0);
            setEnhanceRoomIndex(0);
            lastAvailabilityFetchKeyByStayRoomRef.current = {};
            setAvailabilityByStayRoomId({});
            setAvailabilityCurrencyKeys([]);
            setIsCheckingAvailability(false);
            toast.success(
              b.splitOccupancy.dividedAcrossRoomsToast.replace(
                /\{\{n\}\}/g,
                String(splitRes.rooms.length),
              ),
            );
            void fetchAvailabilityForStayRoom({
              stayRoomId: splitRes.rooms[0]!.id,
              adults: splitRes.rooms[0]!.adults,
              children: splitRes.rooms[0]!.children,
              checkIn: args.checkIn,
              checkOut: args.checkOut,
              criteriaGen: nextGen,
              criteriaOccupancyKey: stayRoomsOccupancyKey(splitRes.rooms),
            });
            return;
          }
        }
      }

      lastAvailabilityFetchKeyByStayRoomRef.current[args.stayRoomId] = fetchKey;

      setAvailabilityCurrencyKeys((prev) => {
        const merged = new Set(
          [...prev, ...currencyKeyAccumulator].map((c) => c.trim().toUpperCase()),
        );
        return sortCurrencySelectorOptions([...merged], configuration?.CurrencyCodes);
      });

      setAvailabilityByStayRoomId((prev) => ({
        ...prev,
        [args.stayRoomId]: normalized,
      }));

      if (import.meta.env.DEV) {
        Object.entries(normalized).forEach(([localRoomId, avail]) => {
          const rateIdsReceived = Object.keys(avail.ratesByRateId);
          const mapped = rateIdsReceived.map((rateId) => {
            const planId = mapMewsRateToLocalRatePlan(rateId, avail.ratesByRateId[rateId]?.rateName);
            return { rateId, planId };
          });
          const hasStandard = mapped.some((m) => m.planId === "standard");
          const hasSignature = mapped.some((m) => m.planId === "signature");
          if (!hasStandard || !hasSignature) {
            console.log("[Availability] Rate plans per room:", {
              localRoomId,
              rateIdsReceived,
              mapped,
              hasStandard,
              hasSignature,
              hint: !hasStandard
                ? "Saver/Standard is greyed out: no Mews rate mapped to 'standard'. Ensure VITE_MEWS_RATE_MAPPING_JSON includes the Saver rate Id and that your Mews Configuration includes this rate."
                : undefined,
            });
          }
        });
      }

      setIsCheckingAvailability(false);
    },
    [
      configuration,
      ladderRooms,
      b.errors,
      b.bar.roomLabelTemplate,
      b.splitOccupancy.dividedAcrossRoomsToast,
      mewsCategoryBeds,
    ],
  );

  /** Sequential `getAvailability`: room 1 after criteria apply; room k after advancing to that plan step. */
  useEffect(() => {
    if (searchParams.get("step") === "confirmation") return;
    if (!criteriaConfirmed || currentStep !== "rooms") return;
    if (!checkIn || !checkOut || !configuration) return;
    const sr = stayRooms[planRoomIndex];
    if (!sr) return;
    const gen = availabilityCriteriaGenRef.current;
    void fetchAvailabilityForStayRoom({
      stayRoomId: sr.id,
      adults: sr.adults,
      children: sr.children,
      checkIn,
      checkOut,
      criteriaGen: gen,
      criteriaOccupancyKey: stayRoomsCriteriaOccupancyKey,
    });
  }, [
    searchParams,
    criteriaConfirmed,
    currentStep,
    checkIn,
    checkOut,
    configuration,
    planRoomIndex,
    planningStayRoom?.id,
    planningStayRoom?.adults,
    planningStayRoom?.children,
    stayRoomsCriteriaOccupancyKey,
    fetchAvailabilityForStayRoom,
  ]);

  const onChooseRoom = (_key: LadderKey, roomId: string) => {
    patchPlanningStayRoom({
      selectedRoomId: roomId,
      selectedRatePlanId: null,
      selectedMewsRateId: null,
      mewsRoomCategoryId: null,
    });
    setRatesSectionExpanded(false);
  };

  const confirmCriteria = (overrides?: {
    checkIn?: string;
    checkOut?: string;
    /** Criteria overlay vs booking bar — bar cannot lock criteria before overlay (task 2.4). */
    applySource?: "overlay" | "bar";
  }) => {
    if (mewsConfigError && import.meta.env.DEV) {
      console.warn(
        "[Mews] Configuration error detected when confirming criteria:",
        mewsConfigError,
      );
    }

    const applySource = overrides?.applySource ?? "bar";
    if (applySource === "bar" && !criteriaConfirmedRef.current) {
      toast.message(b.splitOccupancy.barApplyRequiresOverlayFirst);
      return;
    }

    const cIn = overrides?.checkIn ?? checkIn;
    const cOut = overrides?.checkOut ?? checkOut;

    if (!cIn || !cOut) return;

    setCriteriaOccupancySplitError(null);

    /** Split only from the **criteria overlay** so the booking-bar dropdown keeps the rows the user set (no live 2+1 / 2+2 expansion in the popover). Bar Apply uses current `stayRooms` as-is. */
    let roomsForApply: StayRoom[];
    if (applySource === "overlay") {
      const splitRes = applyOccupancySplitToStayRooms(
        stayRoomsRef.current,
        b.bar.roomLabelTemplate,
        () => `sr-${stayRoomIdSeqRef.current++}`,
        MAX_STAY_ROOMS,
      );
      if (!splitRes.ok) {
        setCriteriaOccupancySplitError(splitRes.reason);
        const msg =
          splitRes.reason === "max_rooms"
            ? b.splitOccupancy.splitBlockedMaxRooms
            : b.splitOccupancy.splitBlockedInsufficientAdults.replace(
                /\{\{addRoom\}\}/g,
                b.bar.addRoom,
              );
        toast.error(msg);
        return;
      }
      roomsForApply = splitRes.rooms;
      if (splitRes.splitOccurred) {
        toast.success(
          b.splitOccupancy.dividedAcrossRoomsToast.replace(
            /\{\{n\}\}/g,
            String(roomsForApply.length),
          ),
        );
      }
    } else {
      roomsForApply = stayRoomsRef.current;
    }

    const occupancyKey = stayRoomsOccupancyKey(roomsForApply);
    const isDuplicateApply =
      lastSearchCriteria !== null &&
      lastSearchCriteria.checkIn === cIn &&
      lastSearchCriteria.checkOut === cOut &&
      lastSearchCriteria.occupancyKey === occupancyKey;

    if (roomsForApply !== stayRoomsRef.current) {
      setStayRooms(roomsForApply);
    }
    stayRoomsRef.current = roomsForApply;

    setCheckIn(cIn);
    setCheckOut(cOut);
    setCriteriaConfirmed(true);
    setDatesPopoverOpen(false);
    const lead = roomsForApply[0];
    setSearchParams({
      checkIn: cIn,
      checkOut: cOut,
      adults: String(lead?.adults ?? 2),
      children: String(lead?.children ?? 0),
    });
    setCurrentStep("rooms");

    if (!isDuplicateApply) {
      availabilityCriteriaGenRef.current += 1;
      const genAfterBump = availabilityCriteriaGenRef.current;
      setAvailabilityByStayRoomId({});
      setAvailabilityCurrencyKeys([]);
      lastAvailabilityFetchKeyByStayRoomRef.current = {};
      setLastSearchCriteria({ checkIn: cIn, checkOut: cOut, occupancyKey });
      setPlanRoomIndex(0);
      setEnhanceRoomIndex(0);
      /** Fire fetch here so it cannot be missed when URL/searchParams are unchanged (lead-room query only) or effect ordering leaves an empty cache. Same `occupancyKey` snapshot as duplicate check. */
      const planSr = roomsForApply[0];
      if (planSr && cIn && cOut && configuration) {
        void fetchAvailabilityForStayRoom({
          stayRoomId: planSr.id,
          adults: planSr.adults,
          children: planSr.children,
          checkIn: cIn,
          checkOut: cOut,
          criteriaGen: genAfterBump,
          criteriaOccupancyKey: occupancyKey,
        });
      }
    }
  };

  const goToRooms = () => {
    setCurrentStep("rooms");
  };

  /** From summary: open plan step for stay `index`, expand rates if that stay already has a room picked, scroll into view. */
  const focusPlanStepOnStayRoom = useCallback(
    (index: number) => {
      const sr = stayRooms[index];
      if (!sr) return;
      const roomId = sr.selectedRoomId;
      setCurrentStep("rooms");
      setPlanRoomIndex(index);
      setMobileSummaryExpanded(false);
      window.setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!roomId) {
              setRatesSectionExpanded(false);
              mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              return;
            }
            setRatesSectionExpanded(true);
            window.setTimeout(() => {
              const el =
                document.getElementById(`rates-content-${roomId}`) ??
                document.getElementById(`room-${roomId}`);
              if (!el) {
                mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
              }
              const rect = el.getBoundingClientRect();
              const padding = 24;
              const viewportHeight = window.innerHeight;
              const scrollY = window.scrollY ?? window.pageYOffset;
              let targetScrollY: number | null = null;
              if (rect.top < padding) {
                targetScrollY = scrollY + rect.top - padding;
              } else if (rect.bottom > viewportHeight - padding) {
                targetScrollY = scrollY + rect.bottom - viewportHeight + padding;
              }
              if (targetScrollY !== null) {
                window.scrollTo({ top: Math.max(0, targetScrollY), behavior: "smooth" });
              }
            }, 560);
          });
        });
      }, 0);
    },
    [stayRooms],
  );

  const goToEnhance = () => {
    setEnhanceRoomIndex(Math.max(0, stayRooms.length - 1));
    setCurrentStep("addons");
  };

  const goToDetails = () => {
    setCurrentStep("details");
  };

  /** Expanded mobile summary: jump to guest-details step (full stay summary before payment). */
  const openFullBookingSummaryFromExpandedSheet = useCallback(() => {
    setMobileSummaryExpanded(false);
    setCurrentStep("details");
    window.setTimeout(() => {
      mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  }, []);

  const continueEnhanceStepOrDetails = useCallback(() => {
    if (enhanceRoomIndex < stayRooms.length - 1) {
      setPlanRoomIndex(enhanceRoomIndex + 1);
      setCurrentStep("rooms");
      mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setCurrentStep("details");
    }
  }, [enhanceRoomIndex, stayRooms.length]);

  const goBack = () => {
    if (currentStep === "rooms") {
      if (planRoomIndex > 0) {
        setEnhanceRoomIndex(planRoomIndex - 1);
        setCurrentStep("addons");
      }
    } else if (currentStep === "addons") {
      setCurrentStep("rooms");
      setPlanRoomIndex(enhanceRoomIndex);
    } else if (currentStep === "details") {
      setCurrentStep("addons");
      setEnhanceRoomIndex(Math.max(0, stayRoomsRef.current.length - 1));
    }
  };

  /** Upsells currently added to the stay (for Booking Summary list). */
  const addedUpsellsForSummary = useMemo(() => {
    return enhanceStayUpsells.filter((u) => (aggregateAddonUpsellCounts[u.id] ?? 0) > 0);
  }, [enhanceStayUpsells, aggregateAddonUpsellCounts]);

  /** Single-room stay summary: per–add-on line sell in display/room currency (matches browse totals). */
  const staySummaryAddonLineSellFormattedByUpsellId = useMemo(() => {
    if (stayRooms.length !== 1) return {};
    const sr = stayRooms[0];
    if (!sr) return {};
    return addonSellFormattedByUpsellIdForStayRoom(
      sr,
      aggregateAddonUpsellCounts,
      perStayRoomBrowseBreakdown[0]?.mewsPrice,
      enhanceStayUpsells,
      hotels?.Products,
      displayCurrency,
      chargedNights,
      locale,
    );
  }, [
    stayRooms,
    aggregateAddonUpsellCounts,
    perStayRoomBrowseBreakdown,
    enhanceStayUpsells,
    hotels?.Products,
    displayCurrency,
    chargedNights,
    locale,
  ]);

  /** Multi-room: per stay row, add-ons with quantity > 0 and formatted line totals. */
  const perStayRoomAddonSummaryLines = useMemo(() => {
    return stayRooms.map((sr, i) => {
      const br = perStayRoomBrowseBreakdown[i];
      const moneyById = addonSellFormattedByUpsellIdForStayRoom(
        sr,
        sr.addonUpsellCounts,
        br?.mewsPrice,
        enhanceStayUpsells,
        hotels?.Products,
        displayCurrency,
        chargedNights,
        locale,
      );
      return enhanceStayUpsells
        .map((u) => {
          const count = sr.addonUpsellCounts[u.id] ?? 0;
          if (count <= 0) return null;
          return {
            id: u.id,
            name: getLocalizedUpsellCopy(u.id, { name: u.name, description: u.description }, b).name,
            count,
            sellFormatted: moneyById[u.id] ?? "—",
          };
        })
        .filter((x): x is MultiRoomStayAddonLine => x != null);
    });
  }, [
    stayRooms,
    perStayRoomBrowseBreakdown,
    enhanceStayUpsells,
    hotels?.Products,
    displayCurrency,
    chargedNights,
    locale,
    b,
  ]);

  const PriceBreakdownContent = () => {
    const p = b.policies;
    const showCharges = Boolean(selectedRoom && selectedRatePlan && allStayRoomsPlanned);
    const grandTotal = aggregatedBrowseRoomTotal + addonTotal;
    const anySignatureRate = stayRooms.some((sr) => sr.selectedRatePlanId === "signature");
    const signatureCancellationLine =
      anySignatureRate && checkIn ? formatSignatureCancellationSummaryLine(checkIn, b.signature, locale) : null;

    if (stayRooms.length > 1 && showCharges) {
      return (
        <div className="space-y-8">
          <div className="space-y-2">
            <div className="font-semibold text-neutral-900">{p.charges}</div>
            <div className="mt-3 space-y-4 text-sm">
              {perStayRoomBrowseBreakdown.map((row, i) => {
                const apiRow =
                  currentStep === "details" &&
                  finalPriceData &&
                  finalPriceData.length === stayRooms.length
                    ? finalPriceData[i]
                    : undefined;
                const lineMoney = resolvePerStaySummaryLineMoney(
                  perStayRoomDisplayMoneyLines[i],
                  apiRow,
                  {
                    roomTotal: row.roomTotal,
                    browse: row.browse,
                    addonSell: row.addonSell,
                    addonList: row.addonList,
                  },
                  {
                    displayCurrency,
                    locale,
                    products: hotels?.Products,
                    enhanceStayUpsells,
                    addonUpsellCounts: stayRooms[i]?.addonUpsellCounts ?? {},
                    occupancy: {
                      adults: stayRooms[i]?.adults ?? 1,
                      children: stayRooms[i]?.children ?? 0,
                    },
                    chargedNights,
                    ratePlanId: stayRooms[i]?.selectedRatePlanId ?? "standard",
                  },
                );
                return (
                  <div
                    key={row.stayRoomId}
                    className="space-y-2 rounded-lg border border-gray-100 bg-pine-green/[0.02] p-3"
                  >
                    <div className="font-medium text-neutral-900">{row.label}</div>
                    {row.localized ? (
                      <div className="text-xs text-neutral-600">
                        <RoomNameDisplay variant="compact" displayName={row.localized.displayName} />
                      </div>
                    ) : null}
                    <div className="text-xs text-neutral-600">
                      {row.ratePlan ? (
                        <>
                          {p.ratesPerNight}{" "}
                          <span className="font-semibold text-pine-green">
                            {row.browse?.nightlyFormatted ?? formatCHF(row.pricePerNight)}
                          </span>{" "}
                          • {chargedNights} {chargedNights === 1 ? p.night : p.nights} · {row.ratePlan.name}
                        </>
                      ) : (
                        p.selectRoomRate
                      )}
                    </div>
                    <div className="flex items-center justify-between border-t border-pine-green/10 pt-2">
                      <span className="text-neutral-600">{p.roomTotal}</span>
                      <MultiStayRoomPriceLine
                        compareAt={lineMoney.room.compareAt}
                        effective={lineMoney.room.effective}
                        effectiveClassName="font-semibold tabular-nums text-pine-green"
                      />
                    </div>
                    <MultiRoomAddonsSummaryRow
                      lineMoney={lineMoney}
                      addonLines={perStayRoomAddonSummaryLines[i] ?? []}
                      expanded={Boolean(multiRoomAddonDetailsExpandedByStayId[row.stayRoomId])}
                      onToggle={() =>
                        setMultiRoomAddonDetailsExpandedByStayId((prev) => ({
                          ...prev,
                          [row.stayRoomId]: !prev[row.stayRoomId],
                        }))
                      }
                      label={p.addOns}
                      toggleAriaLabel={b.summary.toggleMultiRoomAddonsAria}
                      rowClassName="text-sm text-neutral-600"
                      effectiveClassName="font-semibold tabular-nums text-pine-green"
                    />
                  </div>
                );
              })}
              <div className="h-px bg-gray-200" />
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">{p.total}</span>
                <span className="font-semibold text-pine-green">{formatCHF(grandTotal)}</span>
              </div>
              <div className="text-xs text-neutral-500">{p.includingAllTaxes}</div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <div className="font-semibold text-neutral-900">{p.guaranteeTitle}</div>
              <div className="text-sm text-neutral-600">{p.guaranteeBody}</div>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-neutral-900">{p.cancellationTitle}</div>
              <div className="text-sm text-neutral-600">
                {signatureCancellationLine ? (
                  <>
                    <span className="font-medium text-neutral-700">{p.brsSignaturePrefix}</span>
                    {signatureCancellationLine}
                  </>
                ) : anySignatureRate ? (
                  b.signature.summarySelectDates
                ) : (
                  b.signature.summaryChooseSignature
                )}
              </div>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-neutral-900">{p.checkInTitle}</div>
              <div className="text-sm text-neutral-600">{p.checkInBody}</div>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-neutral-900">{p.childrenTitle}</div>
              <div className="text-sm text-neutral-600">{p.childrenBody}</div>
            </div>
            <div className="space-y-1">
              <div className="font-semibold text-neutral-900">{p.petsTitle}</div>
              <div className="text-sm text-neutral-600">{p.petsBody}</div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        <div className="space-y-2">
          <div className="font-semibold text-neutral-900">{p.charges}</div>
          <div className="text-sm text-neutral-600">
            {showCharges ? (
              <>
                {p.ratesPerNight}{" "}
                <span className="font-semibold text-pine-green">
                  {selectedRateBrowse?.nightlyFormatted ?? formatCHF(roomPricePerNight)}
                </span>{" "}
                •{" "}
                {chargedNights}{" "}
                {chargedNights === 1 ? p.night : p.nights}
              </>
            ) : (
              p.selectRoomRate
            )}
          </div>

          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">{p.roomTotal}</span>
              <span className="font-semibold text-pine-green">
                {showCharges ? selectedRateBrowse?.totalFormatted ?? formatCHF(roomTotal) : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">{p.addOns}</span>
              <span className="font-semibold text-pine-green">{showCharges ? formatCHF(addonTotal) : "—"}</span>
            </div>
            <div className="h-px bg-gray-200 my-2" />
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">{p.total}</span>
              <span className="font-semibold text-pine-green">{showCharges ? formatCHF(grandTotal) : "—"}</span>
            </div>
            <div className="text-xs text-neutral-500">{p.includingAllTaxes}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-1">
            <div className="font-semibold text-neutral-900">{p.guaranteeTitle}</div>
            <div className="text-sm text-neutral-600">{p.guaranteeBody}</div>
          </div>
          <div className="space-y-1">
            <div className="font-semibold text-neutral-900">{p.cancellationTitle}</div>
            <div className="text-sm text-neutral-600">
              {signatureCancellationLine ? (
                <>
                  <span className="font-medium text-neutral-700">{p.brsSignaturePrefix}</span>
                  {signatureCancellationLine}
                </>
              ) : selectedRatePlanId === "signature" ? (
                b.signature.summarySelectDates
              ) : (
                b.signature.summaryChooseSignature
              )}
            </div>
          </div>
          <div className="space-y-1">
            <div className="font-semibold text-neutral-900">{p.checkInTitle}</div>
            <div className="text-sm text-neutral-600">{p.checkInBody}</div>
          </div>
          <div className="space-y-1">
            <div className="font-semibold text-neutral-900">{p.childrenTitle}</div>
            <div className="text-sm text-neutral-600">{p.childrenBody}</div>
          </div>
          <div className="space-y-1">
            <div className="font-semibold text-neutral-900">{p.petsTitle}</div>
            <div className="text-sm text-neutral-600">{p.petsBody}</div>
          </div>
        </div>
      </div>
    );
  };

  // Nav items for booking bar hamburger menu (same as NewHeader)
  const bookingBarNavItems = [
    { name: t("nav.rooms"), link: "/accommodations" },
    { name: t("nav.wellness"), link: "/spa" },
    { name: t("nav.dining"), link: "/dining" },
    { name: t("nav.experiences"), link: "/local-attractions" },
    { name: t("nav.activities"), link: "/activities" },
    { name: t("nav.events"), link: "/events" },
  ];

  const scrollToTop = () => {
    mainRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const renderBookingBarDateTrigger = () => (
    <button
      type="button"
      data-testid="book-bar-date-guest-trigger"
      className="flex min-w-0 max-md:min-h-9 items-center text-left text-sm transition-colors hover:bg-gray-50 border-r border-gray-200 lg:min-w-[37rem]"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 px-3 py-0.5 sm:px-4 md:py-2">
        <CalendarIcon className="hidden h-4 w-4 shrink-0 text-neutral-600 md:block" aria-hidden />
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0 font-medium leading-tight text-neutral-900 text-xs book:flex-nowrap book:text-sm book:leading-normal tabular-nums">
          <span className="whitespace-nowrap">
            {checkIn ? (
              <>
                <span className="hidden md:inline">
                  {format(new Date(checkIn), "EEEE, MMMM d, yyyy")}
                </span>
                <span className="inline md:hidden">{format(new Date(checkIn), "MMMM d")}</span>
              </>
            ) : (
              b.bar.selectDate
            )}
          </span>
          <span className="shrink-0 font-normal text-neutral-400" aria-hidden>
            -
          </span>
          <span className="whitespace-nowrap">
            {checkOut ? (
              <>
                <span className="hidden md:inline">
                  {format(new Date(checkOut), "EEEE, MMMM d, yyyy")}
                </span>
                <span className="inline md:hidden">{format(new Date(checkOut), "MMMM d")}</span>
              </>
            ) : (
              b.bar.selectDate
            )}
          </span>
        </div>
      </div>

      <div className="h-9 w-px shrink-0 bg-gray-200 md:h-8" aria-hidden />
      <div className="flex min-w-0 items-center px-3 py-0.5 sm:px-4 md:py-2">
        <span className="font-medium text-neutral-900 whitespace-nowrap text-xs book:text-sm">
          {occupancy} {occupancy === 1 ? b.bar.guest : b.bar.guests},{" "}
          {stayRooms.length} {stayRooms.length === 1 ? b.bar.room : b.bar.rooms}
        </span>
      </div>

      <ChevronDown className="h-4 w-4 shrink-0 text-neutral-500 mr-2" />
    </button>
  );

  const guestCounterAria =
    language === "de"
      ? { fewer: "Weniger", more: "Mehr" }
      : language === "fr"
        ? { fewer: "Moins", more: "Plus" }
        : { fewer: "Decrease", more: "Increase" };

  /** Stay summary: show night count + moon icon; screen readers get localized night/nights. */
  const displaySummaryNights =
    useConfirmationRailSummary && confirmationRailSummary
      ? confirmationRailSummary.totalNights
      : chargedNights;
  const displaySummaryTotalGuests =
    useConfirmationRailSummary && confirmationRailSummary
      ? confirmationRailSummary.totalGuests
      : totalGuestsAcrossRooms;

  const renderStaySummaryNightCount = (moonClassName: string) => (
    <span className="inline-flex items-center gap-0.5">
      <span className="tabular-nums">{displaySummaryNights}</span>
      <IoMoon className={cn("shrink-0 text-neutral-500", moonClassName)} aria-hidden />
      <span className="sr-only">
        {" "}
        {displaySummaryNights === 1 ? b.policies.night : b.policies.nights}
      </span>
    </span>
  );

  const summaryActiveStayGuestTotal =
    (contextStayRoom?.adults ?? 0) + (contextStayRoom?.children ?? 0);

  /** Collapsed mobile strip: guests for the active stay row only (matches Plan your stay context). */
  const renderStaySummaryGuestCount = (peopleClassName: string) => (
    <span className="inline-flex items-center gap-0.5">
      <span className="tabular-nums">{summaryActiveStayGuestTotal}</span>
      <IoPeople className={cn("shrink-0 text-neutral-400", peopleClassName)} aria-hidden />
      {contextStayRoom ? (
        <span className="sr-only">
          {" "}
          {formatStayRoomOccupancySummary(contextStayRoom, b)}
        </span>
      ) : null}
    </span>
  );

  /** Desktop + expanded mobile: total guests across all stays (next to combined total price). */
  const renderStaySummaryTotalGuestCount = (peopleClassName: string) => (
    <span className="inline-flex items-center gap-0.5">
      <span className="tabular-nums">{displaySummaryTotalGuests}</span>
      <IoPeople className={cn("shrink-0 text-neutral-400", peopleClassName)} aria-hidden />
      <span className="sr-only">
        {" "}
        {displaySummaryTotalGuests === 1 ? b.bar.guest : b.bar.guests}
      </span>
    </span>
  );

  const stayRowGuestSteppersStepBtnClass =
    "flex h-11 w-9 shrink-0 items-center justify-center text-neutral-500 transition-colors hover:text-pine-green disabled:pointer-events-none disabled:opacity-25 book:h-9 book:w-8";

  const renderStayRowGuestSteppers = (rowIndex: number) => {
    const row = stayRooms[rowIndex];
    if (!row) return null;
    const maxAdults = Math.min(
      MAX_ADULTS_PER_STAY_ROW,
      MAX_GUESTS_PER_STAY_ROW - row.children,
    );
    const maxChildren = Math.min(
      MAX_CHILDREN_PER_STAY_ROW,
      MAX_GUESTS_PER_STAY_ROW - row.adults,
    );
    const adultLabel =
      row.adults === 1 ? b.bar.guestCounterAdultSingular : b.bar.guestCounterAdultPlural;
    const childLabel =
      row.children <= 1 ? b.bar.guestCounterChildSingular : b.bar.guestCounterChildPlural;
    return (
      <div className="flex min-w-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-neutral-100 book:rounded-md book:border-gray-200/90">
        <div className="flex min-w-0 flex-1 items-center gap-1 px-1.5 py-2.5 sm:gap-2 sm:px-3 book:gap-1 book:px-2 book:py-1.5">
          <button
            type="button"
            className={stayRowGuestSteppersStepBtnClass}
            aria-label={`${guestCounterAria.fewer} ${b.bar.adults}`}
            disabled={row.adults <= 1}
            onClick={() =>
              patchStayRoomAt(rowIndex, { adults: Math.max(1, row.adults - 1) })
            }
          >
            <Minus className="h-5 w-5 book:h-4 book:w-4" strokeWidth={1.5} aria-hidden />
          </button>
          <div className="min-w-0 flex-1 text-center font-lato">
            <span className="text-sm font-medium text-neutral-800 book:text-[13px]">
              {row.adults} {adultLabel}
            </span>
          </div>
          <button
            type="button"
            className={stayRowGuestSteppersStepBtnClass}
            aria-label={`${guestCounterAria.more} ${b.bar.adults}`}
            disabled={row.adults >= maxAdults}
            onClick={() =>
              patchStayRoomAt(rowIndex, { adults: clamp(row.adults + 1, 1, maxAdults) })
            }
          >
            <Plus className="h-5 w-5 book:h-4 book:w-4" strokeWidth={1.5} aria-hidden />
          </button>
        </div>

        <div className="w-px shrink-0 self-stretch bg-gray-300 book:bg-gray-200" aria-hidden />

        <div className="flex min-w-0 flex-1 items-center gap-1 px-1.5 py-2.5 sm:gap-2 sm:px-3 book:gap-1 book:px-2 book:py-1.5">
          <button
            type="button"
            className={stayRowGuestSteppersStepBtnClass}
            aria-label={`${guestCounterAria.fewer} ${b.bar.children}`}
            disabled={row.children <= 0}
            onClick={() =>
              patchStayRoomAt(rowIndex, { children: Math.max(0, row.children - 1) })
            }
          >
            <Minus className="h-5 w-5 book:h-4 book:w-4" strokeWidth={1.5} aria-hidden />
          </button>
          <div className="min-w-0 flex-1 text-center font-lato">
            <div className="text-sm font-medium leading-tight text-neutral-800 book:text-[13px]">
              {row.children} {childLabel}
            </div>
            <div className="mt-0.5 text-[11px] leading-tight text-neutral-500 book:mt-0 book:text-[10px]">
              {b.bar.guestCounterChildAgeHint}
            </div>
          </div>
          <button
            type="button"
            className={stayRowGuestSteppersStepBtnClass}
            aria-label={`${guestCounterAria.more} ${b.bar.children}`}
            disabled={row.children >= maxChildren}
            onClick={() =>
              patchStayRoomAt(rowIndex, {
                children: clamp(row.children + 1, 0, maxChildren),
              })
            }
          >
            <Plus className="h-5 w-5 book:h-4 book:w-4" strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      </div>
    );
  };

  const renderGuestCounterBar = (opts?: { placement?: "bar" | "criteriaOverlay" | "minimal" }) => {
    const placement = opts?.placement ?? "minimal";
    const usePillAddRoom = placement === "bar" || placement === "criteriaOverlay";
    const showSplitGuestHint =
      hasOverTwoGuestsInSomeStayRow &&
      (placement === "criteriaOverlay" ||
        (placement === "bar" && barSplitGuestHintRemaining > 0));

    return (
      <div className="space-y-3 book:space-y-2.5">
        {stayRooms.map((row, rowIndex) => {
          return (
            <div key={row.id} className="space-y-2 book:space-y-1.5">
              <div className="px-0.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-neutral-700 book:text-[11px]">
                  {row.label}
                </span>
              </div>
              <div className="flex w-full min-w-0 items-center gap-1.5 sm:gap-2">
                {renderStayRowGuestSteppers(rowIndex)}
                {rowIndex > 0 ? (
                  <button
                    type="button"
                    className="inline-flex h-11 w-9 shrink-0 items-center justify-center self-center rounded-md text-rose-950/50 transition-colors hover:bg-rose-950/[0.06] hover:text-rose-950/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-rose-400/50 book:h-9 book:w-8"
                    aria-label={b.bar.removeRoom}
                    onClick={() => removeStayRoom(rowIndex)}
                  >
                    <MdOutlineDeleteForever
                      className="h-[1.125rem] w-[1.125rem] book:h-4 book:w-4"
                      aria-hidden
                    />
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}

        {stayRooms.length < MAX_STAY_ROOMS ? (
          <div className="flex w-full max-w-full flex-col items-center gap-2">
            <div className="flex justify-center">
              <button
                type="button"
                className={cn(
                  "text-sm font-medium text-neutral-900 transition-colors",
                  usePillAddRoom
                    ? "rounded-full border border-neutral-200 bg-white px-4 py-2 shadow-sm hover:border-alpine-gold/50 hover:bg-[#faf8f5]"
                    : "underline-offset-4 hover:underline",
                )}
                onClick={addStayRoom}
              >
                {b.bar.addRoom}
              </button>
            </div>
            {showSplitGuestHint ? (
              <p
                className="w-full max-w-full text-balance text-center text-[11px] leading-snug text-neutral-600"
                data-testid={
                  placement === "criteriaOverlay"
                    ? "book-overlay-split-guest-hint"
                    : "book-bar-split-guest-hint"
                }
              >
                {b.splitOccupancy.barSplitGuestHintCompact}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="rounded-lg border border-pine-green/15 bg-pine-green/[0.04] px-3 py-2 text-sm text-neutral-800">
            <p className="font-medium text-neutral-900">{b.bar.maxRoomsTitle}</p>
            <p
              className="mt-1 text-sm leading-snug text-neutral-700 [&_a]:text-neutral-900 [&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2"
              dangerouslySetInnerHTML={{ __html: b.bar.maxRoomsContactHtml }}
            />
          </div>
        )}
      </div>
    );
  };

  /** Multi-room plan / enhance: guest summary + chevron opens adults/children steppers (sheet on narrow bar width, popover otherwise). */
  const renderMultiStayRoomOccupancyPicker = (rowIndex: number, stayRoom: StayRoom) => {
    const occupancyLabel = formatStayRoomOccupancySummary(stayRoom, b);
    const aria = b.steps.editStayRoomOccupancyAria.replace(/\{\{label\}\}/g, stayRoom.label);
    const title = b.steps.editStayRoomOccupancyTitle.replace(/\{\{label\}\}/g, stayRoom.label);
    const trigger = (
      <button
        type="button"
        aria-expanded={multiStepOccupancyOpen}
        aria-haspopup="dialog"
        aria-label={aria}
        data-testid="book-multi-step-room-occupancy-trigger"
        className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md py-0.5 pl-0.5 pr-0 text-left font-sans font-medium tabular-nums tracking-tight text-neutral-600 transition-colors hover:bg-pine-green/[0.06] hover:text-neutral-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-pine-green/40"
      >
        <IoPeople
          className="h-3.5 w-3.5 shrink-0 text-neutral-400 sm:h-4 sm:w-4"
          aria-hidden
        />
        <span className="min-w-0">{occupancyLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-500 sm:h-4 sm:w-4" aria-hidden />
      </button>
    );

    return narrowBookBar ? (
      <Dialog open={multiStepOccupancyOpen} onOpenChange={setMultiStepOccupancyOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent
          overlayClassName="bg-black/15 backdrop-blur-md"
          className={cn(
            "gap-0 overflow-hidden border border-gray-200 bg-white p-0 shadow-xl sm:rounded-xl",
            "w-[calc(100vw-1.5rem)] max-w-md max-h-[min(calc(100dvh-8px),56rem)]",
          )}
        >
          <DialogHeader className="border-b border-gray-100 px-4 py-3 text-left">
            <DialogTitle className="text-base font-semibold text-neutral-900">{title}</DialogTitle>
          </DialogHeader>
          <div className="px-3 py-3">{renderStayRowGuestSteppers(rowIndex)}</div>
        </DialogContent>
      </Dialog>
    ) : (
      <Popover open={multiStepOccupancyOpen} onOpenChange={setMultiStepOccupancyOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="w-auto border-gray-200 p-3 shadow-lg" align="start" sideOffset={6}>
          {renderStayRowGuestSteppers(rowIndex)}
        </PopoverContent>
      </Popover>
    );
  };

  /** Full width of bar/overlay footer so long strings (DE/FR) are not clipped; allow wrap on very narrow viewports (Button base uses whitespace-nowrap). */
  const checkAvailabilityButtonClassName =
    "w-full max-w-full min-h-11 whitespace-normal rounded-full px-4 py-2.5 text-center leading-snug hover:!scale-100 hover:!shadow-lg sm:px-6 disabled:opacity-40 disabled:saturate-0 disabled:cursor-not-allowed";

  const overlayDatesReady = Boolean(overlayFirstClick && overlaySecondClick);

  const renderOccupancySplitErrorBanner = (wrapperClass: string) => {
    if (criteriaOccupancySplitError == null) return null;
    const kind = criteriaOccupancySplitError;
    return (
      <div className={wrapperClass}>
        <Alert
          variant="destructive"
          className="border-amber-200/90 bg-amber-50 text-neutral-900 shadow-none"
        >
          <AlertTitle className="text-neutral-900">
            {kind === "max_rooms"
              ? b.bar.maxRoomsTitle
              : b.splitOccupancy.splitInsufficientAdultsTitle}
          </AlertTitle>
          <AlertDescription className="mt-2 space-y-2 text-neutral-800">
            {kind === "max_rooms" ? (
              <>
                <p>{b.splitOccupancy.splitOverflowInlineLead}</p>
                <p
                  className="text-sm leading-snug [&_a]:font-medium [&_a]:text-neutral-900 [&_a]:underline [&_a]:underline-offset-2"
                  dangerouslySetInnerHTML={{ __html: b.bar.maxRoomsContactHtml }}
                />
              </>
            ) : (
              <p>
                {b.splitOccupancy.splitBlockedInsufficientAdults.replace(
                  /\{\{addRoom\}\}/g,
                  b.bar.addRoom,
                )}
              </p>
            )}
          </AlertDescription>
        </Alert>
      </div>
    );
  };

  const renderBookingBarDatesPickerBody = () => (
    <div className="flex min-h-0 max-h-full flex-1 flex-col overflow-hidden">
      <div className="shrink-0">
        <Calendar
          mode="range"
          month={barPickerDisplayedMonth}
          onMonthChange={setBarPickerDisplayedMonth}
          selected={
            checkIn && checkOut
              ? { from: new Date(checkIn), to: new Date(checkOut) }
              : checkIn
                ? { from: new Date(checkIn), to: undefined }
                : undefined
          }
          onSelect={handleDatesSelect}
          disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
          numberOfMonths={isMobile ? 1 : 2}
          className="rounded-lg border-0 p-3 mx-auto"
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col border-t border-gray-200">
        <div
          ref={datePickerGuestsScrollRef}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain px-3 pt-2 pb-2"
        >
          <p className="text-center text-[10px] leading-snug text-neutral-600 px-1">
            {b.bar.organicBedding}
          </p>
          {renderGuestCounterBar({ placement: "bar" })}
        </div>
        {renderOccupancySplitErrorBanner("shrink-0 border-t border-gray-100 bg-white px-3 pt-3")}
        <div className="flex shrink-0 justify-center border-t border-gray-100 bg-white px-3 py-3">
          <Button
            variant="hero"
            className={checkAvailabilityButtonClassName}
            data-testid="book-bar-check-availability"
            disabled={!criteriaConfirmed || stayDatesIncomplete}
            title={
              !criteriaConfirmed
                ? b.splitOccupancy.barApplyRequiresOverlayFirst
                : stayDatesIncomplete
                  ? b.confirmation.paymentAwaitingDatesTitle
                  : undefined
            }
            onClick={() => confirmCriteria({ applySource: "bar" })}
          >
            {b.bar.checkAvailability}
          </Button>
        </div>
      </div>
    </div>
  );

  /** Hide the fixed booking header on narrow viewports while the date sheet or initial criteria overlay is active (avoids duplicate chrome above the picker). */
  const hideBookingBarOnNarrowDateFlow =
    narrowBookBar &&
    (datesPopoverOpen || (currentStep === "rooms" && !criteriaConfirmed));

  return (
    <div className="relative min-h-screen" data-book-display-currency={displayCurrency}>
      <Dialog open={leaveGuardOpen} onOpenChange={onLeaveGuardOpenChange}>
        <DialogContent
          overlayClassName="z-[100] bg-black/40 backdrop-blur-sm"
          className={cn(
            "z-[100] max-w-md gap-6 border-pine-green/20 bg-[#fdfbf7] p-6 shadow-xl sm:rounded-xl",
            "[&>button.absolute]:hidden",
          )}
          onPointerDownOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleLeaveGuardStay();
          }}
        >
          <DialogHeader className="space-y-4 text-center sm:text-center">
            <DialogTitle className="font-playfair text-base font-normal uppercase leading-snug tracking-[0.08em] text-neutral-900 sm:text-lg">
              {b.leaveGuard.warning}
            </DialogTitle>
            <DialogDescription className="font-playfair text-sm leading-relaxed text-neutral-600">
              {b.leaveGuard.question}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              variant="hero"
              className="order-1 flex-1 rounded-md sm:order-none"
              onClick={handleLeaveGuardStay}
            >
              {b.leaveGuard.stay}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="order-2 flex-1 rounded-md border-alpine-gold text-alpine-gold hover:bg-alpine-gold/10 sm:order-none"
              onClick={handleLeaveGuardLeave}
            >
              {b.leaveGuard.leave}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-page background: mob/tab = top half 60% white, bottom half 60%→90% (stronger near bottom); book/desktop = radial 60%→90% */}
      <div className="fixed inset-0 z-0 isolate">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/images/booking-page/Con2.webp"
            alt=""
            className="absolute inset-0 w-full h-full [&>img]:object-cover [&>img]:object-top [&>img]:min-h-full"
            priority
            hideLoadingSpinner
          />
        </div>
        <div
          className={cn(
            "absolute inset-0 z-[1] pointer-events-none",
            "bg-[linear-gradient(to_bottom,rgba(255,255,255,0.6)_0%,rgba(255,255,255,0.6)_50%,rgba(255,255,255,0.9)_100%)]",
            "book:bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.6)_0%,rgba(255,255,255,0.9)_100%)]",
          )}
          aria-hidden
        />
      </div>

      {/* Floating booking bar: Logo | Dates | Guests | Language | Hamburger */}
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-50",
          hideBookingBarOnNarrowDateFlow && "hidden",
        )}
      >
        <header className="flex h-14 items-center gap-3 sm:gap-4 border-b border-gray-200 bg-white px-2 sm:px-4 shadow-sm">
        <div className="hidden book:block">
          <NavbarLogo />
        </div>
        <div className="h-6 w-px bg-gray-200 shrink-0 hidden book:block" />

        {/* book: matches logo — one centered bar layout from 940px up (Book page only) */}
        <div className="hidden book:block book:flex-1 book:min-w-0" />

        {/* Dates + occupancy in one box (Taj-style), centred */}
        <div className="mx-auto my-0 flex max-w-full items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:my-1 book:max-w-[720px] book:shrink-0">
          {narrowBookBar ? (
            <Dialog open={datesPopoverOpen} onOpenChange={setDatesPopoverOpen}>
              <DialogTrigger asChild>{renderBookingBarDateTrigger()}</DialogTrigger>
              <DialogContent
                overlayClassName="bg-black/15 backdrop-blur-md"
                className={cn(
                  "flex w-[calc(100vw-1.5rem)] max-w-md flex-col gap-0 overflow-hidden border border-gray-200 bg-white p-0 shadow-xl",
                  /** Centered sheet: leave ~4px to top and bottom of viewport; allow a bit more height than old 44rem cap */
                  "max-h-[min(calc(100dvh-8px),56rem)] sm:rounded-xl",
                )}
              >
                {renderBookingBarDatesPickerBody()}
              </DialogContent>
            </Dialog>
          ) : (
            <Popover open={datesPopoverOpen} onOpenChange={setDatesPopoverOpen}>
              <PopoverTrigger asChild>{renderBookingBarDateTrigger()}</PopoverTrigger>
              <PopoverContent
                className={cn(
                  "flex w-auto flex-col overflow-hidden p-0",
                  /** Radix exposes space to the anchored edge; reserve 4px to viewport; slightly taller cap than 44rem */
                  "max-h-[min(calc(var(--radix-popover-content-available-height,100dvh)-4px),56rem)]",
                )}
                align="center"
                sideOffset={8}
              >
                {renderBookingBarDatesPickerBody()}
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="hidden book:block book:flex-1 book:min-w-0" />

        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2 book:ml-0">
          <div className="hidden md:flex md:items-center">
            <LanguageSelector lightBackground />
          </div>
          <div className="hidden h-6 w-px shrink-0 bg-gray-200 md:block" aria-hidden />
          <div className="flex items-center gap-1">
            {showCurrencyPicker ? (
              <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                <SelectTrigger
                  className="h-9 min-w-[3.25rem] max-w-[8.5rem] shrink-0 border-gray-200 bg-white px-1.5 sm:min-w-[6.75rem] sm:px-2 text-xs font-semibold text-gray-800 shadow-sm"
                  aria-label={t("nav.displayCurrency")}
                >
                  <span className="flex min-w-0 flex-1 items-center">
                    <span className="truncate font-mono tabular-nums md:hidden">{displayCurrency}</span>
                    <span className="hidden min-w-0 md:inline md:truncate">
                      <SelectValue />
                    </span>
                  </span>
                </SelectTrigger>
                <SelectContent align="end" className="max-h-[min(70vh,22rem)]">
                  {selectorOptions.map((code) => (
                    <SelectItem key={code} value={code} className="text-xs">
                      <CurrencyCodeWithFlag code={code} compact />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span
                className="flex h-9 min-w-[3.25rem] items-center justify-center rounded-md border border-gray-200 bg-white px-1.5 sm:min-w-[6.75rem] sm:px-2 text-xs font-semibold text-gray-800 shadow-sm"
                aria-label={`${t("nav.displayCurrency")} CHF`}
              >
                <CurrencyCodeWithFlag code="CHF" compact hideFlagBelowMd />
              </span>
            )}
          </div>
        </div>

        <div className="h-6 w-px shrink-0 bg-gray-200" />
        <MobileNavToggle
          isOpen={bookingBarMenuOpen}
          onClick={() => setBookingBarMenuOpen(!bookingBarMenuOpen)}
        />
        </header>

        {/* Hamburger menu overlay */}
        <MobileNavMenu
        isOpen={bookingBarMenuOpen}
        onClose={() => setBookingBarMenuOpen(false)}
        isBookingPage
      >
        {bookingBarNavItems.map((item, idx) => (
          <a
            key={`booking-bar-nav-${idx}`}
            href={getLocalizedUrl(item.link, language)}
            onClick={() => setBookingBarMenuOpen(false)}
            className="relative text-neutral-600 dark:text-neutral-300"
          >
            {item.name}
          </a>
        ))}
        <div className="w-full border-t border-gray-200 pt-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t("nav.language")}</span>
            <Globe size={16} className="text-gray-500" />
          </div>
          <div className="flex space-x-3">
            {[
              { code: "en", label: "EN", name: "English" },
              { code: "de", label: "DE", name: "Deutsch" },
              { code: "fr", label: "FR", name: "Français" },
            ].map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code as Language);
                  setBookingBarMenuOpen(false);
                }}
                className={cn(
                  "flex-1 px-4 py-2 rounded-lg text-sm font-lato transition-colors duration-200",
                  language === lang.code
                    ? "bg-alpine-gold text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                )}
              >
                <div className="font-semibold">{lang.label}</div>
                <div className="text-xs opacity-75">{lang.name}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="w-full border-t border-gray-200 pt-4 mt-4">
          {showCurrencyPicker ? (
            <Select
              value={displayCurrency}
              onValueChange={(code) => {
                setDisplayCurrency(code);
                setBookingBarMenuOpen(false);
              }}
            >
              <SelectTrigger
                className="h-10 w-full border-gray-200 bg-white font-mono text-sm font-semibold"
                aria-label={t("nav.displayCurrency")}
              >
                <span className="flex min-w-0 flex-1 items-center">
                  <span className="truncate md:hidden">{displayCurrency}</span>
                  <span className="hidden min-w-0 md:inline md:truncate">
                    <SelectValue />
                  </span>
                </span>
              </SelectTrigger>
              <SelectContent className="max-h-[min(70vh,22rem)]">
                {selectorOptions.map((code) => (
                  <SelectItem key={code} value={code} className="text-sm">
                    <CurrencyCodeWithFlag code={code} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex justify-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-neutral-700 dark:bg-neutral-800">
              <CurrencyCodeWithFlag
                code="CHF"
                className="text-sm font-semibold text-gray-800 dark:text-gray-100"
                hideFlagBelowMd
              />
            </div>
          )}
        </div>
        </MobileNavMenu>
      </div>

      {/* Floating contact — opens concierge modal; mobile: under booking bar; desktop (book:): 255px from top */}
      <Button
        type="button"
        variant="hero"
        size="icon"
        trackingType="contact_modal"
        trackingLabel="Contact Concierge"
        trackingLocation="book_page_floating_bubble"
        trackingData={{ modalType: "concierge" }}
        className={cn(
          "fixed right-4 z-[45] h-14 w-14 shrink-0 rounded-full shadow-lg ring-2 ring-white/90 hover:scale-105",
          hideBookingBarOnNarrowDateFlow
            ? "top-4"
            : "top-[calc(3.5rem+0.5rem)]",
          "book:top-[255px]",
        )}
        aria-label={t("contactModals.concierge.title")}
        onClick={() => setBookContactConciergeOpen(true)}
      >
        <MessageCircle className="h-6 w-6" aria-hidden />
      </Button>

      {/* Criteria overlay: calendar + guest picker in one card with blurred bg when no criteria confirmed */}
      {currentStep === "rooms" && !criteriaConfirmed && (
        <div
          className={cn(
            "fixed inset-0 z-40 flex items-center justify-center px-4 pb-6 sm:px-6 sm:pb-6",
            /** Bar is hidden on narrow while this overlay is open; otherwise keep ≥4px under fixed h-14 bar */
            hideBookingBarOnNarrowDateFlow
              ? "pt-4 sm:pt-6"
              : "pt-[calc(3.5rem+4px)]",
          )}
          aria-modal="true"
          role="dialog"
          aria-labelledby="criteria-overlay-title"
          data-testid="book-criteria-overlay"
        >
          <div className="absolute inset-0 bg-black/15 backdrop-blur-md" aria-hidden />
          <div
            className={cn(
              "relative z-10 flex w-full max-w-2xl flex-col gap-1 overflow-hidden rounded-2xl border border-white/80 bg-white/95 shadow-2xl",
              hideBookingBarOnNarrowDateFlow
                ? /** Taller card; ≥24px clearance from viewport bottom (pb-6) */
                  "max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2.5rem)]"
                : /** Match pt (h-14 + 4px) + pb-6 so a tall card cannot slide under the bar */
                  "max-h-[calc(100dvh-5rem-4px)]",
            )}
          >
            <div className="shrink-0 border-b border-gray-100 bg-white/95 px-4 pb-4 pt-[max(0.25rem,1rem)] sm:px-6 sm:pb-6 sm:pt-[max(0.25rem,1.5rem)]">
              <h2 id="criteria-overlay-title" className="font-playfair text-xl sm:text-2xl text-neutral-900">
                {b.criteriaOverlay.title}
              </h2>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <div
                ref={criteriaOverlayGuestsScrollRef}
                className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-y-contain px-4 pb-4 pt-0 sm:px-6 sm:pb-4 sm:pt-0"
              >
                <div>
                  <Calendar
                    mode="range"
                    month={criteriaOverlayPickerMonth}
                    onMonthChange={setCriteriaOverlayPickerMonth}
                    selected={
                      overlayFirstClick && overlaySecondClick
                        ? {
                            from: overlayFirstClick.getTime() <= overlaySecondClick.getTime() ? overlayFirstClick : overlaySecondClick,
                            to: overlayFirstClick.getTime() <= overlaySecondClick.getTime() ? overlaySecondClick : overlayFirstClick,
                          }
                        : overlayFirstClick
                          ? { from: overlayFirstClick, to: undefined }
                          : undefined
                    }
                    onSelect={handleOverlayDatesSelect}
                    disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
                    numberOfMonths={isMobile ? 1 : 2}
                    className="rounded-lg border-0 p-0 mx-auto"
                  />
                  <p className="mt-3 px-1 text-center text-[11px] leading-snug text-neutral-600">
                    {b.bar.organicBedding}
                  </p>
                </div>
                <div>{renderGuestCounterBar({ placement: "criteriaOverlay" })}</div>
              </div>
              {renderOccupancySplitErrorBanner(
                "shrink-0 border-t border-gray-100 bg-white/95 px-4 pt-3 sm:px-6",
              )}
              <div className="flex shrink-0 justify-center border-t border-gray-100 bg-white/95 px-4 py-3 sm:px-6 sm:py-4">
                <Button
                  variant="hero"
                  className={checkAvailabilityButtonClassName}
                  data-testid="book-overlay-check-availability"
                  disabled={!overlayDatesReady}
                  title={
                    !overlayDatesReady ? b.confirmation.paymentAwaitingDatesTitle : undefined
                  }
                  onClick={() => {
                    if (!overlayFirstClick || !overlaySecondClick) return;
                    const tStart = overlayFirstClick.getTime();
                    const tEnd = overlaySecondClick.getTime();
                    const [earlier, later] =
                      tStart <= tEnd
                        ? [overlayFirstClick, overlaySecondClick]
                        : [overlaySecondClick, overlayFirstClick];
                    confirmCriteria({
                      checkIn: format(earlier, "yyyy-MM-dd"),
                      checkOut: format(later, "yyyy-MM-dd"),
                      applySource: "overlay",
                    });
                  }}
                >
                  {b.bar.checkAvailability}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extra bottom padding when mobile sticky summary is shown (after criteria confirmed) */}
      <main
        ref={mainRef}
        className={cn(
          "relative z-10 pt-20 max-book:pt-[4.5rem] overflow-visible",
          criteriaConfirmed ? "pb-24 book:pb-10" : "pb-10",
        )}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 overflow-visible">
          <div className="grid gap-8 book:grid-cols-[1fr_300px] overflow-visible">
            {/* Main */}
            <div className="min-w-0">
              <div className="pt-2 sm:pt-3 book:pt-0">
                {currentStep === "confirmation" && (
                  <a
                    href="/"
                    className="flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:underline mb-6 inline-block"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {b.confirmation.backHome}
                  </a>
                )}

                {currentStep === "confirmation" && (
                  <div className="mb-8">
                    <h2 className="font-playfair text-xl sm:text-2xl text-neutral-900 break-words">
                      {isLoadingConfirmation && isVerifyingOnSessionReturn
                        ? b.confirmation.verifyingPaymentTitle
                        : paymentVerificationOutcome?.status === "pending"
                          ? b.confirmation.paymentNotCompletedTitle
                          : paymentVerificationOutcome?.status === "failed"
                            ? paymentVerificationOutcome.reason === "expired"
                              ? b.confirmation.paymentFailedExpiredTitle
                              : paymentVerificationOutcome.reason === "missing"
                                ? b.confirmation.paymentFailedMissingTitle
                                : b.confirmation.paymentFailedCanceledTitle
                            : b.confirmation.title}
                    </h2>
                    <p className="text-neutral-600 text-sm mt-0.5">
                      {isLoadingConfirmation && isVerifyingOnSessionReturn
                        ? b.confirmation.verifyingPaymentHint
                        : paymentVerificationOutcome?.status === "pending"
                          ? b.confirmation.paymentNotCompletedBody
                          : paymentVerificationOutcome?.status === "failed"
                            ? paymentVerificationOutcome.reason === "expired"
                              ? b.confirmation.paymentFailedExpiredBody
                              : paymentVerificationOutcome.reason === "missing"
                                ? b.confirmation.paymentFailedMissingBody
                                : b.confirmation.paymentFailedCanceledBody
                            : b.confirmation.subtitle}
                    </p>
                  </div>
                )}

                {/* Mobile/tablet pill: outside space-y-8 so a hidden first child does not add mt-8 to the rooms column on desktop */}
                {currentStep === "rooms" && (
                  <div className="mb-6 flex justify-center book:hidden">
                    <BestRateGuaranteePill copy={b.bestRateGuarantee} href={brgHref} />
                  </div>
                )}

                {/* Dates & guests are in the floating bar at the top */}
                <div className="space-y-8">
                  {currentStep === "confirmation" && (
                    <div className="space-y-6">
                      {isLoadingConfirmation && (
                        <div className="flex flex-col items-center justify-center gap-6 rounded-2xl border border-gray-200/80 bg-white/95 px-8 py-12 shadow-sm">
                          <Loader2 className="h-10 w-10 animate-spin text-alpine-gold" aria-hidden />
                          <p className="font-playfair text-lg font-semibold text-neutral-900">
                            {isVerifyingOnSessionReturn
                              ? b.confirmation.verifyingPaymentTitle
                              : b.confirmation.loadingTitle}
                          </p>
                          <p className="text-sm text-neutral-600">
                            {isVerifyingOnSessionReturn
                              ? b.confirmation.verifyingPaymentHint
                              : b.confirmation.loadingHint}
                          </p>
                        </div>
                      )}
                      {confirmationLoadError && !isLoadingConfirmation && (
                        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 p-6 text-neutral-900">
                          <p className="font-medium">{confirmationLoadError}</p>
                          <p className="mt-2 text-sm text-neutral-700">{b.confirmation.contactWithNumber}</p>
                        </div>
                      )}
                      {paymentVerificationOutcome?.status === "pending" && !isLoadingConfirmation && (
                        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 p-6 text-neutral-900 space-y-4">
                          <p className="font-medium">{b.confirmation.paymentNotCompletedTitle}</p>
                          <p className="text-sm text-neutral-700">{b.confirmation.paymentNotCompletedBody}</p>
                          <div className="flex flex-wrap gap-3">
                            <Button type="button" variant="hero" className="rounded-full" onClick={handleRetryPayment}>
                              {b.confirmation.tryAgainPayment}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => setBookContactConciergeOpen(true)}
                            >
                              {b.confirmation.contactUs}
                            </Button>
                          </div>
                        </div>
                      )}
                      {paymentVerificationOutcome?.status === "failed" && !isLoadingConfirmation && (
                        <div className="rounded-2xl border border-amber-200/80 bg-amber-50/80 p-6 text-neutral-900 space-y-4">
                          <p className="font-medium">
                            {paymentVerificationOutcome.reason === "expired"
                              ? b.confirmation.paymentFailedExpiredTitle
                              : paymentVerificationOutcome.reason === "missing"
                                ? b.confirmation.paymentFailedMissingTitle
                                : b.confirmation.paymentFailedCanceledTitle}
                          </p>
                          <p className="text-sm text-neutral-700">
                            {paymentVerificationOutcome.reason === "expired"
                              ? b.confirmation.paymentFailedExpiredBody
                              : paymentVerificationOutcome.reason === "missing"
                                ? b.confirmation.paymentFailedMissingBody
                                : b.confirmation.paymentFailedCanceledBody}
                          </p>
                          <div className="flex flex-wrap gap-3">
                            <Button type="button" variant="hero" className="rounded-full" asChild>
                              <a
                                href="/book"
                                onClick={() => clearPendingOnSessionReservationGroupId()}
                              >
                                {b.confirmation.startOver}
                              </a>
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-full"
                              onClick={() => setBookContactConciergeOpen(true)}
                            >
                              {b.confirmation.contactUs}
                            </Button>
                          </div>
                        </div>
                      )}
                      {confirmationPaid && createReservationResult && (
                        <>
                          <div className="rounded-2xl border border-pine-green/20 bg-gradient-to-b from-green-50/60 to-white p-8 shadow-sm">
                            <div className="flex items-start gap-4">
                              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-alpine-gold/15">
                                <CheckCircle2 className="h-7 w-7 text-alpine-gold" aria-hidden />
                              </div>
                              <div className="min-w-0 flex-1 space-y-3">
                                <h2 className="font-playfair text-xl font-semibold text-neutral-900">
                                  {b.confirmation.confirmedTitle}
                                </h2>
                                <p className="text-neutral-700">
                                  {b.confirmation.confirmedBodyPrefix}
                                  {createReservationResult.Reservations.length === 1 ? (
                                    <>: <strong className="font-semibold text-neutral-900">{createReservationResult.Reservations[0].Number}</strong></>
                                  ) : (
                                    <>
                                      {b.confirmation.numbersPrefix}{" "}
                                      {createReservationResult.Reservations.map((r) => r.Number).join(", ")}
                                    </>
                                  )}
                                  {b.confirmation.confirmedBodySuffix}
                                </p>
                                {isOnSessionMode && confirmationPaymentReceived ? (
                                  <p className="text-sm text-neutral-700">
                                    <span className="font-semibold text-neutral-900">
                                      {b.confirmation.paymentReceived}
                                    </span>
                                    {": "}
                                    {formatBookMoney(
                                      confirmationPaymentReceived.grossValue,
                                      confirmationPaymentReceived.currency,
                                      locale,
                                    )}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {/* Stay details: rooms, nights, guests, total, additional products */}
                          {(() => {
                            const res = createReservationResult;
                            const reservations = res.Reservations ?? [];
                            const first = reservations[0];
                            if (!first) return null;
                            const nightsFromUtc = (start: string, end: string) => {
                              const a = new Date(start);
                              const b = new Date(end);
                              const diff = Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
                              return Math.max(0, Number.isFinite(diff) ? diff : 0);
                            };
                            const totalNights = reservations.reduce(
                              (sum, r) => sum + nightsFromUtc(r.StartUtc, r.EndUtc),
                              0
                            );
                            const totalAdults = reservations.reduce((s, r) => s + (r.AdultCount ?? 0), 0);
                            const totalChildren = reservations.reduce((s, r) => s + (r.ChildCount ?? 0), 0);
                            const roomDisplayName = (roomCategoryId: string) => {
                              const localId = getLocalRoomId(roomCategoryId);
                              const room = ROOM_LADDER.find((l) => l.sourceRoom.id === localId);
                              if (!room) return b.confirmation.roomFallbackName;
                              const meta = getRoomMetadata(room.sourceRoom.id);
                              return getLocalizedBookRoomDisplay(room, meta, b).displayName;
                            };
                            const roomNames = reservations.map((r) => roomDisplayName(r.RoomCategoryId));
                            const visibleFirstProducts = filterConfirmationVisibleProductIds(first.ProductIds);
                            const startDate = first.StartUtc.slice(0, 10);
                            const endDate = first.EndUtc.slice(0, 10);

                            const totalFooter =
                              res.TotalAmount?.GrossValue != null ? (
                                <div className="mt-4 pt-3 border-t border-pine-green/10">
                                  <div className="text-neutral-500 font-medium text-sm">{b.confirmation.total}</div>
                                  <div className="text-neutral-900 font-semibold mt-0.5">
                                    {res.TotalAmount.Currency ?? "CHF"} {res.TotalAmount.GrossValue.toFixed(2)}
                                    <span className="block text-xs font-normal text-neutral-500 mt-0.5">
                                      {b.policies.includingTaxesFees}
                                    </span>
                                  </div>
                                </div>
                              ) : null;

                            if (reservations.length > 1) {
                              return (
                                <div className="rounded-2xl border border-pine-green/15 bg-white p-6 shadow-sm">
                                  <h3 className="font-playfair text-lg font-semibold text-neutral-900 mb-4">
                                    {b.confirmation.stayDetails}
                                  </h3>
                                  <dl className="grid gap-3 text-sm">
                                    <div>
                                      <dt className="text-neutral-500 font-medium">{b.confirmation.datesNights}</dt>
                                      <dd className="text-neutral-900 mt-0.5">
                                        {startDate} → {endDate} · {totalNights}{" "}
                                        {totalNights === 1 ? b.policies.night : b.policies.nights}
                                      </dd>
                                    </div>
                                    <div>
                                      <dt className="text-neutral-500 font-medium">{b.confirmation.rooms}</dt>
                                      <dd className="text-neutral-900 font-semibold mt-0.5">
                                        {[...new Set(roomNames)].join(", ")}
                                      </dd>
                                    </div>
                                  </dl>
                                  <div className="mt-4 space-y-3">
                                    {reservations.map((r, idx) => {
                                      const adults = r.AdultCount ?? 0;
                                      const children = r.ChildCount ?? 0;
                                      const pids = filterConfirmationVisibleProductIds(r.ProductIds);
                                      return (
                                        <div
                                          key={`${r.Number}-${idx}`}
                                          className="rounded-xl border border-pine-green/10 bg-pine-green/[0.02] p-4 space-y-2"
                                        >
                                          <div className="font-playfair font-semibold text-neutral-900">
                                            {b.confirmation.confirmationNumberShort.replace(
                                              /\{\{number\}\}/g,
                                              r.Number,
                                            )}
                                          </div>
                                          <div className="text-sm text-neutral-900 font-medium">
                                            {roomDisplayName(r.RoomCategoryId)}
                                          </div>
                                          <div className="text-sm text-neutral-700">
                                            {adults}{" "}
                                            {adults === 1 ? b.confirmation.adult : b.confirmation.adults}
                                            {children > 0
                                              ? `, ${children} ${children === 1 ? b.confirmation.child : b.confirmation.children}`
                                              : ""}
                                          </div>
                                          {pids.length > 0 ? (
                                            <div className="pt-1">
                                              <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                                                {b.confirmation.additionalServices}
                                              </div>
                                              <ul className="mt-1 list-disc pl-4 text-sm text-neutral-900 space-y-0.5">
                                                {pids.map((pid) => (
                                                  <li key={pid}>{resolveBookedProductDisplayName(pid, b)}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          ) : null}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {totalFooter}
                                </div>
                              );
                            }

                            return (
                              <div className="rounded-2xl border border-pine-green/15 bg-white p-6 shadow-sm">
                                <h3 className="font-playfair text-lg font-semibold text-neutral-900 mb-4">
                                  {b.confirmation.stayDetails}
                                </h3>
                                <dl className="grid gap-3 text-sm">
                                  <div>
                                    <dt className="text-neutral-500 font-medium">{b.confirmation.rooms}</dt>
                                    <dd className="text-neutral-900 font-semibold mt-0.5">
                                      {[...new Set(roomNames)].join(", ")}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-neutral-500 font-medium">{b.confirmation.datesNights}</dt>
                                    <dd className="text-neutral-900 mt-0.5">
                                      {startDate} → {endDate} · {totalNights}{" "}
                                      {totalNights === 1 ? b.policies.night : b.policies.nights}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt className="text-neutral-500 font-medium">{b.confirmation.guestsLabel}</dt>
                                    <dd className="text-neutral-900 mt-0.5">
                                      {totalAdults}{" "}
                                      {totalAdults === 1 ? b.confirmation.adult : b.confirmation.adults}
                                      {totalChildren > 0
                                        ? `, ${totalChildren} ${totalChildren === 1 ? b.confirmation.child : b.confirmation.children}`
                                        : ""}
                                    </dd>
                                  </div>
                                  {visibleFirstProducts.length > 0 ? (
                                    <div>
                                      <dt className="text-neutral-500 font-medium">
                                        {b.confirmation.additionalServices}
                                      </dt>
                                      <dd className="text-neutral-900 mt-0.5">
                                        <ul className="list-disc pl-4 space-y-0.5">
                                          {visibleFirstProducts.map((pid) => (
                                            <li key={pid}>{resolveBookedProductDisplayName(pid, b)}</li>
                                          ))}
                                        </ul>
                                      </dd>
                                    </div>
                                  ) : null}
                                  {res.TotalAmount?.GrossValue != null ? (
                                    <div className="pt-3 border-t border-pine-green/10">
                                      <dt className="text-neutral-500 font-medium">{b.confirmation.total}</dt>
                                      <dd className="text-neutral-900 font-semibold mt-0.5">
                                        {res.TotalAmount.Currency ?? "CHF"} {res.TotalAmount.GrossValue.toFixed(2)}
                                        <span className="block text-xs font-normal text-neutral-500 mt-0.5">
                                          {b.policies.includingTaxesFees}
                                        </span>
                                      </dd>
                                    </div>
                                  ) : null}
                                </dl>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  )}
                  {currentStep === "rooms" && (
                      <div className="space-y-6">
                      <div className="flex flex-col gap-6 mb-8">
                        {/* Plan your stay + category tabs in one card; filter icon on the right */}
                        <div className="rounded-2xl border border-white/50 bg-[rgba(253,253,253,0.4)] backdrop-blur-[10px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex flex-col gap-3 sm:gap-4 min-w-0">
                            <div className="flex items-center gap-3 sm:gap-4">
                              <button
                                type="button"
                                onClick={goBack}
                                aria-label={b.steps.backAria}
                                className="group relative flex h-10 w-12 shrink-0 items-center justify-center gap-1 rounded-xl bg-pine-green/5 hover:bg-pine-green/10 transition-all duration-500 cursor-pointer"
                              >
                                <span className="flex items-center justify-center transition-opacity duration-500 group-hover:opacity-0">
                                  <TbHomeCheck className="h-5 w-5 shrink-0 text-neutral-500" aria-hidden />
                                </span>
                                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <ArrowLeft className="h-4 w-4 text-neutral-500 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                                </span>
                              </button>
                              <div className="h-8 sm:h-10 w-px shrink-0 bg-pine-green/10" aria-hidden />
                              <div className="min-w-0">
                                <h2 className="font-playfair text-xl sm:text-2xl text-neutral-900 leading-snug">
                                  {b.steps.chooseViewTitle}
                                </h2>
                                {stayRooms.length > 1 && planningStayRoom ? (
                                  <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-sans font-medium tabular-nums tracking-tight text-neutral-600">
                                    <span>{planRoomProgressPhrase}</span>
                                    <span className="text-neutral-400 select-none" aria-hidden>
                                      ·
                                    </span>
                                    {renderMultiStayRoomOccupancyPicker(
                                      planRoomIndex,
                                      planningStayRoom,
                                    )}
                                  </p>
                                ) : null}
                                {stayRooms.length <= 1 ? (
                                  <p className="mt-0.5 text-neutral-600 text-sm leading-relaxed">
                                    {b.steps.chooseViewSubtitle}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                            <div
                              className="min-w-0 pb-0.5"
                              role="toolbar"
                              aria-label={planStepToolbarAriaLabel}
                            >
                              <div className="flex flex-wrap gap-2">
                                {(
                                  [
                                    "All",
                                    "Eiger, Mönch & Jungfrau View",
                                    "Lake View",
                                    "Beatenberg View",
                                    "Balcony",
                                    "No balcony",
                                    "Suites",
                                  ] as FilterCategory[]
                                ).map((filter) => (
                                  <button
                                    key={filter}
                                    type="button"
                                    onClick={() => setActiveFilter(filter)}
                                    className={cn(
                                      "shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium leading-tight transition-all duration-200 sm:px-3.5 sm:text-[13px]",
                                      activeFilter === filter
                                        ? "border-pine-green bg-pine-green text-white shadow-sm"
                                        : "border-gray-200 bg-white/80 text-neutral-600 hover:border-gray-300 hover:bg-white"
                                    )}
                                  >
                                    {b.filters[filter]}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                          {/* Hidden filter icon/button on the right */}
                          <div className="hidden" aria-hidden="true" />
                        </div>

                        {availabilityError && (
                          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {availabilityError}
                          </div>
                        )}

                      </div>

                      {(!criteriaConfirmed || isMewsConfigLoading || isCheckingAvailability) ? (
                        <div aria-busy="true" aria-live="polite">
                          <div className="hidden space-y-6 book:block">
                            <RoomCardSkeletonPremium />
                            <RoomCardSkeletonPremium />
                            <RoomCardSkeletonPremium />
                          </div>
                          <div className="space-y-6 book:hidden">
                            <RoomCardSkeletonCompact />
                            <RoomCardSkeletonCompact />
                            <RoomCardSkeletonCompact />
                          </div>
                        </div>
                      ) : filteredRooms.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 p-4 text-sm text-neutral-600">
                          {b.steps.noRoomsMatch}
                        </div>
                      ) : (
                      <div className="space-y-6">
                        {filteredRooms.map((r) => {
                          const isSelected = selectedRoomId === r.sourceRoom.id;
                          const ratesExpanded = isSelected && ratesSectionExpanded;
                          const localRoomId = r.sourceRoom.id;
                          const roomAvailability = availabilityByLocalRoomId[localRoomId];
                          const standardPricePerNight = getStandardPricePerNightForRoom(localRoomId);
                          const cheapestMappedRate = getCheapestMappedAvailabilityRate(roomAvailability);
                          const occupancyDisplay = occupancyDisplayForLocalRoom(
                            localRoomId,
                            r.sourceRoom.capacity.maxOccupancy,
                          );

                          const handleViewRates = () => {
                            if (selectedRoomId === r.sourceRoom.id) {
                              setRatesSectionExpanded((prev) => !prev);
                            } else {
                              const targetKey = r.key;
                              const targetRoomId = r.sourceRoom.id;
                              setRatesSectionExpanded(false);
                              const closeOverlapMs = 380;
                              setTimeout(() => {
                                onChooseRoom(targetKey, targetRoomId);
                                requestAnimationFrame(() => {
                                  setRatesSectionExpanded(true);
                                });
                                const expandDurationMs = 520;
                                setTimeout(() => {
                                  const el = document.getElementById(`rates-content-${targetRoomId}`);
                                  if (!el) return;
                                  const rect = el.getBoundingClientRect();
                                  const padding = 24;
                                  const viewportHeight = window.innerHeight;
                                  const scrollY = window.scrollY ?? window.pageYOffset;
                                  let targetScrollY: number | null = null;
                                  if (rect.top < padding) {
                                    targetScrollY = scrollY + rect.top - padding;
                                  } else if (rect.bottom > viewportHeight - padding) {
                                    targetScrollY = scrollY + rect.bottom - viewportHeight + padding;
                                  }
                                  if (targetScrollY !== null) {
                                    window.scrollTo({ top: Math.max(0, targetScrollY), behavior: "smooth" });
                                  }
                                }, expandDurationMs);
                              }, closeOverlapMs);
                            }
                          };

                          return (
                            <div key={r.sourceRoom.id} id={`room-${r.sourceRoom.id}`} className="space-y-4">
                              {/* Desktop: unified card (room + rates when expanded), rates animate open/close 500ms */}
                              <div className="hidden book:block">
                                {isSelected ? (
                                  <div className="rounded-[32px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.06)] backdrop-blur-[10px] bg-[rgba(253,253,253,0.65)]">
                                    <PremiumRoomCard
                                      room={r}
                                      selected={isSelected}
                                      ratesTrayOpen={ratesExpanded}
                                      onViewRates={handleViewRates}
                                      roomAvailability={roomAvailability}
                                      standardPricePerNight={standardPricePerNight}
                                      criteriaConfirmed={criteriaConfirmed}
                                      displayCurrency={displayCurrency}
                                      chargedNights={chargedNights}
                                      cheapestMappedRate={cheapestMappedRate}
                                      noBottomRadius={ratesSectionExpanded}
                                      occupancyDisplay={occupancyDisplay}
                                    />
                                    <div
                                      className={cn(
                                        "overflow-hidden transition-[max-height] ease-in-out",
                                        ratesSectionExpanded ? "max-h-[2000px] duration-500" : "max-h-0 [transition-duration:750ms]"
                                      )}
                                    >
                                      <div
                                        id={`rates-content-${r.sourceRoom.id}`}
                                        className="rounded-b-[32px] border-t-2 border-r-2 border-b-2 border-l-0 border-white px-6 py-6 bg-[rgba(253,253,253,0.65)] backdrop-blur-[10px]"
                                      >
                                        <div
                                          ref={ratesRef}
                                          className="grid gap-4 book:grid-cols-2 book:items-stretch [&>div]:min-h-0 [&>div]:min-w-0"
                                        >
                                      {(() => {
                                        const localRoomId = r.sourceRoom.id;
                                        const rateOptions = getRateOptionsByPlanId(localRoomId);
                                        
                                        return ratePlans.map((plan) => {
                                          const planSelected = selectedRatePlanId === plan.id;
                                          const rateOption = plan.id === "standard" ? rateOptions.standard : rateOptions.signature;
                                          
                                          // Use MEWS pricing if available, otherwise fall back to multiplier-based calculation
                                          const hasMewsPricing = !!rateOption;
                                          const nightly = hasMewsPricing 
                                            ? rateOption.pricePerNight 
                                            : plan.multiplier * effectiveBaseNightly;
                                          
                                          return (
                                            <div key={plan.id} className="flex min-h-0 h-full min-w-0 w-full flex-col">
                                              <BookingRatePlanCard
                                                plan={plan}
                                                planSelected={planSelected}
                                                selectedRatePlanId={selectedRatePlanId}
                                                hasMewsPricing={hasMewsPricing}
                                                rateOption={rateOption}
                                                nightly={nightly}
                                                chargedNights={chargedNights}
                                                displayCurrency={displayCurrency}
                                                checkIn={checkIn}
                                                onCardActivate={() => {
                                                  activatePlanningRatePlan(plan.id, localRoomId, rateOption);
                                                }}
                                                onSelectRate={() => {
                                                  finalizePlanningRatePlan(plan.id, localRoomId, rateOption);
                                                }}
                                              />
                                            </div>
                                          );
                                        });
                                      })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <PremiumRoomCard
                                    room={r}
                                    selected={isSelected}
                                    ratesTrayOpen={ratesExpanded}
                                    onViewRates={handleViewRates}
                                    roomAvailability={roomAvailability}
                                    standardPricePerNight={standardPricePerNight}
                                    criteriaConfirmed={criteriaConfirmed}
                                    displayCurrency={displayCurrency}
                                    chargedNights={chargedNights}
                                    cheapestMappedRate={cheapestMappedRate}
                                    occupancyDisplay={occupancyDisplay}
                                  />
                                )}
                              </div>
                              {/* Mobile: rates animate open/close 500ms */}
                              <div className="book:hidden">
                                {isSelected ? (
                                  <div className="rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.06)] backdrop-blur-[10px] bg-[rgba(253,253,253,0.8)]">
                                    <RoomCard
                                      room={r}
                                      selected={isSelected}
                                      ratesTrayOpen={ratesExpanded}
                                      onViewRates={handleViewRates}
                                      testMobileAspectRatio={true}
                                      roomAvailability={roomAvailability}
                                      standardPricePerNight={standardPricePerNight}
                                      criteriaConfirmed={criteriaConfirmed}
                                      displayCurrency={displayCurrency}
                                      chargedNights={chargedNights}
                                      cheapestMappedRate={cheapestMappedRate}
                                      noBottomRadius={ratesSectionExpanded}
                                      occupancyDisplay={occupancyDisplay}
                                    />
                                    <div
                                      className={cn(
                                        "overflow-hidden transition-[max-height] ease-in-out",
                                        ratesSectionExpanded ? "max-h-[2000px] duration-500" : "max-h-0 [transition-duration:750ms]"
                                      )}
                                    >
                                      <div
                                        id={`rates-content-${r.sourceRoom.id}`}
                                        className="rounded-b-2xl border-t-2 border-r-2 border-b-2 border-l-0 border-white pl-1 pr-2 pt-4 pb-5 bg-[rgba(253,253,253,0.8)] backdrop-blur-[10px]"
                                      >
                                        <div
                                          ref={ratesRef}
                                          className="flex items-stretch gap-2 overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth snap-x snap-mandatory pb-1 pl-1 pr-4 [-webkit-overflow-scrolling:touch] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                                        >
                                          {(() => {
                                            const rateOptions = getRateOptionsByPlanId(localRoomId);
                                            return ratePlans.map((plan) => {
                                              const planSelected = selectedRatePlanId === plan.id;
                                              const rateOption = plan.id === "standard" ? rateOptions.standard : rateOptions.signature;
                                              const hasMewsPricing = !!rateOption;
                                              const nightly = hasMewsPricing ? rateOption!.pricePerNight : plan.multiplier * effectiveBaseNightly;
                                              return (
                                                <div
                                                  key={plan.id}
                                                  className={cn(
                                                    "flex h-full min-h-0 shrink-0 snap-center snap-always flex-col self-stretch",
                                                    plan.id === "standard"
                                                      ? "w-[min(82vw,18.5rem)]"
                                                      : "w-[min(88vw,20rem)]",
                                                  )}
                                                >
                                                  <BookingRatePlanCard
                                                    plan={plan}
                                                    planSelected={planSelected}
                                                    selectedRatePlanId={selectedRatePlanId}
                                                    hasMewsPricing={hasMewsPricing}
                                                    rateOption={rateOption}
                                                    nightly={nightly}
                                                    chargedNights={chargedNights}
                                                    displayCurrency={displayCurrency}
                                                    checkIn={checkIn}
                                                    dense
                                                    onCardActivate={() => {
                                                      activatePlanningRatePlan(plan.id, localRoomId, rateOption);
                                                    }}
                                                    onSelectRate={() => {
                                                      finalizePlanningRatePlan(plan.id, localRoomId, rateOption);
                                                    }}
                                                  />
                                                </div>
                                              );
                                            });
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <RoomCard
                                    room={r}
                                    selected={isSelected}
                                    ratesTrayOpen={ratesExpanded}
                                    onViewRates={handleViewRates}
                                    testMobileAspectRatio={true}
                                    roomAvailability={roomAvailability}
                                    standardPricePerNight={standardPricePerNight}
                                    criteriaConfirmed={criteriaConfirmed}
                                    displayCurrency={displayCurrency}
                                    chargedNights={chargedNights}
                                    cheapestMappedRate={cheapestMappedRate}
                                    occupancyDisplay={occupancyDisplay}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                      </div>
                  )}

                  {currentStep === "addons" && (
                    <div className="space-y-6">
                      <div className="flex flex-col gap-6 mb-8">
                        {/* Enhance your stay + category chips in one card */}
                        <div className="rounded-2xl border border-white/50 bg-[rgba(253,253,253,0.4)] backdrop-blur-[10px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-4 flex flex-col gap-3 sm:gap-4">
                          <div className="flex flex-col gap-3 sm:gap-4 min-w-0">
                            <div className="flex items-center gap-3 sm:gap-4">
                              <button
                                type="button"
                                onClick={goBack}
                                aria-label={b.steps.backAria}
                                className="group relative flex h-10 w-12 shrink-0 items-center justify-center gap-1 rounded-xl bg-pine-green/5 hover:bg-pine-green/10 transition-all duration-500 cursor-pointer"
                              >
                                <span className="flex items-center justify-center transition-opacity duration-500 group-hover:opacity-0">
                                  <TbHomeStar className="h-5 w-5 shrink-0 text-neutral-500" aria-hidden />
                                </span>
                                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                  <ArrowLeft className="h-4 w-4 text-neutral-500 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                                </span>
                              </button>
                              <div className="h-8 sm:h-10 w-px shrink-0 bg-pine-green/10" aria-hidden />
                              <div className="min-w-0">
                                <h2 className="font-playfair text-xl sm:text-2xl text-neutral-900 leading-snug">
                                  {b.steps.enhanceTitle}
                                </h2>
                                {stayRooms.length > 1 && enhancingStayRoom ? (
                                  <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-sans font-medium tabular-nums tracking-tight text-neutral-600">
                                    <span>{enhanceRoomProgressPhrase}</span>
                                    <span className="text-neutral-400 select-none" aria-hidden>
                                      ·
                                    </span>
                                    {renderMultiStayRoomOccupancyPicker(
                                      enhanceRoomIndex,
                                      enhancingStayRoom,
                                    )}
                                  </p>
                                ) : (
                                  <p className="mt-0.5 text-sm leading-relaxed text-neutral-600">
                                    {b.steps.enhanceSubtitle}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div
                              className="-mx-4 min-w-0 overflow-x-auto overscroll-x-contain pb-0.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                              role="toolbar"
                              aria-label={enhanceStepToolbarAriaLabel}
                            >
                              <div className="flex w-max min-w-0 flex-nowrap gap-2 px-4">
                                {ADDON_FILTER_OPTIONS.map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => setAddonFilter(option)}
                                    className={cn(
                                      "shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium leading-tight transition-all duration-200 sm:px-3.5 sm:text-[13px]",
                                      addonFilter === option
                                        ? "border-pine-green bg-pine-green text-white shadow-sm"
                                        : "border-gray-200 bg-white/80 text-neutral-600 hover:border-gray-300 hover:bg-white"
                                    )}
                                  >
                                    {b.addonFilters[option]}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {filteredAddonUpsells.map((u) => {
                          const money = upsellCardMoneyByUpsellId[u.id];
                          return (
                            <UpsellCard
                              key={u.id}
                              upsell={u}
                              count={enhancingStayRoom.addonUpsellCounts[u.id] ?? 0}
                              onCountChange={(count) => patchEnhancingStayRoomAddon(u.id, count)}
                              sellFormatted={
                                money?.sellFormatted ?? formatBookMoney(u.price, "CHF", locale)
                              }
                              listFormatted={
                                money?.listFormatted ??
                                (u.listPrice > u.price + 1e-6
                                  ? formatBookMoney(u.listPrice, "CHF", locale)
                                  : null)
                              }
                              pricedForRoomGuestsHint={upsellPricedForRoomGuestsHintById[u.id]}
                            />
                          );
                        })}
                      </div>
                      {filteredAddonUpsells.length === 0 && (
                        <p className="text-sm text-neutral-500 py-4">{b.steps.noAddonsCategory}</p>
                      )}
                    </div>
                  )}

                  {currentStep === "details" && (
                    <div className="rounded-2xl border border-white/50 bg-[rgba(253,253,253,0.4)] backdrop-blur-[10px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-4">
                      {/* Header: step + title/subtitle on left; on PC, booking tabs on the right */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                          <button
                            type="button"
                            onClick={goBack}
                            aria-label={b.steps.backAria}
                            className="group relative flex h-10 w-12 shrink-0 items-center justify-center gap-1 rounded-xl bg-pine-green/5 hover:bg-pine-green/10 transition-all duration-500 cursor-pointer"
                          >
                            <span className="flex items-center justify-center transition-opacity duration-500 group-hover:opacity-0">
                              <BsPersonVcard className="h-5 w-5 shrink-0 text-neutral-500" aria-hidden />
                            </span>
                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <ArrowLeft className="h-4 w-4 text-neutral-500 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                            </span>
                          </button>
                          <div className="h-8 sm:h-10 w-px shrink-0 bg-pine-green/10" aria-hidden />
                          <div className="min-w-0">
                            <h2 className="font-playfair text-xl sm:text-2xl text-neutral-900 break-words">
                              {b.steps.detailsTitle}
                            </h2>
                            <p className="text-neutral-600 text-sm mt-0.5">{b.steps.detailsSubtitle}</p>
                          </div>
                        </div>
                        <div className="flex justify-center sm:justify-end gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => setGuestDetails((prev) => ({ ...prev, isBookingOnBehalf: false }))}
                            className={cn(
                              "px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                              !guestDetails.isBookingOnBehalf
                                ? "bg-gray-100 border border-gray-300 text-neutral-900"
                                : "border border-gray-200 text-neutral-600 hover:bg-gray-50 hover:text-pine-green"
                            )}
                          >
                            {b.steps.bookingForSelf}
                          </button>
                          <button
                            type="button"
                            onClick={() => setGuestDetails((prev) => ({ ...prev, isBookingOnBehalf: true }))}
                            className={cn(
                              "px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                              guestDetails.isBookingOnBehalf
                                ? "bg-gray-100 border border-gray-300 text-neutral-900"
                                : "border border-gray-200 text-neutral-600 hover:bg-gray-50 hover:text-pine-green"
                            )}
                          >
                            {b.steps.bookingForOther}
                          </button>
                        </div>
                      </div>
                      <div className="relative space-y-8">
                        {/* Loading overlay while createReservationGroup runs (and before Mews redirect) */}
                      {isCreatingReservation && (
                        <div
                          className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-pine-green/5 backdrop-blur-[2px]"
                          aria-live="polite"
                          aria-busy="true"
                        >
                          <div className="mx-4 flex max-w-sm flex-col items-center gap-6 rounded-2xl border border-gray-200/80 bg-white/95 px-8 py-10 shadow-lg">
                            <Loader2 className="h-12 w-12 animate-spin text-alpine-gold" aria-hidden />
                            <div className="text-center space-y-1">
                              <p className="font-playfair text-lg font-semibold text-neutral-900">
                                {isRedirectingToPayment
                                  ? b.steps.redirectingToPayment
                                  : onSessionEligible
                                    ? b.steps.creatingReservation
                                    : b.steps.processingReservation}
                              </p>
                              <p className="text-sm text-neutral-600">{b.steps.doNotClose}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {!guestDetails.isBookingOnBehalf ? (
                        /* Single section when guest is the booker */
                        <section>
                          <h3 className="text-sm font-semibold text-neutral-900 mb-4">{b.steps.yourDetails}</h3>
                          <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-neutral-900">{b.steps.firstName}</label>
                          <input
                            className={cn(
                              "w-full rounded-xl border px-3 py-2",
                              guestDetailsErrors.firstName ? "border-red-500 bg-red-50/50" : "border-gray-200"
                            )}
                            value={guestDetails.firstName}
                            onChange={(e) => updateGuestDetailsField("firstName", e.target.value)}
                          />
                          {guestDetailsErrors.firstName && (
                            <p className="text-xs text-red-600">{guestDetailsErrors.firstName}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-neutral-900">{b.steps.lastName}</label>
                          <input
                            className={cn(
                              "w-full rounded-xl border px-3 py-2",
                              guestDetailsErrors.lastName ? "border-red-500 bg-red-50/50" : "border-gray-200"
                            )}
                            value={guestDetails.lastName}
                            onChange={(e) => updateGuestDetailsField("lastName", e.target.value)}
                          />
                          {guestDetailsErrors.lastName && (
                            <p className="text-xs text-red-600">{guestDetailsErrors.lastName}</p>
                          )}
                        </div>
                        {/* Line 2: Email, Phone */}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-neutral-900">{b.steps.email}</label>
                          <input
                            type="email"
                            className={cn(
                              "w-full rounded-xl border px-3 py-2",
                              guestDetailsErrors.email ? "border-red-500 bg-red-50/50" : "border-gray-200"
                            )}
                            value={guestDetails.email}
                                onChange={(e) => updateGuestDetailsField("email", e.target.value)}
                          />
                          <p className="text-xs text-neutral-500">{b.steps.emailHint}</p>
                          {guestDetailsErrors.email && (
                            <p className="text-xs text-red-600">{guestDetailsErrors.email}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-neutral-900">
                            {b.steps.phone}
                            {mewsRequiredFields.includes("Telephone") ? " *" : ""}
                          </label>
                          <PhoneCountryInput
                            value={guestDetails.telephone}
                            onChange={(val) => updateGuestDetailsField("telephone", val)}
                            onBlur={() => handleGuestDetailsPhoneBlur("telephone")}
                            placeholder=""
                            hasError={!!guestDetailsErrors.telephone}
                          />
                          {guestDetailsErrors.telephone && (
                            <p className="text-xs text-red-600">{guestDetailsErrors.telephone}</p>
                          )}
                        </div>
                        {/* Line 3: Nationality */}
                        <div className="space-y-2 sm:col-span-2">
                          <label className="text-sm font-semibold text-neutral-900">{b.steps.nationality}</label>
                          <NationalityCountryInput
                            value={guestDetails.nationalityCode}
                            onChange={(val) =>
                              setGuestDetails((prev) => ({ ...prev, nationalityCode: val }))
                            }
                            placeholder=""
                            className="w-full"
                          />
                        </div>
                        {/* Line 4: Special requests */}
                        <div className="space-y-2 sm:col-span-2">
                          <label className="text-sm font-semibold text-neutral-900">{b.steps.specialRequests}</label>
                          <textarea
                            className="w-full rounded-xl border border-gray-200 px-3 py-2 min-h-[72px]"
                            value={guestDetails.notes}
                            onChange={(e) =>
                              setGuestDetails((prev) => ({ ...prev, notes: e.target.value }))
                            }
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-4 pt-1">
                          <label className="flex items-center gap-2 text-sm text-neutral-900 cursor-pointer" htmlFor="sendMarketing">
                            <input
                              type="checkbox"
                              id="sendMarketing"
                              checked={guestDetails.sendMarketingEmails === true}
                            onChange={(e) =>
                              setGuestDetails((prev) => ({
                                ...prev,
                                sendMarketingEmails: e.target.checked ? true : null,
                              }))
                            }
                          />
                            {b.steps.marketingOptIn}
                          </label>
                        </div>
                        </div>
                      </section>
                      ) : (
                        <>
                          <section>
                            <h3 className="text-sm font-semibold text-neutral-900 mb-4">{b.steps.yourDetails}</h3>
                            <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.firstName}</label>
                              <input
                                className={cn(
                                  "w-full rounded-xl border px-3 py-2",
                                  guestDetailsErrors.bookerFirstName
                                    ? "border-red-500 bg-red-50/50"
                                    : "border-gray-200"
                                )}
                                value={guestDetails.bookerFirstName}
                                onChange={(e) => updateGuestDetailsField("bookerFirstName", e.target.value)}
                              />
                              {guestDetailsErrors.bookerFirstName && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.bookerFirstName}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.lastName}</label>
                              <input
                                className={cn(
                                  "w-full rounded-xl border px-3 py-2",
                                  guestDetailsErrors.bookerLastName
                                    ? "border-red-500 bg-red-50/50"
                                    : "border-gray-200"
                                )}
                                value={guestDetails.bookerLastName}
                                onChange={(e) => updateGuestDetailsField("bookerLastName", e.target.value)}
                              />
                              {guestDetailsErrors.bookerLastName && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.bookerLastName}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.email}</label>
                              <input
                                type="email"
                                className={cn(
                                  "w-full rounded-xl border px-3 py-2",
                                  guestDetailsErrors.bookerEmail
                                    ? "border-red-500 bg-red-50/50"
                                    : "border-gray-200"
                                )}
                                value={guestDetails.bookerEmail}
                                onChange={(e) => updateGuestDetailsField("bookerEmail", e.target.value)}
                              />
                              <p className="text-xs text-neutral-500">{b.steps.emailHint}</p>
                              {guestDetailsErrors.bookerEmail && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.bookerEmail}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">
                                {b.steps.phone}
                                {mewsRequiredFields.includes("Telephone") ? " *" : ""}
                              </label>
                              <PhoneCountryInput
                                value={guestDetails.bookerTelephone}
                                onChange={(val) => updateGuestDetailsField("bookerTelephone", val)}
                                onBlur={() => handleGuestDetailsPhoneBlur("bookerTelephone")}
                                placeholder=""
                                hasError={!!guestDetailsErrors.bookerTelephone}
                              />
                              {guestDetailsErrors.bookerTelephone && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.bookerTelephone}</p>
                              )}
                            </div>
                            <div className="space-y-2 sm:col-span-2 flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="bookerSendMarketing"
                                checked={guestDetails.bookerSendMarketingEmails === true}
                                onChange={(e) =>
                                  setGuestDetails((prev) => ({
                                    ...prev,
                                    bookerSendMarketingEmails: e.target.checked ? true : null,
                                  }))
                                }
                              />
                              <label htmlFor="bookerSendMarketing" className="text-sm text-neutral-900">
                                {b.steps.marketingOptInBooker}
                              </label>
                            </div>
                          </div>
                        </section>
                        <section>
                          <h3 className="text-sm font-semibold text-neutral-900 mb-4">{b.steps.guestDetailsSection}</h3>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.firstName}</label>
                              <input
                                className={cn(
                                  "w-full rounded-xl border px-3 py-2",
                                  guestDetailsErrors.firstName ? "border-red-500 bg-red-50/50" : "border-gray-200"
                                )}
                                value={guestDetails.firstName}
                                onChange={(e) => updateGuestDetailsField("firstName", e.target.value)}
                              />
                              {guestDetailsErrors.firstName && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.firstName}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.lastName}</label>
                              <input
                                className={cn(
                                  "w-full rounded-xl border px-3 py-2",
                                  guestDetailsErrors.lastName ? "border-red-500 bg-red-50/50" : "border-gray-200"
                                )}
                                value={guestDetails.lastName}
                                onChange={(e) => updateGuestDetailsField("lastName", e.target.value)}
                              />
                              {guestDetailsErrors.lastName && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.lastName}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.email}</label>
                              <input
                                type="email"
                                className={cn(
                                  "w-full rounded-xl border px-3 py-2",
                                  guestDetailsErrors.email ? "border-red-500 bg-red-50/50" : "border-gray-200"
                                )}
                                value={guestDetails.email}
                                onChange={(e) => updateGuestDetailsField("email", e.target.value)}
                              />
                              <p className="text-xs text-neutral-500">{b.steps.emailHint}</p>
                              {guestDetailsErrors.email && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.email}</p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-neutral-900">
                                {b.steps.phone}
                                {mewsRequiredFields.includes("Telephone") ? " *" : ""}
                              </label>
                              <PhoneCountryInput
                                value={guestDetails.telephone}
                                onChange={(val) => updateGuestDetailsField("telephone", val)}
                                onBlur={() => handleGuestDetailsPhoneBlur("telephone")}
                                placeholder=""
                                hasError={!!guestDetailsErrors.telephone}
                              />
                              {guestDetailsErrors.telephone && (
                                <p className="text-xs text-red-600">{guestDetailsErrors.telephone}</p>
                              )}
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.nationality}</label>
                              <NationalityCountryInput
                                value={guestDetails.nationalityCode}
                                onChange={(val) =>
                                  setGuestDetails((prev) => ({ ...prev, nationalityCode: val }))
                                }
                                placeholder=""
                                className="w-full"
                              />
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                              <label className="text-sm font-semibold text-neutral-900">{b.steps.specialRequests}</label>
                              <textarea
                                className="w-full rounded-xl border border-gray-200 px-3 py-2 min-h-[72px]"
                                value={guestDetails.notes}
                                onChange={(e) =>
                                  setGuestDetails((prev) => ({ ...prev, notes: e.target.value }))
                                }
                              />
                            </div>
                          </div>
                        </section>
                      </>
                      )}
                      {/* Payment section: card form, confirm (same screen as guest details) */}
                      <div ref={paymentRef} className="space-y-6 pt-8 border-t border-gray-200">
                        {createReservationResult ? (
                        <div className="rounded-2xl border border-green-200 bg-green-50/50 p-5">
                          <div className="font-semibold text-neutral-900">{b.confirmation.reservationCreated}</div>
                          <div className="text-sm text-neutral-600 mt-1">
                            {b.confirmation.reservationCreatedBodyPrefix}
                            {createReservationResult.Reservations.length === 1 ? (
                              <>: <strong>{createReservationResult.Reservations[0].Number}</strong></>
                            ) : (
                              <>
                                {b.confirmation.numbersPrefix}{" "}
                                {createReservationResult.Reservations.map((r) => r.Number).join(", ")}
                              </>
                            )}
                            {b.confirmation.reservationCreatedBodySuffix}
                          </div>
                        </div>
                      ) : !paymentStepActive ? (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5">
                          <div className="font-semibold text-neutral-900">
                            {stayDatesIncomplete
                              ? b.confirmation.paymentAwaitingDatesTitle
                              : isOnSessionMode
                                ? b.confirmation.onSessionPaymentNotAvailableTitle
                                : b.confirmation.cardNotAvailableTitle}
                          </div>
                          <div className="text-sm text-neutral-600 mt-1">
                            {stayDatesIncomplete
                              ? b.confirmation.paymentAwaitingDatesBody
                              : isOnSessionMode
                                ? b.confirmation.onSessionPaymentNotAvailableBody
                                : b.confirmation.cardNotAvailableBody}
                          </div>
                          {!stayDatesIncomplete ? (
                            <a
                              href="/#contact"
                              className="mt-3 inline-block text-sm font-semibold text-neutral-900 underline underline-offset-4"
                            >
                              {b.confirmation.contactUs}
                            </a>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          {onSessionEligible && amountToChargeOnConfirmation ? (
                            <section className="rounded-2xl border border-gray-200 p-5 space-y-4">
                              <h3 className="text-sm font-semibold text-neutral-900">{b.confirmation.paymentDetails}</h3>
                              <p className="text-sm text-neutral-600">{b.confirmation.onSessionSecureNote}</p>
                              <div>
                                <div className="text-sm text-neutral-500">{b.confirmation.amountDueNow}</div>
                                <div className="text-2xl font-semibold text-neutral-900">
                                  {formatBookMoney(
                                    amountToChargeOnConfirmation.grossValue,
                                    amountToChargeOnConfirmation.currency,
                                    locale,
                                  )}
                                </div>
                                {displayCurrency &&
                                  displayCurrency.toUpperCase() !==
                                    amountToChargeOnConfirmation.currency.toUpperCase() && (
                                    <p className="mt-1 text-xs text-neutral-500">
                                      {b.confirmation.settlementCurrencyNote.replace(
                                        /\{\{currency\}\}/g,
                                        amountToChargeOnConfirmation.currency,
                                      )}
                                    </p>
                                  )}
                              </div>
                              {bookingSummaryAmounts &&
                                Math.abs(
                                  bookingSummaryAmounts.effective - amountToChargeOnConfirmation.grossValue,
                                ) > 0.01 && (
                                  <div className="border-t border-gray-100 pt-3">
                                    <div className="text-sm text-neutral-500">{b.confirmation.totalStayAmount}</div>
                                    <div className="text-base font-medium text-neutral-700">
                                      {formatBookMoney(
                                        bookingSummaryAmounts.effective,
                                        bookingSummaryAmounts.currency,
                                        locale,
                                      )}
                                    </div>
                                  </div>
                                )}
                            </section>
                          ) : null}
                          {optionBActive ? (
                          <section className="rounded-2xl border border-gray-200 p-5 space-y-4 book:flex book:items-center book:justify-between book:gap-6 book:space-y-0">
                            <div className="min-w-0 space-y-4">
                            <h3 className="text-sm font-semibold text-neutral-900">{b.confirmation.paymentDetails}</h3>
                            <p className="text-sm text-neutral-600">{b.confirmation.paymentSecureNote}</p>
                            <div className="max-w-md space-y-4">
                              {/* Line 1: Name on Card */}
                              <div>
                                <label htmlFor="card-holder-name" className="block text-sm font-medium text-neutral-900 mb-1">
                                  {b.confirmation.nameOnCard}
                                </label>
                                <input
                                  id="card-holder-name"
                                  type="text"
                                  autoComplete="cc-name"
                                  placeholder=""
                                  value={cardHolderName}
                                  onChange={(e) => setCardHolderName(e.target.value)}
                                  className={cn(
                                    "w-full rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-500",
                                    cardFormErrors.holderName ? "border-amber-500" : "border-gray-300"
                                  )}
                                  aria-invalid={!!cardFormErrors.holderName}
                                  aria-describedby={cardFormErrors.holderName ? "card-holder-name-error" : undefined}
                                />
                                {cardFormErrors.holderName && (
                                  <p id="card-holder-name-error" className="mt-1 text-sm text-amber-700" role="alert">
                                    {cardFormErrors.holderName}
                                  </p>
                                )}
                              </div>
                              {/* Line 2: Card number; Line 3: Validity + CVC (SecureFieldsCardInput) */}
                              {paymentConfiguration?.PaymentGateway?.PublicKey && (
                                <SecureFieldsCardInput
                                  ref={secureFieldsRef}
                                  publicKey={paymentConfiguration.PaymentGateway.PublicKey}
                                  onReady={() => setSecureFieldsReady(true)}
                                  onInitError={() => {
                                    setSecureFieldsReady(false);
                                    setSecureFieldsNumberAndCvvValid(false);
                                  }}
                                  onFieldsValidityChange={handleSecureFieldsValidityChange}
                                >
                                  <div>
                                    <label htmlFor="card-expiration" className="block text-sm font-medium text-neutral-900 mb-1">
                                      {b.confirmation.validity}
                                    </label>
                                    <input
                                      id="card-expiration"
                                      type="text"
                                      inputMode="numeric"
                                      maxLength={5}
                                      value={cardExpiration}
                                      onChange={(e) => {
                                        const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                                        if (v.length <= 2) setCardExpiration(v);
                                        else setCardExpiration(`${v.slice(0, 2)}/${v.slice(2, 4)}`);
                                      }}
                                      className={cn(
                                        "w-full max-w-[120px] rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-500",
                                        cardFormErrors.expiration ? "border-amber-500" : "border-gray-300"
                                      )}
                                      aria-invalid={!!cardFormErrors.expiration}
                                      aria-describedby={cardFormErrors.expiration ? "card-expiration-error" : undefined}
                                    />
                                    {cardFormErrors.expiration && (
                                      <p id="card-expiration-error" className="mt-1 text-sm text-amber-700" role="alert">
                                        {cardFormErrors.expiration}
                                      </p>
                                    )}
                                  </div>
                                </SecureFieldsCardInput>
                              )}
                            </div>
                            {optionBActive && createReservationError ? (
                              <div className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 text-sm text-neutral-900">
                                <p className="font-medium text-neutral-900">{createReservationError}</p>
                                <p className="mt-2 text-neutral-700">{b.confirmation.tryAnotherCardHint}</p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="mt-4 rounded-full border-pine-green/30 text-pine-green hover:bg-pine-green/5"
                                  onClick={() => {
                                    setCreateReservationError(null);
                                    setCardFormErrors({});
                                    paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                                  }}
                                >
                                  {b.confirmation.tryAnotherCard}
                                </Button>
                              </div>
                            ) : null}
                            </div>
                          </section>
                          ) : null}
                          {onSessionEligible && createReservationError ? (
                            <div className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-4 text-sm text-neutral-900">
                              <p className="font-medium text-neutral-900">{createReservationError}</p>
                            </div>
                          ) : null}
                          <div className="hidden book:flex book:justify-end pt-4">
                              <Button
                                ref={confirmBookingButtonRef}
                                variant="hero"
                                className="rounded-full px-8"
                                disabled={detailsConfirmBookingDisabled}
                                onMouseDown={(e) => {
                                  // Keep focus out of the button so Datatrans iframe doesn't fire cross-origin onfocus.
                                  if (optionBActive) e.preventDefault();
                                }}
                                onClick={async () => {
                                if (!selectedRoom || !selectedRatePlan || !allStayRoomsPlanned) return;

                                const errors = validateGuestDetails(
                                  guestDetails,
                                  mewsRequiredFields,
                                  guestValidationMessages,
                                );
                                if (Object.keys(errors).length > 0) {
                                  setGuestDetailsErrors(errors);
                                  setCreateReservationError(b.validation.completeGuestDetails);
                                  return;
                                }
                                setGuestDetailsErrors({});
                                setCreateReservationError(null);

                                // Option B: validate card form and get transactionId from Secure Fields
                                let creditCardData: CreateReservationGroupCreditCardData | undefined;
                                if (optionBActive) {
                                  const cardErrors = validateCardForm(cardExpiration, cardHolderName);
                                  if (Object.keys(cardErrors).length > 0) {
                                    setCardFormErrors(cardErrors);
                                    setCreateReservationError(b.validation.completePaymentFields);
                                    return;
                                  }
                                  setCardFormErrors({});
                                  const expirationYyyyMm = mmYyToYyyyMm(cardExpiration);
                                  if (!expirationYyyyMm) {
                                    setCardFormErrors({ expiration: b.validation.expirationFormat });
                                    setCreateReservationError(b.validation.expirationValid);
                                    return;
                                  }
                                  try {
                                    console.log("[Book] Secure Fields submit: requesting transactionId…");
                                    const transactionId = await secureFieldsRef.current?.submit(expirationYyyyMm);
                                    console.log("[Book] Secure Fields submit: result", { transactionId: transactionId ? `${transactionId.slice(0, 12)}…` : null });
                                    if (!transactionId) {
                                      setCreateReservationError(b.errors.cardVerificationFailed);
                                      return;
                                    }
                                    creditCardData = {
                                      PaymentGatewayData: transactionId,
                                      Expiration: expirationYyyyMm,
                                      HolderName: cardHolderName.trim(),
                                    };
                                  } catch (err) {
                                    console.log("[Book] Secure Fields submit: catch", err);
                                    setCreateReservationError(
                                      err instanceof Error ? err.message : b.errors.cardVerificationFailedGeneric,
                                    );
                                    return;
                                  }
                                }

                                const groupReservations = buildStayRoomsCreateGroupReservations({
                                  stayRooms,
                                  availabilityByStayRoomId,
                                  checkIn,
                                  checkOut,
                                  notes: guestDetails.notes,
                                });
                                if (!groupReservations || groupReservations.length !== stayRooms.length) {
                                  setCreateReservationError(b.errors.roomMappingMissing);
                                  return;
                                }

                                const { customer, booker } = mapGuestDetailsToMewsCustomer(guestDetails);
                                setIsCreatingReservation(true);
                                const response = await createReservationGroup({
                                  customer,
                                  booker: booker ?? undefined,
                                  reservations: groupReservations,
                                  creditCardData,
                                });

                                if (response.error) {
                                  setIsCreatingReservation(false);
                                  const exceedingIndexes = getExceedingReservationIndexes(response.error);
                                  if (exceedingIndexes?.length) {
                                    setCreateReservationError(b.errors.availabilityChanged);
                                  } else {
                                    setCreateReservationError(
                                      response.error.Message ?? b.errors.createReservation,
                                    );
                                  }
                                  return;
                                }
                                const data = response.data;
                                if (!data) {
                                  setIsCreatingReservation(false);
                                  return;
                                }

                                await persistBookPurchaseBeforeRedirect(data.Id, data);

                                if (onSessionEligible) {
                                  if (!data.PaymentRequestId) {
                                    setIsCreatingReservation(false);
                                    setCreateReservationError(b.confirmation.onSessionPaymentNotAvailableBody);
                                    return;
                                  }
                                  const pendingId = getPendingOnSessionReservationGroupId();
                                  if (pendingId) {
                                    setIsCreatingReservation(false);
                                    await persistBookPurchaseBeforeRedirect(pendingId);
                                    markOnSessionPaymentRedirect(pendingId);
                                    setSearchParams(
                                      (prev) => {
                                        const next = new URLSearchParams(prev);
                                        next.set("step", "confirmation");
                                        next.set("reservationGroupId", pendingId);
                                        return next;
                                      },
                                      { replace: true },
                                    );
                                    return;
                                  }
                                  const returnUrl = buildPaymentConfirmationReturnUrl(data.Id);
                                  await redirectToExternalPayment(
                                    buildPaymentRequestRedirectUrl(
                                      data.PaymentRequestId,
                                      returnUrl,
                                    ),
                                    {
                                      reservationGroupId: data.Id,
                                      group: data,
                                      beforeNavigate: () => markOnSessionPaymentRedirect(data.Id),
                                    },
                                  );
                                  return;
                                }

                                if (optionBActive && !data.PaymentCardId) {
                                  setIsCreatingReservation(false);
                                  setCreateReservationError(b.errors.paymentNotLinked);
                                  return;
                                }

                                // Option B: getPaymentCards → authorizePaymentCard (Task 6); handle 3DS redirect (Task 6b)
                                if (optionBActive && data.PaymentCardId) {
                                  const paymentCardId = data.PaymentCardId;
                                  const reservationGroupId = data.Id;
                                  const hotelId = getApiClient().getHotelId();
                                  if (!hotelId) {
                                    setIsCreatingReservation(false);
                                    setCreateReservationError(b.errors.configError);
                                    return;
                                  }
                                  try {
                                    const cardsRes = await getPaymentCards({ paymentCardIds: [paymentCardId] });
                                    if (cardsRes.error) {
                                      setCreateReservationError(
                                        cardsRes.error.Message ?? b.errors.verifyCard,
                                      );
                                      return;
                                    }
                                    const cards = cardsRes.data?.PaymentCards ?? [];
                                    const card = cards.find((c) => c.Id === paymentCardId);
                                    // If getPaymentCards returns empty (e.g. demo eventual consistency), treat as Authorizable and proceed to authorize
                                    const state = card?.AuthorizationState ?? "Authorizable";
                                    if (card && state === "Authorized") {
                                      // Task 7: On-site Authorized — fetch reservation and show same confirmation view as 3DS return
                                      setCurrentStep("confirmation");
                                      setConfirmationLoadError(null);
                                      setIsLoadingConfirmation(true);
                                      getReservationGroup({ reservationGroupId })
                                        .then((res) => {
                                          if (res.error) {
                                            setConfirmationLoadError(
                                              res.error.Message ?? b.errors.loadReservationShort,
                                            );
                                            return;
                                          }
                                          if (res.data) {
                                            setCreateReservationResult(res.data as CreateReservationGroupResponse);
                                          }
                                        })
                                        .finally(() => setIsLoadingConfirmation(false));
                                      return;
                                    }
                                    if (state === "Unauthorizable") {
                                      setCreateReservationError(b.errors.cardNotAuthorized);
                                      return;
                                    }
                                    if (state !== "Authorizable") return;

                                    const browserInfo = collectBrowserInfo();
                                    const authRes = await authorizePaymentCard({
                                      enterpriseId: hotelId,
                                      paymentCardId,
                                      browserInfo,
                                    });
                                    if (authRes.error) {
                                      setCreateReservationError(
                                        authRes.error.Message ?? b.errors.verifyCard,
                                      );
                                      return;
                                    }
                                    const authState = authRes.data?.State;
                                    if (authState === "Authorized") {
                                      // Task 7: On-site Authorized — fetch reservation and show same confirmation view as 3DS return
                                      setCurrentStep("confirmation");
                                      setConfirmationLoadError(null);
                                      setIsLoadingConfirmation(true);
                                      getReservationGroup({ reservationGroupId })
                                        .then((res) => {
                                          if (res.error) {
                                            setConfirmationLoadError(
                                              res.error.Message ?? b.errors.loadReservationShort,
                                            );
                                            return;
                                          }
                                          if (res.data) {
                                            setCreateReservationResult(res.data as CreateReservationGroupResponse);
                                          }
                                        })
                                        .finally(() => setIsLoadingConfirmation(false));
                                      return;
                                    }
                                    if (authState === "Declined") {
                                      setCreateReservationError(b.errors.cardDeclined);
                                      return;
                                    }
                                    if (authState === "Canceled") {
                                      setCreateReservationError(b.errors.authCanceled);
                                      return;
                                    }
                                    if (authState === "Pending" || authState === "Requested") {
                                      const baseUrl = getMewsApplicationsBaseUrl().replace(/\/$/, "");
                                      const returnUrl =
                                        window.location.origin +
                                        "/book?step=confirmation&reservationGroupId=" +
                                        encodeURIComponent(reservationGroupId);
                                      const encodedReturnUrl = encodeMewsReturnUrl(returnUrl);
                                      const mewsUrl =
                                        `${baseUrl}/navigator/card-authorization/detail/${paymentCardId}?returnUrl=${encodeURIComponent(encodedReturnUrl)}`;
                                      await redirectToExternalPayment(mewsUrl, {
                                        reservationGroupId,
                                        group: data,
                                      });
                                      return;
                                    }
                                  } finally {
                                    setIsCreatingReservation(false);
                                  }
                                }

                                setCreateReservationResult(data);
                                setIsCreatingReservation(false);
                              }}
                            >
                              {isCreatingReservation
                                ? b.confirmation.processing
                                : onSessionEligible
                                  ? b.confirmation.confirmAndPay
                                  : b.confirmation.confirmBooking}
                            </Button>
                          </div>
                        </>
                      )}
                      </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop right rail */}
            <aside className="hidden book:block h-full">
              {/* z-40 above Footer (z-30); max-h leaves room above footer so long enhancement lists scroll inside the card */}
              <div className="sticky top-28 z-40 flex max-h-[calc(100dvh-7rem-6rem)] flex-col gap-4 pr-1">
                <div className="w-full shrink-0">
                  <BestRateGuaranteePill copy={b.bestRateGuarantee} href={brgHref} />
                </div>
                <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-white/50 bg-[rgba(253,253,253,0.75)] backdrop-blur-[10px] shadow-[0_4px_24px_rgba(0,0,0,0.06)]">
                  <CardHeader className="space-y-0 px-4 pb-2 pt-2 text-center">
                    <CardTitle className="w-full text-center font-playfair text-lg text-neutral-900">
                      {b.summary.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex min-h-0 flex-1 flex-col gap-4 space-y-0">
                    {/* Scrollable room + stay enhancements */}
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-y-contain pr-1">
                      {useConfirmationRailSummary && confirmationRailSummary ? (
                        <div className="space-y-3 text-neutral-900">
                          <div className="text-xs text-neutral-600">
                            {confirmationRailSummary.checkIn} → {confirmationRailSummary.checkOut}
                            {" · "}
                            {confirmationRailSummary.totalNights}{" "}
                            {confirmationRailSummary.totalNights === 1
                              ? b.policies.night
                              : b.policies.nights}
                          </div>
                          {confirmationRailSummary.rows.map((row, i) => (
                            <div
                              key={`confirmation-rail-${i}`}
                              className="space-y-1.5 rounded-lg border border-pine-green/10 bg-pine-green/[0.02] p-2.5"
                            >
                              {confirmationRailSummary.isMultiRoom ? (
                                <div className="text-xs font-semibold text-neutral-900">
                                  {stayRooms[i]?.label ??
                                    b.steps.stayStepRoomProgress
                                      .replace(/\{\{current\}\}/g, String(i + 1))
                                      .replace(/\{\{total\}\}/g, String(confirmationRailSummary.rows.length))}
                                </div>
                              ) : null}
                              <div className="text-sm font-semibold text-neutral-900">
                                <RoomNameDisplay displayName={row.roomName} variant="compact" />
                              </div>
                              <div className="text-xs text-neutral-600">
                                {row.adults}{" "}
                                {row.adults === 1 ? b.confirmation.adult : b.confirmation.adults}
                                {row.children > 0
                                  ? `, ${row.children} ${row.children === 1 ? b.confirmation.child : b.confirmation.children}`
                                  : ""}
                              </div>
                              {row.productNames.length > 0 ? (
                                <div className="pt-1">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                    {b.confirmation.additionalServices}
                                  </div>
                                  <ul className="mt-1 list-disc pl-4 text-xs text-neutral-700 space-y-0.5">
                                    {row.productNames.map((name) => (
                                      <li key={name}>{name}</li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : stayRooms.length > 1 ? (
                        <div className="space-y-3 text-neutral-900">
                          {stayRooms.map((sr, i) => {
                            const br = perStayRoomBrowseBreakdown[i];
                            const apiRow =
                              currentStep === "details" &&
                              finalPriceData &&
                              finalPriceData.length === stayRooms.length
                                ? finalPriceData[i]
                                : undefined;
                            const lineMoney =
                              br != null
                                ? resolvePerStaySummaryLineMoney(
                                    perStayRoomDisplayMoneyLines[i],
                                    apiRow,
                                    {
                                      roomTotal: br.roomTotal,
                                      browse: br.browse,
                                      addonSell: br.addonSell,
                                      addonList: br.addonList,
                                    },
                                    {
                                      displayCurrency,
                                      locale,
                                      products: hotels?.Products,
                                      enhanceStayUpsells,
                                      addonUpsellCounts: sr.addonUpsellCounts,
                                      occupancy: { adults: sr.adults, children: sr.children },
                                      chargedNights,
                                      ratePlanId: sr.selectedRatePlanId ?? "standard",
                                    },
                                  )
                                : null;
                            return (
                              <div
                                key={sr.id}
                                className="w-full space-y-1.5 rounded-lg border border-pine-green/10 bg-pine-green/[0.02] p-2.5 text-left text-neutral-900"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="text-xs font-semibold text-neutral-900">{sr.label}</div>
                                  {currentStep !== "confirmation" ? (
                                    <button
                                      type="button"
                                      onClick={() => focusPlanStepOnStayRoom(i)}
                                      aria-label={`${b.summary.edit}: ${sr.label}`}
                                      className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100/80 hover:text-pine-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2"
                                    >
                                      <AiOutlineEdit className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                                    </button>
                                  ) : null}
                                </div>
                                {br?.localized ? (
                                  <div className="text-xs text-neutral-600">
                                    <RoomNameDisplay
                                      variant="compact"
                                      displayName={br.localized.displayName}
                                    />
                                  </div>
                                ) : (
                                  <div className="text-xs text-neutral-500">{b.summary.chooseRoom}</div>
                                )}
                                <div className="flex items-start justify-between gap-2 text-xs">
                                  <span className="min-w-0 text-neutral-700">
                                    {br?.ratePlan?.name ?? b.summary.chooseRate}
                                  </span>
                                  <span className="shrink-0 text-right font-semibold tabular-nums text-pine-green">
                                    {br?.browse?.nightlyFormatted
                                      ? `${br.browse.nightlyFormatted} ${b.roomCard.perNight}`
                                      : "—"}
                                  </span>
                                </div>
                                <div className="flex justify-between border-t border-pine-green/10 pt-1.5 text-[11px] text-neutral-600">
                                  <span>{b.policies.roomTotal}</span>
                                  {lineMoney ? (
                                    <MultiStayRoomPriceLine
                                      compareAt={lineMoney.room.compareAt}
                                      effective={lineMoney.room.effective}
                                    />
                                  ) : (
                                    <span className="font-medium tabular-nums text-pine-green">—</span>
                                  )}
                                </div>
                                <MultiRoomAddonsSummaryRow
                                  lineMoney={lineMoney}
                                  addonLines={perStayRoomAddonSummaryLines[i] ?? []}
                                  expanded={Boolean(multiRoomAddonDetailsExpandedByStayId[sr.id])}
                                  onToggle={() =>
                                    setMultiRoomAddonDetailsExpandedByStayId((prev) => ({
                                      ...prev,
                                      [sr.id]: !prev[sr.id],
                                    }))
                                  }
                                  label={b.policies.addOns}
                                  toggleAriaLabel={b.summary.toggleMultiRoomAddonsAria}
                                  rowClassName="text-[11px] text-neutral-600"
                                />
                              </div>
                            );
                          })}
                          {stayRooms.some((s) => s.selectedRatePlanId === "signature") && checkIn ? (
                            <p className="text-xs leading-snug text-neutral-500">
                              <span className="font-medium text-neutral-700">{b.policies.brsSignaturePrefix}</span>
                              {formatSignatureCancellationSummaryLine(checkIn, b.signature, locale)}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          {/* Room as title */}
                          <div className="text-neutral-900">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-base font-semibold">
                                {selectedRoomLocalized ? (
                                  <RoomNameDisplay displayName={selectedRoomLocalized.displayName} />
                                ) : (
                                  b.summary.chooseRoom
                                )}
                              </div>
                              {selectedRoom && selectedRatePlan && currentStep !== "confirmation" ? (
                                <button
                                  type="button"
                                  onClick={goToRooms}
                                  aria-label={b.summary.edit}
                                  className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100/80 hover:text-pine-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2"
                                >
                                  <AiOutlineEdit className="h-4 w-4" aria-hidden />
                                </button>
                              ) : null}
                            </div>
                          </div>
                          {/* Rate as subheading with per night on the right; policy line full width below both */}
                          <div className="space-y-1">
                            <div className="flex items-start justify-between gap-2 text-sm">
                              <div className="min-w-0 flex-1 text-neutral-700 font-medium">
                                {selectedRatePlan ? selectedRatePlan.name : b.summary.chooseRate}
                              </div>
                              <div className="shrink-0 text-right font-semibold text-pine-green">
                                {selectedRoom && selectedRatePlan
                                  ? `${selectedRateBrowse?.nightlyFormatted ?? formatCHF(roomPricePerNight)} ${b.roomCard.perNight}`
                                  : "—"}
                              </div>
                            </div>
                            {selectedRatePlanId === "signature" && selectedRatePlan ? (
                              <p className="text-xs leading-snug text-neutral-500">
                                {checkIn
                                  ? formatSignatureCancellationSummaryLine(checkIn, b.signature, locale)
                                  : b.signature.summarySelectDates}
                              </p>
                            ) : null}
                          </div>
                        </>
                      )}

                      {stayRooms.length === 1 ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-semibold text-neutral-900 text-sm">{b.summary.stayEnhancements}</div>
                            {selectedRoom && selectedRatePlan && currentStep !== "confirmation" ? (
                              <button
                                type="button"
                                onClick={goToEnhance}
                                aria-label={b.summary.edit}
                                className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100/80 hover:text-pine-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2"
                              >
                                <AiOutlineEdit className="h-4 w-4" aria-hidden />
                              </button>
                            ) : null}
                          </div>
                          <p className="text-xs text-neutral-600">
                            {staySummaryEnhancementDiscountLine(selectedRatePlanId, b.summary)}
                          </p>
                          {addedUpsellsForSummary.length > 0 ? (
                            <div className="space-y-1.5 text-xs text-neutral-700">
                              {addedUpsellsForSummary.map((u) => {
                                const count = aggregateAddonUpsellCounts[u.id] ?? 0;
                                const upsellName = getLocalizedUpsellCopy(
                                  u.id,
                                  { name: u.name, description: u.description },
                                  b,
                                ).name;
                                return (
                                  <div
                                    key={u.id}
                                    className="flex items-start justify-between gap-2"
                                  >
                                    <span className="min-w-0 leading-snug">
                                      {upsellName}
                                      {count > 1 ? ` × ${count}` : ""}
                                    </span>
                                    <span className="shrink-0 font-medium tabular-nums text-pine-green">
                                      {staySummaryAddonLineSellFormattedByUpsellId[u.id] ?? "—"}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-xs text-neutral-500">{b.summary.noAddons}</p>
                          )}
                        </div>
                      ) : null}
                    </div>

                    {/* Fixed bottom section: total, benefits, CTA */}
                    <div className="space-y-3 border-t border-pine-green/15 pt-2">
                      <div className="text-center leading-none">
                        <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                          {b.policies.total}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5">
                          {bookingSummaryTotalLabels.compareAtFormatted != null ? (
                            <span className="text-base tabular-nums text-rose-950/50 line-through decoration-rose-400/45">
                              {bookingSummaryTotalLabels.compareAtFormatted}
                            </span>
                          ) : null}
                          <span className="text-xl font-semibold tabular-nums leading-none text-pine-green">
                            {bookingSummaryTotalLabels.effectiveFormatted}
                          </span>
                        </div>
                        <p className="mt-1 flex flex-wrap items-center justify-center gap-x-1 text-[10px] leading-snug text-neutral-500">
                          {renderStaySummaryNightCount("h-2.5 w-2.5")}
                          <span className="text-neutral-400" aria-hidden>
                            ·
                          </span>
                          {renderStaySummaryTotalGuestCount("h-2.5 w-2.5")}
                          <span className="text-neutral-400" aria-hidden>
                            ·
                          </span>
                          {b.policies.includingAllTaxes}
                        </p>
                      </div>

                      {(currentStep === "rooms" || currentStep === "addons") &&
                      selectedRatePlan &&
                      bookingSummarySavingsPillFormatted ? (
                        <SummaryDirectBookerPill
                          amountFormatted={bookingSummarySavingsPillFormatted}
                          book={b}
                        />
                      ) : null}

                      <div className="flex gap-2 pt-2">
                        {currentStep === "rooms" ? (
                          <Button
                            variant="hero"
                            className="rounded-full w-full"
                            disabled={!canContinue}
                            onClick={continuePlanStepOrEnhance}
                          >
                            {b.summary.continue}
                          </Button>
                        ) : currentStep === "addons" ? (
                          <Button
                            variant="hero"
                            className="rounded-full w-full"
                            onClick={continueEnhanceStepOrDetails}
                          >
                            {b.summary.continue}
                          </Button>
                        ) : null}
                      </div>
                      {!useConfirmationRailSummary && (!selectedRoom || !selectedRatePlan) ? (
                        <div className="text-xs text-neutral-500">{b.summary.selectRoomRateContinue}</div>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />

      <ContactConciergeModal
        isOpen={bookContactConciergeOpen}
        onClose={() => setBookContactConciergeOpen(false)}
        hideBookButton
      />

      {/* Mobile and tablet (below book breakpoint): minimal bottom bar + chevron to expand full summary */}
      {criteriaConfirmed ? (
        <>
          {mobileSummaryExpanded ? (
            <button
              type="button"
              className="book:hidden fixed inset-0 z-[35] bg-black/15 backdrop-blur-md"
              aria-label={b.summary.closeExpandedSummaryAria}
              onClick={() => setMobileSummaryExpanded(false)}
            />
          ) : null}
          <div className="book:hidden fixed bottom-0 left-0 right-0 z-40 rounded-t-2xl border-t border-gray-200 bg-white/95 shadow-[0_-8px_32px_rgba(0,0,0,0.1)]">
            <button
              type="button"
              onClick={() => setMobileSummaryExpanded((open) => !open)}
              aria-expanded={mobileSummaryExpanded}
              className={cn(
                "flex w-full flex-col items-stretch text-left outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2",
                !mobileSummaryExpanded && "border-b border-pine-green/10",
              )}
            >
              <div className="flex justify-center py-1.5">
                <ChevronUp
                  className={cn(
                    "h-4 w-4 text-neutral-400 transition-transform duration-200",
                    mobileSummaryExpanded && "rotate-180",
                  )}
                  aria-hidden
                />
              </div>
              {!mobileSummaryExpanded ? (
                <div
                  className={cn(
                    "flex gap-3 px-3 pb-3 sm:px-4",
                    stayRooms.length > 1 && currentStep === "details"
                      ? "items-stretch"
                      : mobileCollapsedMultiRoomStayKn
                        ? "items-start"
                        : "items-center",
                  )}
                >
                  <div
                    className={cn(
                      "min-w-0 flex-1",
                      stayRooms.length > 1 &&
                        currentStep === "details" &&
                        "flex min-h-0 flex-col justify-center",
                    )}
                  >
                    {mobileCollapsedMultiRoomStayKn ? (
                      <>
                        <div className="min-w-0 text-sm font-semibold leading-snug text-neutral-900">
                          <span className="whitespace-nowrap">
                            {b.summary.mobileMultiRoomStayProgress
                              .replace(/\{\{k\}\}/g, String(mobileCollapsedMultiRoomStayKn.k))
                              .replace(/\{\{n\}\}/g, String(mobileCollapsedMultiRoomStayKn.n))}
                            {": "}
                          </span>
                          {mobileMultiRoomPlanEnhanceStayPriceLabels?.compareAtFormatted ? (
                            <span className="text-sm font-semibold tabular-nums text-rose-950/50 line-through decoration-rose-400/45">
                              {mobileMultiRoomPlanEnhanceStayPriceLabels.compareAtFormatted}
                            </span>
                          ) : null}
                          {mobileMultiRoomPlanEnhanceStayPriceLabels?.compareAtFormatted ? <>{" "}</> : null}
                          <span className="text-sm font-semibold tabular-nums text-pine-green">
                            {mobileMultiRoomPlanEnhanceStayPriceLabels?.effectiveFormatted ?? "—"}
                          </span>
                        </div>
                        <div className="mt-0.5 text-xs text-neutral-500">
                          {activeStayRoomBrowseRow?.localized?.displayName ?? b.summary.chooseRoom}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-neutral-500">
                          {renderStaySummaryNightCount("h-3 w-3")}
                          <span className="text-neutral-400" aria-hidden>
                            ·
                          </span>
                          {renderStaySummaryGuestCount("h-3 w-3")}
                          {selectedRatePlan ? (
                            <>
                              <span className="text-neutral-400" aria-hidden>
                                ·
                              </span>
                              <span className="font-medium text-neutral-600">{selectedRatePlan.name}</span>
                            </>
                          ) : null}
                        </div>
                      </>
                    ) : stayRooms.length > 1 && currentStep === "details" ? (
                      (multiRoomMobilePolicyCounts.freeCancellation > 0 ||
                        multiRoomMobilePolicyCounts.lowestPrice > 0) && (
                        <div className="flex flex-col items-start gap-1">
                          {multiRoomMobilePolicyCounts.freeCancellation > 0 ? (
                            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5">
                              <span className="min-w-[1.25rem] shrink-0 text-right text-sm font-semibold tabular-nums text-neutral-900">
                                {multiRoomMobilePolicyCounts.freeCancellation}
                              </span>
                              <span
                                className="shrink-0 text-[11px] font-medium leading-none text-neutral-400"
                                aria-hidden
                              >
                                ×
                              </span>
                              <Badge className={cn("min-w-0 max-w-full shrink", rateCardPillGold)}>
                                {b.signature.badgeFreeCancellation}
                              </Badge>
                              {mobileFreeCancellationPillDeadlinePhrase ? (
                                <span className="min-w-0 shrink text-left text-[10px] font-medium leading-tight text-neutral-500">
                                  {mobileFreeCancellationPillDeadlinePhrase}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                          {multiRoomMobilePolicyCounts.lowestPrice > 0 ? (
                            <div className="flex min-w-0 max-w-full items-center gap-1.5">
                              <span className="min-w-[1.25rem] shrink-0 text-right text-sm font-semibold tabular-nums text-neutral-900">
                                {multiRoomMobilePolicyCounts.lowestPrice}
                              </span>
                              <span
                                className="shrink-0 text-[11px] font-medium leading-none text-neutral-400"
                                aria-hidden
                              >
                                ×
                              </span>
                              <Badge className={cn("min-w-0 max-w-full shrink", rateCardPillGold)}>
                                {b.signature.badgeLowestPrice}
                              </Badge>
                            </div>
                          ) : null}
                        </div>
                      )
                    ) : (
                      <>
                        <div className="truncate text-sm font-semibold leading-tight text-neutral-900">
                          {selectedRoomLocalized ? (
                            splitDisplayName(selectedRoomLocalized.displayName)[0]
                          ) : (
                            b.summary.chooseRoom
                          )}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-neutral-500">
                          {renderStaySummaryNightCount("h-3 w-3")}
                          <span className="text-neutral-400" aria-hidden>
                            ·
                          </span>
                          {renderStaySummaryGuestCount("h-3 w-3")}
                          {selectedRatePlan ? (
                            <>
                              <span className="text-neutral-400" aria-hidden>
                                ·
                              </span>
                              <span className="font-medium text-neutral-600">{selectedRatePlan.name}</span>
                            </>
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    {stayRooms.length > 1 && currentStep === "details" ? (
                      <div className="text-[11px] font-medium leading-snug text-neutral-600">
                        <span className="sr-only">
                          {b.summary.mobileCollapsedDetailsTotalNightsLine
                            .replace(/\{\{count\}\}/g, String(chargedNights))
                            .replace(
                              /\{\{nightsLabel\}\}/g,
                              chargedNights === 1 ? b.policies.night : b.policies.nights,
                            )}
                        </span>
                        <span className="inline-flex items-center gap-1" aria-hidden>
                          <span>
                            {b.summary.mobileCollapsedDetailsTotalNightsLine
                              .replace(/\{\{count\}\}/g, String(chargedNights))
                              .replace(/\s*\{\{nightsLabel\}\}\s*/g, "")}
                          </span>
                          <IoMoon className="h-3 w-3 shrink-0 text-neutral-500" />
                        </span>
                      </div>
                    ) : (
                      <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                        {b.policies.total}
                      </div>
                    )}
                    <div className="mt-0.5 flex flex-col items-end gap-1.5">
                      <div
                        className={cn(
                          stayRooms.length > 1
                            ? "flex flex-col items-end gap-0.5"
                            : "flex flex-wrap items-baseline justify-end gap-x-1.5 gap-y-0",
                        )}
                      >
                        {bookingSummaryTotalLabels.compareAtFormatted != null ? (
                          <span className="text-sm tabular-nums text-rose-950/50 line-through decoration-rose-400/45">
                            {bookingSummaryTotalLabels.compareAtFormatted}
                          </span>
                        ) : null}
                        <span className="text-base font-semibold tabular-nums leading-none text-pine-green">
                          {bookingSummaryTotalLabels.effectiveFormatted}
                        </span>
                      </div>
                      {selectedRatePlan && bookingSummaryTotalLabels.compareAtFormatted != null ? (
                        <div className="inline-flex max-w-full min-w-0 items-center gap-1 self-end rounded-full border border-pine-green/25 bg-pine-green/[0.06] px-2 py-0.5">
                          <span
                            className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-pine-green text-white"
                            aria-hidden
                          >
                            <Check className="h-2.5 w-2.5 stroke-[2.5]" />
                          </span>
                          <span className="min-w-0 text-left text-[8px] font-semibold uppercase leading-tight tracking-[0.12em] text-neutral-900 sm:text-[9px] sm:tracking-[0.1em]">
                            {b.directBookerDiscountLabel}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </button>

            {mobileSummaryExpanded ? (
              <div className="max-h-[min(78dvh,640px)] overflow-y-auto overscroll-y-contain px-3 pb-2 pt-1 sm:px-4">
                <div className="pb-2 text-center sm:pb-3">
                  <h2 className="font-playfair text-lg text-neutral-900">{b.summary.title}</h2>
                </div>
                <div className="space-y-4 pt-4">
                  {useConfirmationRailSummary && confirmationRailSummary ? (
                    <div className="space-y-3 text-neutral-900">
                      <div className="text-xs text-neutral-600">
                        {confirmationRailSummary.checkIn} → {confirmationRailSummary.checkOut}
                        {" · "}
                        {confirmationRailSummary.totalNights}{" "}
                        {confirmationRailSummary.totalNights === 1
                          ? b.policies.night
                          : b.policies.nights}
                      </div>
                      {confirmationRailSummary.rows.map((row, i) => (
                        <div
                          key={`confirmation-rail-mobile-${i}`}
                          className="space-y-1.5 rounded-lg border border-pine-green/10 bg-pine-green/[0.02] p-2.5"
                        >
                          {confirmationRailSummary.isMultiRoom ? (
                            <div className="text-xs font-semibold text-neutral-900">
                              {stayRooms[i]?.label ??
                                b.steps.stayStepRoomProgress
                                  .replace(/\{\{current\}\}/g, String(i + 1))
                                  .replace(/\{\{total\}\}/g, String(confirmationRailSummary.rows.length))}
                            </div>
                          ) : null}
                          <div className="text-sm font-semibold text-neutral-900">
                            <RoomNameDisplay displayName={row.roomName} variant="compact" />
                          </div>
                          <div className="text-xs text-neutral-600">
                            {row.adults}{" "}
                            {row.adults === 1 ? b.confirmation.adult : b.confirmation.adults}
                            {row.children > 0
                              ? `, ${row.children} ${row.children === 1 ? b.confirmation.child : b.confirmation.children}`
                              : ""}
                          </div>
                          {row.productNames.length > 0 ? (
                            <div className="pt-1">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                                {b.confirmation.additionalServices}
                              </div>
                              <ul className="mt-1 list-disc pl-4 text-xs text-neutral-700 space-y-0.5">
                                {row.productNames.map((name) => (
                                  <li key={name}>{name}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : stayRooms.length > 1 ? (
                    <div className="space-y-3 text-neutral-900">
                      {stayRooms.map((sr, i) => {
                        const br = perStayRoomBrowseBreakdown[i];
                        const apiRow =
                          currentStep === "details" &&
                          finalPriceData &&
                          finalPriceData.length === stayRooms.length
                            ? finalPriceData[i]
                            : undefined;
                        const lineMoney =
                          br != null
                            ? resolvePerStaySummaryLineMoney(
                                perStayRoomDisplayMoneyLines[i],
                                apiRow,
                                {
                                  roomTotal: br.roomTotal,
                                  browse: br.browse,
                                  addonSell: br.addonSell,
                                  addonList: br.addonList,
                                },
                                {
                                  displayCurrency,
                                  locale,
                                  products: hotels?.Products,
                                  enhanceStayUpsells,
                                  addonUpsellCounts: sr.addonUpsellCounts,
                                  occupancy: { adults: sr.adults, children: sr.children },
                                  chargedNights,
                                  ratePlanId: sr.selectedRatePlanId ?? "standard",
                                },
                              )
                            : null;
                        return (
                          <div
                            key={sr.id}
                            className="w-full space-y-1.5 rounded-lg border border-pine-green/10 bg-pine-green/[0.02] p-2.5 text-left text-neutral-900"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="text-xs font-semibold text-neutral-900">{sr.label}</div>
                              {currentStep !== "confirmation" ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMobileSummaryExpanded(false);
                                    focusPlanStepOnStayRoom(i);
                                  }}
                                  aria-label={`${b.summary.edit}: ${sr.label}`}
                                  className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100/80 hover:text-pine-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2"
                                >
                                  <AiOutlineEdit className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                                </button>
                              ) : null}
                            </div>
                            {br?.localized ? (
                              <div className="text-xs text-neutral-600">
                                <RoomNameDisplay
                                  variant="compact"
                                  displayName={br.localized.displayName}
                                />
                              </div>
                            ) : (
                              <div className="text-xs text-neutral-500">{b.summary.chooseRoom}</div>
                            )}
                            <div className="flex items-start justify-between gap-2 text-xs">
                              <span className="min-w-0 text-neutral-700">
                                {br?.ratePlan?.name ?? b.summary.chooseRate}
                              </span>
                              <span className="shrink-0 text-right font-semibold tabular-nums text-pine-green">
                                {br?.browse?.nightlyFormatted
                                  ? `${br.browse.nightlyFormatted} ${b.roomCard.perNight}`
                                  : "—"}
                              </span>
                            </div>
                            <div className="flex justify-between border-t border-pine-green/10 pt-1.5 text-[11px] text-neutral-600">
                              <span>{b.policies.roomTotal}</span>
                              {lineMoney ? (
                                <MultiStayRoomPriceLine
                                  compareAt={lineMoney.room.compareAt}
                                  effective={lineMoney.room.effective}
                                />
                              ) : (
                                <span className="font-medium tabular-nums text-pine-green">—</span>
                              )}
                            </div>
                            <MultiRoomAddonsSummaryRow
                              lineMoney={lineMoney}
                              addonLines={perStayRoomAddonSummaryLines[i] ?? []}
                              expanded={Boolean(multiRoomAddonDetailsExpandedByStayId[sr.id])}
                              onToggle={() =>
                                setMultiRoomAddonDetailsExpandedByStayId((prev) => ({
                                  ...prev,
                                  [sr.id]: !prev[sr.id],
                                }))
                              }
                              label={b.policies.addOns}
                              toggleAriaLabel={b.summary.toggleMultiRoomAddonsAria}
                              rowClassName="text-[11px] text-neutral-600"
                            />
                          </div>
                        );
                      })}
                      {stayRooms.some((s) => s.selectedRatePlanId === "signature") && checkIn ? (
                        <p className="text-xs leading-snug text-neutral-500">
                          <span className="font-medium text-neutral-700">{b.policies.brsSignaturePrefix}</span>
                          {formatSignatureCancellationSummaryLine(checkIn, b.signature, locale)}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      <div className="text-neutral-900">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1 text-base font-semibold">
                            {selectedRoomLocalized ? (
                              <RoomNameDisplay displayName={selectedRoomLocalized.displayName} />
                            ) : (
                              b.summary.chooseRoom
                            )}
                          </div>
                          {selectedRoom && selectedRatePlan && currentStep !== "confirmation" ? (
                            <button
                              type="button"
                              onClick={() => {
                                setMobileSummaryExpanded(false);
                                goToRooms();
                              }}
                              aria-label={b.summary.edit}
                              className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100/80 hover:text-pine-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2"
                            >
                              <AiOutlineEdit className="h-4 w-4" aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-start justify-between gap-2 text-sm">
                          <div className="min-w-0 flex-1 font-medium text-neutral-700">
                            {selectedRatePlan ? selectedRatePlan.name : b.summary.chooseRate}
                          </div>
                          <div className="shrink-0 text-right font-semibold text-pine-green">
                            {selectedRoom && selectedRatePlan
                              ? `${selectedRateBrowse?.nightlyFormatted ?? formatCHF(roomPricePerNight)} ${b.roomCard.perNight}`
                              : "—"}
                          </div>
                        </div>
                        {selectedRatePlanId === "signature" && selectedRatePlan ? (
                          <p className="text-xs leading-snug text-neutral-500">
                            {checkIn
                              ? formatSignatureCancellationSummaryLine(checkIn, b.signature, locale)
                              : b.signature.summarySelectDates}
                          </p>
                        ) : null}
                      </div>
                    </>
                  )}
                  {stayRooms.length === 1 ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-neutral-900">{b.summary.stayEnhancements}</div>
                        {selectedRoom && selectedRatePlan && currentStep !== "confirmation" ? (
                          <button
                            type="button"
                            onClick={() => {
                              setMobileSummaryExpanded(false);
                              goToEnhance();
                            }}
                            aria-label={b.summary.edit}
                            className="inline-flex shrink-0 items-center justify-center rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100/80 hover:text-pine-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pine-green/25 focus-visible:ring-offset-2"
                          >
                            <AiOutlineEdit className="h-4 w-4" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                      <p className="text-xs text-neutral-600">
                        {staySummaryEnhancementDiscountLine(selectedRatePlanId, b.summary)}
                      </p>
                      {addedUpsellsForSummary.length > 0 ? (
                        <div className="space-y-1.5 text-sm text-neutral-700">
                          {addedUpsellsForSummary.map((u) => {
                            const count = aggregateAddonUpsellCounts[u.id] ?? 0;
                            const upsellName = getLocalizedUpsellCopy(
                              u.id,
                              { name: u.name, description: u.description },
                              b,
                            ).name;
                            return (
                              <div
                                key={u.id}
                                className="flex items-start justify-between gap-2"
                              >
                                <span className="min-w-0 leading-snug">
                                  {upsellName}
                                  {count > 1 ? ` × ${count}` : ""}
                                </span>
                                <span className="shrink-0 font-medium tabular-nums text-pine-green">
                                  {staySummaryAddonLineSellFormattedByUpsellId[u.id] ?? "—"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-xs text-neutral-500">{b.summary.noAddons}</p>
                      )}
                    </div>
                  ) : null}
                  <div className="space-y-3 border-t border-pine-green/15 pt-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 text-left leading-none">
                        <div className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                          {b.policies.total}
                        </div>
                        <div
                          className={cn(
                            "mt-1",
                            stayRooms.length > 1
                              ? "flex flex-col items-start gap-0.5"
                              : "flex flex-wrap items-baseline gap-x-2 gap-y-0.5",
                          )}
                        >
                          {bookingSummaryTotalLabels.compareAtFormatted != null ? (
                            <span className="text-base tabular-nums text-rose-950/50 line-through decoration-rose-400/45">
                              {bookingSummaryTotalLabels.compareAtFormatted}
                            </span>
                          ) : null}
                          <span className="text-xl font-semibold tabular-nums leading-none text-pine-green">
                            {bookingSummaryTotalLabels.effectiveFormatted}
                          </span>
                        </div>
                        <p className="mt-1 flex flex-wrap items-center gap-x-1 text-[10px] leading-snug text-neutral-500">
                          {renderStaySummaryNightCount("h-2.5 w-2.5")}
                          <span className="text-neutral-400" aria-hidden>
                            ·
                          </span>
                          {renderStaySummaryTotalGuestCount("h-2.5 w-2.5")}
                          <span className="text-neutral-400" aria-hidden>
                            ·
                          </span>
                          {b.policies.includingAllTaxes}
                        </p>
                      </div>
                      {allStayRoomsPlanned && currentStep !== "details" ? (
                        <button
                          type="button"
                          onClick={openFullBookingSummaryFromExpandedSheet}
                          className="shrink-0 pt-0.5 text-[11px] font-medium text-neutral-600 underline underline-offset-2 hover:text-pine-green"
                        >
                          {b.summary.expandedSheetCheckoutLink}
                        </button>
                      ) : null}
                    </div>
                    {(currentStep === "rooms" || currentStep === "addons") &&
                    selectedRatePlan &&
                    bookingSummarySavingsPillFormatted ? (
                      <SummaryDirectBookerPill
                        amountFormatted={bookingSummarySavingsPillFormatted}
                        book={b}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex gap-2 border-t border-pine-green/10 px-3 py-3 sm:px-4">
              {showBookingStickyBack ? (
                <Button variant="outline" className="rounded-full flex-1" onClick={goBack}>
                  {b.summary.back}
                </Button>
              ) : null}
              {(currentStep === "rooms" || currentStep === "addons") && (
                <Button
                  variant="hero"
                  className={cn(
                    "rounded-full",
                    showBookingStickyBack && (currentStep === "rooms" || currentStep === "addons")
                      ? "flex-[2]"
                      : "w-full flex-1",
                  )}
                  disabled={currentStep === "rooms" && !canContinue}
                  onClick={
                    currentStep === "rooms"
                      ? continuePlanStepOrEnhance
                      : continueEnhanceStepOrDetails
                  }
                >
                  {b.summary.continue}
                </Button>
              )}
              {currentStep === "details" &&
              paymentStepActive &&
              !createReservationResult ? (
                <Button
                  variant="hero"
                  className="rounded-full flex-[2]"
                  disabled={detailsConfirmBookingDisabled}
                  onMouseDown={(e) => {
                    if (optionBActive) e.preventDefault();
                  }}
                  onClick={() => confirmBookingButtonRef.current?.click()}
                >
                  {isCreatingReservation
                    ? b.confirmation.processing
                    : onSessionEligible
                      ? b.confirmation.confirmAndPay
                      : b.confirmation.confirmBooking}
                </Button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default Book;

